import React, { useState, useCallback, useLayoutEffect, useRef, useEffect, useMemo } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Line, Circle, Text, Label, Tag, Group } from 'react-konva';
import useImage from 'use-image';
import { BoundingBox, Section, PnidWizardState, getEquipmentInstrumentName, getInstrumentCategoryName, instrumentRegions, MarkingStatus, MARKING_STATUS_STYLES, MarkingStatusStyle, NodePointCandidate, NodePointCandidateLabel, InstrumentCategory, CanvasComment, LineSegmentStatus, CropResult, TemplateAnnotation, TemplateMatchResult } from '../types';
import { appendImageCacheVersion, documentApi, pnidApi, type ResolvedImageUrl } from '../api';
import { WizardStep } from './PnidWizardSidebar';
import CommentBubble from './CommentBubble';
import { mergeOverlappingLineSegments, MergedLineSegment } from '../utils/geometry';
import {
  EDGE_PAN_COMMIT_INTERVAL_MS,
  EDGE_PAN_DWELL_MS,
  MAX_CANVAS_SCALE,
  MIN_CANVAS_SCALE,
  ZOOM_STEP_FACTOR,
  arrowPanDelta,
  clampPanPosition,
  computeEdgePanVelocity,
  stepPanPosition,
  zoomAtPoint,
  zoomKeyFactor,
  type PanImageSize,
  type PanPointer,
  type PanViewport,
} from './pidCanvasNavigation';
import { COLLECT_ARMING_HINT } from '../pnid/boxDraft';
import type { WalkthroughHighlights } from '../types/plantModel';
import type { CanvasInspectionOverlay } from '../types/pipelineInspection';
import { getInstrumentPrimaryLabel } from '../utils/instrumentIdentity';
import {
  getEquipmentContextInstruments,
  getInstrumentEffectiveContext,
  getNodeContextInstruments,
  getUnattachedInstruments,
} from '../utils/unifiedInstruments';

/** Wizard overlay when item is inactive: green only after explicit review */
function wizardInactivePalette(
  kind: 'equipment' | 'line' | 'instrument',
  reviewed: boolean
): { stroke: string; fill: string; hoverStroke: string; hoverFill: string } {
  if (reviewed) {
    return {
      stroke: '#86efac',
      fill: 'rgba(134, 239, 172, 0.12)',
      hoverStroke: '#4ade80',
      hoverFill: 'rgba(134, 239, 172, 0.22)',
    };
  }
  if (kind === 'equipment') {
    return {
      stroke: '#60a5fa',
      fill: 'rgba(59, 130, 246, 0.12)',
      hoverStroke: '#2563eb',
      hoverFill: 'rgba(59, 130, 246, 0.22)',
    };
  }
  if (kind === 'line') {
    return {
      stroke: '#fb923c',
      fill: 'rgba(251, 146, 60, 0.12)',
      hoverStroke: '#ea580c',
      hoverFill: 'rgba(251, 146, 60, 0.22)',
    };
  }
  return {
    stroke: '#a78bfa',
    fill: 'rgba(167, 139, 250, 0.12)',
    hoverStroke: '#7c3aed',
    hoverFill: 'rgba(167, 139, 250, 0.22)',
  };
}

const targetOrAncestorHasKonvaName = (target: any, names: string[]): boolean => {
  let node = target;
  while (node) {
    if (names.some((name) => node.hasName?.(name))) {
      return true;
    }
    node = node.getParent?.();
  }
  return false;
};

const CanvasPerfHud: React.FC<{
  scale: number;
  perfStats: { nodes: number; nodesWithPolyline: number; totalNodePoints: number } | null;
  nodePointMode: NodePointRenderMode;
  setNodePointMode: (mode: NodePointRenderMode) => void;
  cursorPosition: { x: number; y: number } | null;
  onHide: () => void;
}> = ({ scale, perfStats, nodePointMode, setNodePointMode, cursorPosition, onHide }) => {
  const [fps, setFps] = useState<number | null>(null);
  useEffect(() => {
    let rafId = 0;
    let lastT = performance.now();
    let frameCount = 0;

    const tick = (t: number) => {
      frameCount += 1;
      const dt = t - lastT;
      if (dt >= 500) {
        setFps(Math.round((frameCount * 1000) / dt));
        frameCount = 0;
        lastT = t;
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 12,
        padding: '8px 10px',
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        color: 'white',
        borderRadius: 8,
        fontSize: 12,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        minWidth: 220,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <div style={{ fontWeight: 700, letterSpacing: '0.06em', color: 'rgba(229,231,235,0.9)' }}>CANVAS PERF</div>
        <button
          onClick={onHide}
          style={{
            padding: '1px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
          title="Hide (re-enable with ?canvasPerf=1)"
        >
          Hide
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>FPS</div>
        <div>{fps ?? '…'}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>Zoom</div>
        <div>{scale.toFixed(2)}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>Cursor</div>
        <div>{cursorPosition ? `${cursorPosition.x.toFixed(1)}, ${cursorPosition.y.toFixed(1)}` : '—'}</div>
      </div>
      {perfStats && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>Nodes</div>
            <div>
              {perfStats.nodes} ({perfStats.nodesWithPolyline} poly)
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>Node pts</div>
            <div>{perfStats.totalNodePoints}</div>
          </div>
        </>
      )}

      <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'rgba(229, 231, 235, 0.85)' }}>Node points:</span>
        <button
          onClick={() => setNodePointMode('none')}
          style={{
            padding: '2px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: nodePointMode === 'none' ? '#2563eb' : 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          None
        </button>
        <button
          onClick={() => setNodePointMode('active')}
          style={{
            padding: '2px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: nodePointMode === 'active' ? '#2563eb' : 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          Active
        </button>
        <button
          onClick={() => setNodePointMode('all')}
          style={{
            padding: '2px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: nodePointMode === 'all' ? '#2563eb' : 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          All
        </button>
      </div>
    </div>
  );
};

// Hover tooltip info type
interface HoveredItem {
  type: 'equipment' | 'equipment_instrument' | 'node_instrument' | 'unattached_instrument' | 'node' | 'control_loop';
  id: string;
  tag: string;
  itemType: string;
  bbox: BoundingBox;
  /** Only for type === 'node' */
  lineNo?: string;
}

interface PidCanvasProps {
  imageUrl: string;
  sections: Section[];
  onDraw: (bbox: BoundingBox, anchor: { x: number; y: number }) => void;
  pendingPieces: { bbox: BoundingBox; context: string; id: string }[];
  onCancelPending?: (pieceId:string)=> void;
  onSelectSection: (sectionId: string) => void;
  selectedSectionId?: string;
  onSelectPending?: (pendingId: string) => void;
  selectedPendingId?: string;
  /** Optional fixed width. If omitted, canvas fills parent horizontally */
  width?: number;
  /** Optional fixed height. If omitted, canvas fills parent vertically */
  height?: number;
  /** Document ID for SAM API calls */
  docId?: string;
  /** Changes when the source image changes, so browser/proxy caches refetch the canvas image. */
  imageVersion?: string | null;
  /** Canonical image-coordinate frame for overlays when a lower-resolution image variant is rendered. */
  logicalImageSize?: { width: number; height: number };
  /** Read-only execution-provenance overlays for pipeline inspection. */
  inspectionOverlay?: CanvasInspectionOverlay | null;
  /** Hide developer/performance chrome in embedded read-only inspection views. */
  hidePerfHud?: boolean;
  /** Hide editing guidance when the canvas is used as an evidence viewer. */
  hideInstructions?: boolean;
  /**
   * Scroll the drawing while the cursor rests near a border. Turn this off in a
   * viewer that frames the view itself, where a cursor entering from a sidebar
   * would scroll that framing away.
   */
  edgePanEnabled?: boolean;
  /** Fit the full drawing when an embedded viewer clears its focused evidence box. */
  fitOnFocusClear?: boolean;
  // Wizard mode props
  wizardMode?: boolean;
  pnidState?: PnidWizardState | null;
  wizardStep?: WizardStep;
  activeEquipmentId?: string | null;
  activeNodeId?: string | null;
  selectedInstrumentId?: string | null;
  onRightClickInstrument?: (id: string, screenPos: { x: number; y: number }) => void;
  /** Show only active item (true) or all markup (false) */
  showActiveOnly?: boolean;
  focusBbox?: BoundingBox | null;
  /** Box currently being marked (shown while mark form is open) */
  pendingMarkBbox?: BoundingBox | null;
  /** Update pending mark bbox (for resize/drag handles) */
  onUpdatePendingMarkBbox?: (bbox: BoundingBox) => void;
  /** Select a marked wizard item */
  onSelectWizardItem?: (target: {
    kind: WizardCanvasItemKind;
    id: string;
    screenPosition?: { x: number; y: number };
    isMultiSelect?: boolean;
  }) => void;
  /** Selected persisted wizard annotations for bulk movement/deletion */
  selectedWizardItemKeys?: Set<string>;
  /** Incremented by parent when saved annotations should become directly editable. */
  savedAnnotationEditModeSignal?: number;
  onSelectAllWizardItems?: () => void;
  onClearWizardItemSelection?: () => void;
  onDeleteSelectedWizardItems?: () => void;
  /** Puts the selected items' tags in one redundancy set. Absent where the drawing has no plant or the user cannot edit. */
  onMarkSelectedAsRedundancySet?: () => void;
  onShiftSelectedWizardAnnotations?: (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => Promise<void> | void;
  /** Capture undo before a persisted box drag/resize starts. */
  onBeginWizardItemBboxUpdate?: (
    target: { kind: 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'controlLoop' | 'node'; id: string },
  ) => void;
  /**
   * Adjust a persisted rectangle mark. commit=false is a local preview; commit=true
   * persists the final geometry. `regionIndex` says which of a multi-region marking's
   * rectangles moved; it is absent for marks that carry a single rectangle.
   */
  onUpdateWizardItemBbox?: (
    target: { kind: 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'controlLoop' | 'node'; id: string },
    bbox: BoundingBox,
    options?: { commit?: boolean; regionIndex?: number },
  ) => void;
  /** One-click delete of a persisted instrument annotation (quick remove, undoable via Cmd/Ctrl+Z). */
  onQuickDeleteWizardItem?: (
    target: { kind: 'equipmentInstrument' | 'nodeInstrument'; id: string },
  ) => void;
  /** Shift all saved wizard annotation geometry by a common image-coordinate delta. */
  onShiftWizardAnnotations?: (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => Promise<void> | void;
  /**
   * The regions being identified, each outlined with an "Identifying..." label until
   * the answer arrives. One identify call covers the whole marking, so every region
   * the SME drew for it carries the wait.
   */
  identifyingBboxes?: BoundingBox[];
  /** Whether node marking mode is active (disables clicking on other nodes) */
  isNodeMarkingMode?: boolean;
  /** Geometry selected before drawing a step-4 material-flow connection. */
  nodeDrawingShape?: 'pipeline' | 'duct';
  onNodeDrawingShapeChange?: (shape: 'pipeline' | 'duct') => void;
  /** Candidate instruments detected along a node polyline */
  nodePointCandidates?: NodePointCandidate[];
  /** Resolve a candidate with a label or ignore */
  onResolveNodePointCandidate?: (candidateId: string, label: NodePointCandidateLabel | 'ignore') => void;
  /** Target context for the current draw action */
  drawTarget?: DrawTargetInfo | null;
  /** Notify parent when a draft drawing is in progress */
  onDraftStateChange?: (hasDraft: boolean) => void;
  /** Trigger to clear any in-progress drawing */
  clearDraftSignal?: number;
  /** Callback when user double-clicks a node to enter mark mode */
  onDoubleClickNode?: (nodeId: string, screenPosition?: { x: number; y: number }) => void;
  /** Callback when user starts a path (first point placed) */
  onPathStart?: (screenPosition: { x: number; y: number }, canvasPosition: { x: number; y: number }) => void;
  /** Optional line-segment review overlay for a specific node */
  segmentReview?: {
    nodeId: string | null;
    hoveredDistinctId?: string | null;
    selectedDistinctId?: string | null;
  };
  /** Optional line number to guide trace assist (when drawing a new line). */
  traceLineNo?: string | null;
  /** Optional coordinate transform for AI-extracted line segment coordinates → canvas image coordinates */
  segmentCoordinateScale?: { sx: number; sy: number };
  /** Notify parent when the underlying image natural size is known */
  onImageSize?: (width: number, height: number) => void;
  // Comment mode props
  /** All comments for the document */
  comments?: CanvasComment[];
  /** Currently selected comment ID */
  selectedCommentId?: string | null;
  /** Callback when user clicks in comment mode to create a comment (includes screen position) */
  onCreateComment?: (canvasX: number, canvasY: number, screenX: number, screenY: number) => void;
  /** Toggle comment mode from parent */
  onToggleCommentMode?: () => void;
  /** Callback when a comment bubble is clicked (includes screen position) */
  onSelectComment?: (commentId: string, screenX: number, screenY: number) => void;
  /** Show resolved comments */
  showResolvedComments?: boolean;
  /** Walkthrough mode highlights for segments and equipment (Intelligence View) */
  walkthroughHighlights?: WalkthroughHighlights | null;
  /** Callback when user crops a region on the canvas (for template creation) */
  onCrop?: (result: CropResult) => void;
  // Template annotation props
  /** Saved template annotations (displayed in green) */
  templateAnnotations?: TemplateAnnotation[];
  /** Unsaved template match results, keyed by template ID (displayed in orange) */
  unsavedMatches?: Map<string, TemplateMatchResult[]>;
  /** Template visibility map */
  templateVisibility?: Map<string, boolean>;
  /** Selected annotation IDs for bulk operations */
  selectedAnnotationIds?: Set<string>;
  /** Currently active annotation ID (for SME popup or dialog) */
  activeAnnotationId?: string | null;
  /** Callback when an annotation is deleted */
  onDeleteAnnotation?: (id: string, isUnsaved: boolean, templateId?: string, index?: number) => void;
  /** Callback when an annotation is selected */
  onSelectAnnotation?: (id: string, isMultiSelect: boolean) => void;
  /** Callback when an annotation is clicked in PAN mode (for verification workflow) */
  onAnnotationClick?: (annotationId: string, action: 'sme-capture' | 'edit', screenPos: { x: number; y: number }) => void;
  /** Adjust a saved template annotation bbox. commit=false is a local preview; commit=true persists the final geometry. */
  onUpdateAnnotationBbox?: (
    annotationId: string,
    bbox: BoundingBox,
    options?: { commit?: boolean },
  ) => void;
  /** Callback to bulk delete selected annotations */
  onBulkDeleteAnnotations?: () => void;
  /** Callback to clear annotation selection */
  onClearAnnotationSelection?: () => void;
  /** Callback to attach/detach template annotations to the active equipment/node */
  onAttachTemplateAnnotations?: (annotationIds: string[], action: 'attach' | 'detach') => void;
  /** Click-to-attach: immediately persist an unattached (context-free) template/AI instrument onto the active equipment (step 3) or node (step 4) */
  onDirectAttachAnnotation?: (annotationId: string) => void;
  /** Draft attachment IDs for the active equipment/node (no backend persistence until save) */
  draftAttachedAnnotationIds?: Set<string>;
  /** Draft detached IDs for the active equipment/node (no backend persistence until save) */
  draftDetachedAnnotationIds?: Set<string>;
  /** Entity ID to highlight on canvas (from InstrumentEditor hover) */
  highlightedEntityId?: string | null;
  /** Reviewer mode: view + comment only, no editing/drawing */
  readOnly?: boolean;
  /** Callback when instrument sub-mode is toggled in steps 3/4 */
  onInstrumentSubModeToggle?: (active: boolean) => void;
  /**
   * External signal to arm the collect mode (e.g. from the mark dialog). It stays true
   * across mouse-ups, so the SME draws region after region without re-arming; the mode
   * ends when the workspace clears it.
   */
  collectingRegions?: boolean;
  /** Regions collected so far in the armed collect mode, in draw order. */
  draftRegions?: BoundingBox[];
  /** Finishes the draft. Behind the floating "Done · N regions" pill and the Enter key. */
  onFinishRegions?: () => void;
  /** Backs out the most recently drawn region. Escape, while the draft holds regions. */
  onRemoveLastRegion?: () => void;
  /** Leaves the collect mode. Escape, while the draft is empty. */
  onExitCollect?: () => void;
  /** Callback for batched canvas interaction tracking (called every 10s with accumulated counts) */
  onCanvasInteraction?: (counts: Record<string, number>) => void;
}

interface DrawingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

type DrawMode = 'select' | 'rect' | 'poly' | 'path' | 'comment' | 'crop' | 'select-annotations' | 'shift-annotations';
type WizardCanvasItemKind = 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'node' | 'controlLoop';
type PersistedBboxCanvasTarget = {
  // 'node' targets adjust a duct node's persisted duct_bbox.
  kind: 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'controlLoop' | 'node';
  id: string;
};
type DrawTargetTone = 'new' | 'missing' | MarkingStatus;
type NodePointRenderMode = 'all' | 'active' | 'none';

interface DrawTargetInfo {
  label: string;
  detail?: string;
  tone?: DrawTargetTone;
}

// Minimum crop selection size (in pixels)
const MIN_CROP_SIZE = 10;
// The collect mode's overlays are read, not measured, so they are sized in screen
// pixels and divided by the stage scale — legible at any zoom.
const DRAFT_REGION_CHIP_SIZE_PX = 18;

/**
 * The order chip on a region. Draw order is what makes a marking readable once it is
 * several rectangles — which one is the symbol, which the balloon — so the number is
 * on the sheet rather than only in the dialog. Drafts carry it top-left and saved
 * markings alike carry it at the top-right corner, so the number sits in one place
 * whatever the rectangle is.
 */
function RegionIndexChip({
  region,
  index,
  color,
  scale,
}: {
  region: BoundingBox;
  index: number;
  color: string;
  scale: number;
}) {
  const size = DRAFT_REGION_CHIP_SIZE_PX / scale;
  const inset = 2 / scale;
  const x = region.x2 - inset - size;
  const y = region.y1 + inset;
  return (
    <>
      <Rect
        x={x}
        y={y}
        width={size}
        height={size}
        cornerRadius={4 / scale}
        fill={color}
        listening={false}
        perfectDrawEnabled={false}
      />
      <Text
        x={x}
        y={y}
        width={size}
        height={size}
        text={String(index + 1)}
        fontSize={12 / scale}
        fontStyle="bold"
        fill="white"
        align="center"
        verticalAlign="middle"
        listening={false}
      />
    </>
  );
}
const DONE_PILL_HEIGHT_PX = 30;

/** A key cap inside the collecting pill, sized to sit on the pill's own baseline. */
const COLLECT_KEY_STYLE: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  marginRight: 4,
  borderRadius: 4,
  border: '1px solid rgba(255, 255, 255, 0.55)',
  borderBottomWidth: 2,
  background: 'rgba(255, 255, 255, 0.18)',
  font: '700 11px ui-monospace, SFMono-Regular, Menlo, monospace',
  lineHeight: 1.5,
};
/** Bold 13px system text averages about this wide, which sizes the pill without measuring it. */
const DONE_PILL_GAP_PX = 10;
const DONE_PILL_VIEWPORT_MARGIN_PX = 12;
const INTERACTION_IMAGE_MAX_DIMENSION = 2048;
const INTERACTION_IMAGE_IDLE_MS = 500;
const PNID_IMAGE_LEVELS = [2048, 4096, 8192] as const;
const INITIAL_PNID_IMAGE_LEVEL = 4096;
const IMAGE_LEVEL_SETTLE_MS = 250;
const IMAGE_LEVEL_QUALITY_BUFFER = 1.2;
const VIEWPORT_CULL_PADDING_SCREEN_PX = 360;
const VIEWPORT_CULL_MIN_IMAGE_PADDING = 300;
const OVERVIEW_DETAIL_SCALE = 0.35;
const LABEL_DETAIL_SCALE = 0.6;
const STATS_DETAIL_SCALE = 0.8;

type CanvasBounds = { x1: number; y1: number; x2: number; y2: number };

const boundsIntersect = (a: CanvasBounds, b: CanvasBounds): boolean => (
  a.x1 <= b.x2 &&
  a.x2 >= b.x1 &&
  a.y1 <= b.y2 &&
  a.y2 >= b.y1
);

const mergeBounds = (a: CanvasBounds | null, b: CanvasBounds | null): CanvasBounds | null => {
  if (!a) return b;
  if (!b) return a;
  return {
    x1: Math.min(a.x1, b.x1),
    y1: Math.min(a.y1, b.y1),
    x2: Math.max(a.x2, b.x2),
    y2: Math.max(a.y2, b.y2),
  };
};

const choosePnidImageLevel = (
  scale: number,
  originalWidth: number | null,
  originalHeight: number | null
): number => {
  if (!originalWidth || !originalHeight) return INITIAL_PNID_IMAGE_LEVEL;
  const originalMaxDim = Math.max(originalWidth, originalHeight);
  const deviceScale = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const requiredMaxDim = originalMaxDim * Math.max(scale, 1e-4) * deviceScale * IMAGE_LEVEL_QUALITY_BUFFER;
  return PNID_IMAGE_LEVELS.find((level) => level >= requiredMaxDim) ?? PNID_IMAGE_LEVELS[PNID_IMAGE_LEVELS.length - 1];
};

const rectBounds = (x: number, y: number, width: number, height: number): CanvasBounds => ({
  x1: x,
  y1: y,
  x2: x + width,
  y2: y + height,
});

const boundsFromBbox = (bbox?: BoundingBox | null): CanvasBounds | null => {
  if (!bbox) return null;
  const rect = {
    x1: Math.min(bbox.x1, bbox.x2),
    y1: Math.min(bbox.y1, bbox.y2),
    x2: Math.max(bbox.x1, bbox.x2),
    y2: Math.max(bbox.y1, bbox.y2),
  };
  return mergeBounds(rect, boundsFromPolyline(bbox.polygon));
};

const boundsFromPolyline = (points?: number[] | null): CanvasBounds | null => {
  if (!points || points.length < 4) return null;
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;

  for (let i = 0; i < points.length - 1; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (typeof x !== 'number' || typeof y !== 'number') continue;
    x1 = Math.min(x1, x);
    y1 = Math.min(y1, y);
    x2 = Math.max(x2, x);
    y2 = Math.max(y2, y);
  }

  if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
    return null;
  }

  return { x1, y1, x2, y2 };
};

const boundsFromLineSegments = (
  segments?: { x1: number; y1: number; x2: number; y2: number }[] | null,
  sx = 1,
  sy = 1
): CanvasBounds | null => {
  if (!segments?.length) return null;
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;

  for (const segment of segments) {
    const ax = segment.x1 * sx;
    const ay = segment.y1 * sy;
    const bx = segment.x2 * sx;
    const by = segment.y2 * sy;
    x1 = Math.min(x1, ax, bx);
    y1 = Math.min(y1, ay, by);
    x2 = Math.max(x2, ax, bx);
    y2 = Math.max(y2, ay, by);
  }

  if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
    return null;
  }

  return { x1, y1, x2, y2 };
};

const getPnidAnnotationBounds = (
  state: PnidWizardState,
  lineSegmentScale: { sx: number; sy: number }
): CanvasBounds | null => {
  let bounds: CanvasBounds | null = null;

  for (const equipment of state.equipments) {
    bounds = mergeBounds(bounds, boundsFromBbox(equipment.bbox));
    bounds = mergeBounds(bounds, boundsFromBbox(equipment.label_bbox));
  }
  for (const instrument of state.instruments) {
    bounds = mergeBounds(bounds, boundsFromBbox(instrument.bbox));
  }
  for (const node of state.nodes) {
    bounds = mergeBounds(bounds, node.is_duct ? boundsFromBbox(node.duct_bbox) : boundsFromPolyline(node.polyline));
    bounds = mergeBounds(bounds, boundsFromBbox(node.label_bbox));
    bounds = mergeBounds(
      bounds,
      boundsFromLineSegments(node.line_segments, lineSegmentScale.sx, lineSegmentScale.sy)
    );
  }
  for (const loop of state.control_loops) {
    bounds = mergeBounds(bounds, boundsFromBbox(loop.bbox));
  }

  return bounds;
};

const wizardCanvasItemKey = (target: { kind: WizardCanvasItemKind; id: string }) =>
  `${target.kind}:${target.id}`;

const getPnidAnnotationTargetBounds = (
  state: PnidWizardState,
  target: { kind: WizardCanvasItemKind; id: string },
  lineSegmentScale: { sx: number; sy: number }
): CanvasBounds | null => {
  if (target.kind === 'equipment') {
    const equipment = state.equipments.find((item) => item.id === target.id);
    if (!equipment) return null;
    return mergeBounds(boundsFromBbox(equipment.bbox), boundsFromBbox(equipment.label_bbox));
  }
  if (target.kind === 'node') {
    const node = state.nodes.find((item) => item.id === target.id);
    if (!node) return null;
    return mergeBounds(
      mergeBounds(node.is_duct ? boundsFromBbox(node.duct_bbox) : boundsFromPolyline(node.polyline), boundsFromBbox(node.label_bbox)),
      boundsFromLineSegments(node.line_segments, lineSegmentScale.sx, lineSegmentScale.sy)
    );
  }
  if (target.kind === 'controlLoop') {
    const loop = state.control_loops.find((item) => item.id === target.id);
    return boundsFromBbox(loop?.bbox);
  }
  const instrument = state.instruments.find((item) => item.id === target.id);
  return boundsFromBbox(instrument?.bbox);
};

const parseWizardCanvasItemKey = (key: string): { kind: WizardCanvasItemKind; id: string } | null => {
  const index = key.indexOf(':');
  if (index <= 0 || index === key.length - 1) return null;
  const kind = key.slice(0, index) as WizardCanvasItemKind;
  const id = key.slice(index + 1);
  if (
    kind !== 'equipment' &&
    kind !== 'equipmentInstrument' &&
    kind !== 'nodeInstrument' &&
    kind !== 'node' &&
    kind !== 'controlLoop'
  ) {
    return null;
  }
  return { kind, id };
};

const PidCanvas: React.FC<PidCanvasProps> = ({ 
  imageUrl, 
  sections, 
  onDraw, 
  pendingPieces,
  onSelectSection,
  selectedSectionId,
  onSelectPending,
  selectedPendingId,
  width: widthProp,
  height: heightProp,
  docId,
  imageVersion,
  logicalImageSize,
  inspectionOverlay,
  hidePerfHud = false,
  hideInstructions = false,
  edgePanEnabled = true,
  fitOnFocusClear = false,
  // Wizard mode props
  wizardMode,
  pnidState,
  wizardStep,
  activeEquipmentId,
  activeNodeId,
  selectedInstrumentId = null,
  onRightClickInstrument,
  showActiveOnly = false,
  focusBbox,
  pendingMarkBbox,
  onUpdatePendingMarkBbox,
  selectedWizardItemKeys,
  savedAnnotationEditModeSignal = 0,
  onSelectAllWizardItems,
  onClearWizardItemSelection,
  onDeleteSelectedWizardItems,
  onMarkSelectedAsRedundancySet,
  onShiftSelectedWizardAnnotations,
  onBeginWizardItemBboxUpdate,
  onUpdateWizardItemBbox,
  onQuickDeleteWizardItem,
  onShiftWizardAnnotations,
  onSelectWizardItem,
  identifyingBboxes = [],
  isNodeMarkingMode = false,
  nodeDrawingShape = 'pipeline',
  onNodeDrawingShapeChange,
  nodePointCandidates = [],
  onResolveNodePointCandidate,
  drawTarget,
  onDraftStateChange,
  clearDraftSignal,
  onDoubleClickNode,
  onPathStart,
  segmentReview,
  traceLineNo,
  segmentCoordinateScale,
  onImageSize,
  // Comment mode props
  comments = [],
  selectedCommentId = null,
  onCreateComment,
  onSelectComment,
  showResolvedComments = false,
  onToggleCommentMode,
  walkthroughHighlights = null,
  onCrop,
  // Template annotation props
  templateAnnotations = [],
  unsavedMatches,
  templateVisibility,
  selectedAnnotationIds,
  activeAnnotationId,
  onDeleteAnnotation,
  onSelectAnnotation,
  onAnnotationClick,
  onUpdateAnnotationBbox,
  onBulkDeleteAnnotations,
  onClearAnnotationSelection,
  onAttachTemplateAnnotations,
  onDirectAttachAnnotation,
  draftAttachedAnnotationIds,
  draftDetachedAnnotationIds,
  highlightedEntityId,
  readOnly = false,
  onInstrumentSubModeToggle,
  collectingRegions = false,
  draftRegions = [],
  onFinishRegions,
  onRemoveLastRegion,
  onExitCollect,
  onCanvasInteraction,
}) => {
  const [desiredImageMaxDim, setDesiredImageMaxDim] = useState<number>(INITIAL_PNID_IMAGE_LEVEL);
  const [resolvedImageUrl, setResolvedImageUrl] = useState('');
  const [resolvedImageMeta, setResolvedImageMeta] = useState<ResolvedImageUrl | null>(null);
  const [activeImage, setActiveImage] = useState<HTMLImageElement | undefined>(undefined);
  const [activeImageMeta, setActiveImageMeta] = useState<ResolvedImageUrl | null>(null);
  const activeObjectUrlRef = useRef<string | null>(null);
  const pendingRevokeUrlRef = useRef<string | null>(null);
  const imageCacheVersion = imageVersion || (docId ? imageUrl : undefined);
  const canvasImageUrl = docId
    ? documentApi.getImageUrl(docId, desiredImageMaxDim, imageCacheVersion)
    : appendImageCacheVersion(imageUrl, imageVersion);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!canvasImageUrl) {
      setResolvedImageUrl('');
      setResolvedImageMeta(null);
      return undefined;
    }

    documentApi.resolveImageUrl(canvasImageUrl)
      .then((resolved) => {
        if (cancelled) {
          if (resolved.revoke) window.URL.revokeObjectURL(resolved.url);
          return;
        }
        objectUrl = resolved.revoke ? resolved.url : null;
        setResolvedImageMeta(resolved);
        setResolvedImageUrl(resolved.url);
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Could not load P&ID image:', error);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        pendingRevokeUrlRef.current = objectUrl;
      }
    };
  }, [canvasImageUrl]);

  const [loadedImage] = useImage(resolvedImageUrl, 'anonymous');
  useEffect(() => {
    if (!loadedImage) return;
    if (resolvedImageUrl && loadedImage.src !== resolvedImageUrl) return;
    const previousObjectUrl = activeObjectUrlRef.current;
    setActiveImage(loadedImage);
    setActiveImageMeta(resolvedImageMeta);
    activeObjectUrlRef.current = resolvedImageMeta?.revoke ? resolvedImageMeta.url : null;
    if (previousObjectUrl && previousObjectUrl !== activeObjectUrlRef.current) {
      window.URL.revokeObjectURL(previousObjectUrl);
    }
    if (pendingRevokeUrlRef.current && pendingRevokeUrlRef.current !== activeObjectUrlRef.current) {
      window.URL.revokeObjectURL(pendingRevokeUrlRef.current);
      pendingRevokeUrlRef.current = null;
    }
  }, [loadedImage, resolvedImageMeta, resolvedImageUrl]);

  useEffect(() => () => {
    if (activeObjectUrlRef.current) window.URL.revokeObjectURL(activeObjectUrlRef.current);
    if (pendingRevokeUrlRef.current) window.URL.revokeObjectURL(pendingRevokeUrlRef.current);
  }, []);

  const image = activeImage;
  const imageCanvasWidth = logicalImageSize?.width || activeImageMeta?.originalWidth || image?.naturalWidth || image?.width || null;
  const imageCanvasHeight = logicalImageSize?.height || activeImageMeta?.originalHeight || image?.naturalHeight || image?.height || null;

  // Canvas interaction batching — flushes accumulated counts every 10s
  const interactionCounts = useRef<Record<string, number>>({});
  useEffect(() => {
    const timer = setInterval(() => {
      const counts = interactionCounts.current;
      if (Object.keys(counts).length > 0) {
        performance.mark('canvas-flush-start');
        onCanvasInteraction?.({ ...counts });
        performance.mark('canvas-flush-end');
        performance.measure('canvas-flush', 'canvas-flush-start', 'canvas-flush-end');
        interactionCounts.current = {};
      }
    }, 10_000);
    return () => clearInterval(timer);
  }, [onCanvasInteraction]);
  const trackCanvasEvent = useCallback((type: string) => {
    interactionCounts.current[type] = (interactionCounts.current[type] ?? 0) + 1;
  }, []);

  // Reviewer mode: only allow select (pan) and comment
  const effectiveReadOnly = readOnly;
  const equipmentInstruments = useMemo(
    () => getEquipmentContextInstruments(pnidState),
    [pnidState],
  );
  const nodeInstruments = useMemo(
    () => getNodeContextInstruments(pnidState),
    [pnidState],
  );
  const unattachedInstruments = useMemo(
    () => getUnattachedInstruments(pnidState),
    [pnidState],
  );
  const nodesById = useMemo(() => {
    const map = new Map<string, PnidWizardState['nodes'][number]>();
    for (const node of pnidState?.nodes ?? []) {
      map.set(node.id, node);
    }
    return map;
  }, [pnidState]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingBox, setDrawingBox] = useState<DrawingBox | null>(null);
  const [drawMode, setDrawMode] = useState<DrawMode>('select');
  const [isInstrumentSubMode, setIsInstrumentSubMode] = useState(false);
  const previousSavedSelectionCountRef = useRef(0);

  React.useEffect(() => {
    if (effectiveReadOnly && drawMode !== 'select' && drawMode !== 'comment') {
      setDrawMode('select');
    }
  }, [effectiveReadOnly, drawMode]);

  const [polyPoints, setPolyPoints] = useState<number[]>([]); // [x1,y1,x2,y2,...]
  const [isPolyDrawing, setIsPolyDrawing] = useState(false);
  // Path drawing state (click to add points, for connections)
  const [pathPoints, setPathPoints] = useState<number[]>([]); // [x1,y1,x2,y2,...]
  const [pathPreviewPoint, setPathPreviewPoint] = useState<{ x: number; y: number } | null>(null);
  // Path undo/redo stack - stores [x, y] pairs that were undone
  const [pathRedoStack, setPathRedoStack] = useState<number[][]>([]);
  const traceAssistAvailable = import.meta.env.DEV;
  // Trace assist (arrow keys to next bend)
  const [traceAssistEnabled, setTraceAssistEnabled] = useState<boolean>(
    Boolean(traceAssistAvailable && wizardMode && wizardStep === 4)
  );
  const [traceAssistStatus, setTraceAssistStatus] = useState<string | null>(null);
  const [traceAssistBusy, setTraceAssistBusy] = useState(false);
  const traceAssistBusyRef = useRef(false);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const cursorPositionRef = useRef<{ x: number; y: number } | null>(null);
  const cursorRafRef = useRef<number | null>(null);
  // Space key for temporary pan mode (Figma-style)
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  
  // Hover tooltip state
  const [hoveredItem, setHoveredItem] = useState<HoveredItem | null>(null);
  // Clearing hover is slightly delayed so the resize handles / quick-delete badge
  // rendered on hover stay mounted while the pointer travels onto them.
  const hoverClearTimeoutRef = useRef<number | null>(null);
  const cancelHoverClear = useCallback(() => {
    if (hoverClearTimeoutRef.current !== null) {
      window.clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }
  }, []);
  const scheduleHoverClear = useCallback(() => {
    cancelHoverClear();
    hoverClearTimeoutRef.current = window.setTimeout(() => {
      hoverClearTimeoutRef.current = null;
      setHoveredItem(null);
    }, 120);
  }, [cancelHoverClear]);
  useEffect(() => () => cancelHoverClear(), [cancelHoverClear]);
  
  // Crop mode state
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [cropEnd, setCropEnd] = useState<{ x: number; y: number } | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  
  // Template annotation hover state
  const [hoveredAnnotationId, setHoveredAnnotationId] = useState<string | null>(null);
  
  // Select annotations mode state (rectangle selection)
  const [selectRectStart, setSelectRectStart] = useState<{ x: number; y: number } | null>(null);
  const [selectRectEnd, setSelectRectEnd] = useState<{ x: number; y: number } | null>(null);
  const [isSelectingAnnotations, setIsSelectingAnnotations] = useState(false);
  const [annotationShiftPreview, setAnnotationShiftPreview] = useState({ dx: 0, dy: 0 });
  const [isAnnotationShiftSaving, setIsAnnotationShiftSaving] = useState(false);
  const [selectedAnnotationShiftPreview, setSelectedAnnotationShiftPreview] = useState({ dx: 0, dy: 0 });
  const [isSelectedAnnotationShiftSaving, setIsSelectedAnnotationShiftSaving] = useState(false);

  // Attachment rect selection state (Cmd/Ctrl + drag to attach/detach in steps 3/4)
  const [attachRectStart, setAttachRectStart] = useState<{ x: number; y: number } | null>(null);
  const [attachRectEnd, setAttachRectEnd] = useState<{ x: number; y: number } | null>(null);
  const [isAttachingAnnotations, setIsAttachingAnnotations] = useState(false);
  const [attachAction, setAttachAction] = useState<'attach' | 'detach'>('attach');
  
  // Track whether Step 2 selection should be additive (Shift held at drag start)
  const selectAdditiveRef = useRef(false);

  // Clear crop state when leaving crop mode
  useEffect(() => {
    if (drawMode !== 'crop') {
      setCropStart(null);
      setCropEnd(null);
      setIsCropping(false);
    }
  }, [drawMode]);

  useEffect(() => {
    if (drawMode !== 'shift-annotations') {
      setAnnotationShiftPreview({ dx: 0, dy: 0 });
    }
  }, [drawMode]);
  
  // Dynamic container size when width/height props are not provided
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: widthProp ?? 800,
    height: heightProp ?? 600,
  });

  // Observe parent size changes
  useEffect(() => {
    if (widthProp && heightProp) return; // static sizing provided

    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize((current) => (
          current.width === width && current.height === height
            ? current
            : { width, height }
        ));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [widthProp, heightProp]);

  // Decide working width & height
  const width = widthProp ?? containerSize.width;
  const height = heightProp ?? containerSize.height;
  const [interactionImage, setInteractionImage] = useState<HTMLCanvasElement | null>(null);
  const [isZoomInteractionActive, setIsZoomInteractionActive] = useState(false);
  const [isPanInteractionActive, setIsPanInteractionActive] = useState(false);

  const lastImageSizeRef = useRef<{ width: number; height: number } | null>(null);
  useEffect(() => {
    if (!image) return;
    if (!onImageSize) return;
    const w = imageCanvasWidth || (image as any).naturalWidth || image.width;
    const h = imageCanvasHeight || (image as any).naturalHeight || image.height;
    if (!w || !h) return;
    const last = lastImageSizeRef.current;
    if (last && last.width === w && last.height === h) return;
    lastImageSizeRef.current = { width: w, height: h };
    onImageSize(w, h);
  }, [image, imageCanvasHeight, imageCanvasWidth, onImageSize]);

  const interactionImageIdleTimerRef = useRef<number | null>(null);
  const panInteractionIdleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!image) {
      setInteractionImage(null);
      return;
    }

    const sourceWidth = (image as HTMLImageElement).naturalWidth || image.width;
    const sourceHeight = (image as HTMLImageElement).naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) return;

    const maxSourceDimension = Math.max(sourceWidth, sourceHeight);
    if (maxSourceDimension <= INTERACTION_IMAGE_MAX_DIMENSION) {
      setInteractionImage(null);
      return;
    }

    let cancelled = false;
    const buildInteractionImage = () => {
      const scaleFactor = INTERACTION_IMAGE_MAX_DIMENSION / maxSourceDimension;
      const renderWidth = Math.max(1, Math.round(sourceWidth * scaleFactor));
      const renderHeight = Math.max(1, Math.round(sourceHeight * scaleFactor));
      const canvas = document.createElement('canvas');
      canvas.width = renderWidth;
      canvas.height = renderHeight;

      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'low';
      ctx.drawImage(image, 0, 0, sourceWidth, sourceHeight, 0, 0, renderWidth, renderHeight);

      if (cancelled) return;

      setInteractionImage(canvas);
    };

    const idleCallback = window.requestIdleCallback;
    if (typeof idleCallback === 'function') {
      const id = idleCallback(buildInteractionImage, { timeout: 500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback?.(id);
      };
    }

    const timeoutId = window.setTimeout(buildInteractionImage, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [image]);

  // Zoom and pan state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Refs to track latest scale/position for wheel handler (avoids stale closure)
  const scaleRef = useRef(scale);
  const positionRef = useRef(position);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  // True while the edge-pan loop, or a held arrow key, writes `positionRef`
  // directly. The passive sync below would otherwise rewind the ref to the value
  // React committed one or two frames ago, and the drawing would jerk backwards
  // on every throttled commit.
  const edgePanMovingRef = useRef(false);
  const keyPanActiveRef = useRef(false);
  const isImperativePanActive = useCallback(
    () => edgePanMovingRef.current || keyPanActiveRef.current,
    []
  );
  useEffect(() => {
    if (edgePanMovingRef.current || keyPanActiveRef.current) return;
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    setDesiredImageMaxDim(INITIAL_PNID_IMAGE_LEVEL);
    setActiveImage(undefined);
    setActiveImageMeta(null);
    setResolvedImageUrl('');
    setResolvedImageMeta(null);
    if (activeObjectUrlRef.current) {
      window.URL.revokeObjectURL(activeObjectUrlRef.current);
      activeObjectUrlRef.current = null;
    }
    if (pendingRevokeUrlRef.current) {
      window.URL.revokeObjectURL(pendingRevokeUrlRef.current);
      pendingRevokeUrlRef.current = null;
    }
  }, [docId, imageUrl]);

  useEffect(() => {
    if (!docId || !imageCanvasWidth || !imageCanvasHeight) return;
    const timer = window.setTimeout(() => {
      const nextLevel = choosePnidImageLevel(scaleRef.current, imageCanvasWidth, imageCanvasHeight);
      setDesiredImageMaxDim((current) => (current === nextLevel ? current : nextLevel));
    }, IMAGE_LEVEL_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [docId, imageCanvasHeight, imageCanvasWidth, scale]);
  
  const stageRef = useRef<any>(null);
  const panMovedRef = useRef(false);
  const panPointerStartRef = useRef<{ x: number; y: number } | null>(null);

  /** Unscaled drawing size, or null before the drawing loads. */
  const panImageSize = useMemo<PanImageSize | null>(() => {
    const drawingWidth = imageCanvasWidth || image?.width || 0;
    const drawingHeight = imageCanvasHeight || image?.height || 0;
    if (!(drawingWidth > 0) || !(drawingHeight > 0)) return null;
    return { width: drawingWidth, height: drawingHeight };
  }, [image, imageCanvasHeight, imageCanvasWidth]);

  /**
   * Latest inputs every pan path needs, held in one ref.
   *
   * The edge-pan loop runs on animation frames rather than renders, and the
   * wheel handler is declared before the values it clamps against exist. Both
   * read this instead of closing over state that would be a render behind.
   */
  const panContextRef = useRef<{
    viewport: PanViewport;
    imageSize: PanImageSize | null;
    /** True while the SME already drives the drawing by hand. */
    suppressed: boolean;
    /** True while a gesture tracks the image coordinate under the cursor. */
    gestureInFlight: boolean;
  }>({
    viewport: { width: 0, height: 0 },
    imageSize: null,
    suppressed: false,
    gestureInFlight: false,
  });
  useEffect(() => {
    panContextRef.current = {
      viewport: { width, height },
      imageSize: panImageSize,
      // A manual drag pan and a held Space bar already move the drawing. Edge
      // pan must not push against them.
      suppressed: isPanning || isSpaceHeld || !edgePanEnabled,
      gestureInFlight:
        isDrawing
        || isCropping
        || isPolyDrawing
        || isSelectingAnnotations
        || isAttachingAnnotations
        || (drawMode === 'path' && pathPoints.length >= 2),
    };
  }, [
    drawMode,
    edgePanEnabled,
    height,
    isAttachingAnnotations,
    isCropping,
    isDrawing,
    isPanning,
    isPolyDrawing,
    isSelectingAnnotations,
    isSpaceHeld,
    panImageSize,
    pathPoints.length,
    width,
  ]);
  const usingInteractionImage = Boolean(interactionImage) && (isPanning || isZoomInteractionActive || isPanInteractionActive);
  const isInteractionLightMode = isPanning || isZoomInteractionActive || isPanInteractionActive;
  const isOverviewLightMode = Boolean(wizardMode && scale < OVERVIEW_DETAIL_SCALE);
  const reducePassiveOverlays = isInteractionLightMode || isOverviewLightMode;
  const suppressHoverInspection = isInteractionLightMode;
  useEffect(() => {
    if (suppressHoverInspection) {
      setHoveredItem(null);
    }
  }, [suppressHoverInspection]);
  const viewportBounds = useMemo<CanvasBounds>(() => {
    const safeScale = Math.max(scale, 1e-6);
    const padding = Math.max(
      VIEWPORT_CULL_MIN_IMAGE_PADDING,
      VIEWPORT_CULL_PADDING_SCREEN_PX / safeScale
    );

    return {
      x1: (-position.x) / safeScale - padding,
      y1: (-position.y) / safeScale - padding,
      x2: (width - position.x) / safeScale + padding,
      y2: (height - position.y) / safeScale + padding,
    };
  }, [height, position.x, position.y, scale, width]);
  const isInViewport = useCallback(
    (bounds: CanvasBounds | BoundingBox | null | undefined, force = false): boolean => {
      if (force) return true;
      if (!bounds) return false;
      return boundsIntersect(bounds, viewportBounds);
    },
    [viewportBounds]
  );

  /* Perf HUD: a debugging instrument, so it is opt-IN, not opt-out. It used to
     default on, which made a black debug panel the loudest element on the page
     for every user. `?canvasPerf=1` or `localStorage.canvasPerf = "1"` turns it
     on; in a dev build it also stays on once enabled. */
  const [perfDebugEnabled, setPerfDebugEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const qp = params.get('canvasPerf');
    if (qp === '0') return false;
    if (qp === '1') return true;
    /* A NEW key: the old default-on build persisted canvasPerf="1" for every
       user, so honouring it would keep the panel up forever. */
    const ls = window.localStorage.getItem('pnid_canvas_perf');
    if (ls === '1') return true;
    return false;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (perfDebugEnabled) window.localStorage.setItem('pnid_canvas_perf', '1');
    else window.localStorage.removeItem('pnid_canvas_perf');
  }, [perfDebugEnabled]);

  const [nodePointMode, setNodePointMode] = useState<NodePointRenderMode>(() => {
    if (typeof window === 'undefined') return 'active';
    const raw = window.localStorage.getItem('pidcanvas_nodePointMode');
    return raw === 'all' || raw === 'active' || raw === 'none' ? raw : 'active';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('pidcanvas_nodePointMode', nodePointMode);
  }, [nodePointMode]);

  const perfStats = useMemo(() => {
    if (!pnidState) return null;
    let totalNodePoints = 0;
    let nodesWithPolyline = 0;
    for (const node of pnidState.nodes) {
      if (!node.polyline || node.polyline.length < 4) continue;
      nodesWithPolyline += 1;
      totalNodePoints += node.polyline.length / 2;
    }
    return {
      nodes: pnidState.nodes.length,
      nodesWithPolyline,
      totalNodePoints,
    };
  }, [pnidState]);

  const activatePanInteractionRaster = useCallback(() => {
    setIsPanInteractionActive(true);
    if (panInteractionIdleTimerRef.current !== null) {
      window.clearTimeout(panInteractionIdleTimerRef.current);
      panInteractionIdleTimerRef.current = null;
    }
  }, []);

  const releasePanInteractionRasterSoon = useCallback(() => {
    if (panInteractionIdleTimerRef.current !== null) {
      window.clearTimeout(panInteractionIdleTimerRef.current);
    }
    panInteractionIdleTimerRef.current = window.setTimeout(() => {
      setIsPanInteractionActive(false);
      panInteractionIdleTimerRef.current = null;
    }, INTERACTION_IMAGE_IDLE_MS);
  }, []);

  const getPointerScreenPosition = useCallback((): { x: number; y: number } | undefined => {
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition?.();
    const container = stage?.container?.();
    if (!pointer || !container) return undefined;
    const rect = container.getBoundingClientRect();
    return { x: rect.left + pointer.x, y: rect.top + pointer.y };
  }, []);

  const segmentReviewNode = useMemo(() => {
    if (!wizardMode || !pnidState) return null;
    const nodeId = segmentReview?.nodeId;
    if (!nodeId) return null;
    return pnidState.nodes.find((n) => n.id === nodeId) ?? null;
  }, [wizardMode, pnidState, segmentReview?.nodeId]);

  const segmentReviewRaw = segmentReviewNode?.line_segments ?? [];

  const segScale = useMemo(() => {
    const sx = segmentCoordinateScale?.sx ?? 1;
    const sy = segmentCoordinateScale?.sy ?? 1;
    return { sx, sy };
  }, [segmentCoordinateScale?.sx, segmentCoordinateScale?.sy]);

  const annotationBounds = useMemo(() => {
    if (!wizardMode || !pnidState) return null;
    return getPnidAnnotationBounds(pnidState, segScale);
  }, [wizardMode, pnidState, segScale]);

  const selectedAnnotationBounds = useMemo(() => {
    if (!wizardMode || !pnidState || !selectedWizardItemKeys?.size) return null;
    let bounds: CanvasBounds | null = null;
    for (const key of selectedWizardItemKeys) {
      const target = parseWizardCanvasItemKey(key);
      if (!target) continue;
      bounds = mergeBounds(bounds, getPnidAnnotationTargetBounds(pnidState, target, segScale));
    }
    return bounds;
  }, [wizardMode, pnidState, selectedWizardItemKeys, segScale]);

  const shouldRenderSelectedShiftBounds = useMemo(() => {
    const count = selectedWizardItemKeys?.size ?? 0;
    if (count > 1) return true;
    if (count !== 1) return false;
    const first = selectedWizardItemKeys ? Array.from(selectedWizardItemKeys)[0] : null;
    return parseWizardCanvasItemKey(first ?? "")?.kind === "node";
  }, [selectedWizardItemKeys]);

  const segmentReviewStatusById = useMemo(() => {
    const m = new Map<string, LineSegmentStatus>();
    for (const s of segmentReviewRaw) m.set(s.id, s.status);
    return m;
  }, [segmentReviewRaw]);

  const segmentReviewRawScaled = useMemo(() => {
    if (segScale.sx === 1 && segScale.sy === 1) return segmentReviewRaw;
    return segmentReviewRaw.map((s) => ({
      ...s,
      x1: s.x1 * segScale.sx,
      y1: s.y1 * segScale.sy,
      x2: s.x2 * segScale.sx,
      y2: s.y2 * segScale.sy,
    }));
  }, [segmentReviewRaw, segScale.sx, segScale.sy]);

  const segmentReviewDistinct = useMemo<MergedLineSegment[]>(() => {
    if (!segmentReviewRawScaled.length) return [];
    return mergeOverlappingLineSegments(
      segmentReviewRawScaled.map((s) => ({
        id: s.id,
        x1: s.x1,
        y1: s.y1,
        x2: s.x2,
        y2: s.y2,
        kind: s.kind ?? undefined,
        confidence: s.confidence ?? undefined,
      }))
    );
  }, [segmentReviewRawScaled]);

  useEffect(() => {
    return () => {
      if (interactionImageIdleTimerRef.current !== null) {
        window.clearTimeout(interactionImageIdleTimerRef.current);
      }
      if (panInteractionIdleTimerRef.current !== null) {
        window.clearTimeout(panInteractionIdleTimerRef.current);
      }
    };
  }, []);

  const deriveDistinctStatus = useCallback(
    (distinct: MergedLineSegment): LineSegmentStatus => {
      const statuses = distinct.source_segment_ids
        .map((id) => segmentReviewStatusById.get(id))
        .filter(Boolean) as LineSegmentStatus[];
      if (!statuses.length) return 'pending';
      if (statuses.every((s) => s === 'deleted')) return 'deleted';
      if (statuses.some((s) => s === 'edited')) return 'edited';
      if (statuses.some((s) => s === 'pending')) return 'pending';
      if (statuses.every((s) => s === 'confirmed' || s === 'deleted')) return 'confirmed';
      return 'pending';
    },
    [segmentReviewStatusById]
  );

  const getSegmentStroke = useCallback(
    (status: LineSegmentStatus): { stroke: string; dash?: number[] } => {
      switch (status) {
        case 'confirmed':
          return { stroke: '#22c55e' };
        case 'edited':
          return { stroke: '#f59e0b' };
        case 'deleted':
          return { stroke: '#ef4444', dash: [8 / scale, 6 / scale] };
        case 'pending':
        default:
          // Lighter green for inactive stroke contrast
          return { stroke: '#4ade80', dash: [6 / scale, 6 / scale] };
      }
    },
    [scale]
  );

  const traceTargetNode = useMemo(() => {
    if (!pnidState) return null;
    const nodeId = activeNodeId ?? segmentReview?.nodeId ?? null;
    if (!nodeId) return null;
    return pnidState.nodes.find((n) => n.id === nodeId) ?? null;
  }, [pnidState, activeNodeId, segmentReview?.nodeId]);

  const appendTracePoints = useCallback((points: { x: number; y: number }[]) => {
    if (!points.length) return;
    setPathPoints((prev) => {
      const next = [...prev];
      for (const p of points) {
        if (next.length >= 2) {
          const lx = next[next.length - 2];
          const ly = next[next.length - 1];
          if (Math.hypot(p.x - lx, p.y - ly) < 0.5) {
            continue;
          }
          if (next.length >= 4) {
            const px = next[next.length - 4];
            const py = next[next.length - 3];
            const dx = lx - px;
            const dy = ly - py;
            if (Math.abs(dx) >= Math.abs(dy)) {
              if (Math.abs(p.y - ly) <= 0.5) {
                next[next.length - 2] = p.x;
                next[next.length - 1] = ly;
                continue;
              }
            } else {
              if (Math.abs(p.x - lx) <= 0.5) {
                next[next.length - 2] = lx;
                next[next.length - 1] = p.y;
                continue;
              }
            }
          }
        }
        next.push(p.x, p.y);
      }
      return next;
    });
    setPathRedoStack([]);
  }, []);

  const traceNextStep = useCallback(async (directionOverride?: { dx: number; dy: number }) => {
    if (!traceAssistAvailable || !docId || !traceAssistEnabled || traceAssistBusyRef.current) return;
    if (drawMode !== 'path') return;
    if (pendingMarkBbox) return;

    const sx = segScale.sx || 1;
    const sy = segScale.sy || 1;
    const toAi = (x: number, y: number) => ({ x: x / sx, y: y / sy });
    const toCanvas = (x: number, y: number) => ({ x: x * sx, y: y * sy });

    let startX: number | null = null;
    let startY: number | null = null;
    let dirX: number | undefined;
    let dirY: number | undefined;
    const useOverride = Boolean(directionOverride);

    if (pathPoints.length >= 2) {
      startX = pathPoints[pathPoints.length - 2];
      startY = pathPoints[pathPoints.length - 1];
      if (!useOverride && pathPoints.length >= 4) {
        const prevX = pathPoints[pathPoints.length - 4];
        const prevY = pathPoints[pathPoints.length - 3];
        dirX = startX - prevX;
        dirY = startY - prevY;
      }
    } else {
      const selectedDistinct =
        segmentReviewDistinct.find((seg) => seg.id === segmentReview?.selectedDistinctId) ??
        segmentReviewDistinct[0] ??
        null;
      if (selectedDistinct) {
        startX = selectedDistinct.x1;
        startY = selectedDistinct.y1;
        if (!useOverride) {
          dirX = selectedDistinct.x2 - selectedDistinct.x1;
          dirY = selectedDistinct.y2 - selectedDistinct.y1;
        }
        appendTracePoints([{ x: startX, y: startY }]);
      }
    }

    if (startX === null || startY === null) {
      setTraceAssistStatus('Click to set a start point or select a segment.');
      return;
    }

    if (useOverride) {
      dirX = directionOverride?.dx;
      dirY = directionOverride?.dy;
    }

    traceAssistBusyRef.current = true;
    setTraceAssistBusy(true);
    setTraceAssistStatus(null);

    try {
      const startAi = toAi(startX, startY);
      const dirAi = dirX !== undefined && dirY !== undefined ? toAi(dirX, dirY) : null;
      const lineNo = traceLineNo?.trim() || traceTargetNode?.line_no?.trim();
      const minStepCanvas = 200;
      const perpToleranceCanvas = 20;
      const startToleranceCanvas = 20;
      const axisIsHorizontal =
        dirX !== undefined && dirY !== undefined ? Math.abs(dirX) >= Math.abs(dirY) : true;
      const axisScale = axisIsHorizontal ? sx : sy;
      const perpScale = axisIsHorizontal ? sy : sx;
      const minStepAi = axisScale ? minStepCanvas / axisScale : minStepCanvas;
      const perpToleranceAi = perpScale ? perpToleranceCanvas / perpScale : perpToleranceCanvas;
      const avgScale = (sx + sy) / 2 || 1;
      const maxDistanceAi = startToleranceCanvas / avgScale;

      const tracePayload = {
        line_no: lineNo || undefined,
        x: startAi.x,
        y: startAi.y,
        dx: dirAi?.x,
        dy: dirAi?.y,
        min_step_px: minStepAi,
        perp_tolerance_px: perpToleranceAi,
        max_distance_px: maxDistanceAi,
      };
      console.log('[trace-next] request', tracePayload);
      const result = await pnidApi.traceNext(docId, tracePayload);
      console.log('[trace-next] response', result);

      if (!result?.next_point) {
        setTraceAssistStatus(`No trace: ${result?.reason || 'unavailable'}`);
        return;
      }

      const points = (result.points && result.points.length > 0 ? result.points : [result.next_point])
        .map((p) => toCanvas(p.x, p.y));

      const last = { x: startX, y: startY };
      const filtered = points.filter((p, idx) => {
        if (idx === 0 && Math.hypot(p.x - last.x, p.y - last.y) < 0.5) return false;
        return true;
      });
      appendTracePoints(filtered);
      setTraceAssistStatus(result.reason ? `Trace: ${result.reason}` : null);
    } catch (err) {
      console.error('Trace assist failed:', err);
      setTraceAssistStatus('Trace failed.');
    } finally {
      traceAssistBusyRef.current = false;
      setTraceAssistBusy(false);
    }
  }, [
    docId,
    traceAssistEnabled,
    drawMode,
    pendingMarkBbox,
    segScale.sx,
    segScale.sy,
    pathPoints,
    segmentReviewDistinct,
    segmentReview?.selectedDistinctId,
    appendTracePoints,
    traceTargetNode?.line_no,
  ]);

  // Zoom constraints

  const hasDraft = Boolean(
    isDrawing ||
    drawingBox ||
    isPolyDrawing ||
    polyPoints.length > 0 ||
    pathPoints.length > 0 ||
    isAnnotationShiftSaving ||
    annotationShiftPreview.dx !== 0 ||
    annotationShiftPreview.dy !== 0 ||
    isSelectedAnnotationShiftSaving ||
    selectedAnnotationShiftPreview.dx !== 0 ||
    selectedAnnotationShiftPreview.dy !== 0
  );

  useEffect(() => {
    onDraftStateChange?.(hasDraft);
  }, [hasDraft, onDraftStateChange]);

  useEffect(() => {
    if (clearDraftSignal === undefined) return;
    setIsDrawing(false);
    setDrawingBox(null);
    setIsPolyDrawing(false);
    setPolyPoints([]);
    setPathPoints([]);
    setPathPreviewPoint(null);
    setAnnotationShiftPreview({ dx: 0, dy: 0 });
  }, [clearDraftSignal]);

  useEffect(() => {
    if (pendingMarkBbox) {
      setPathPreviewPoint(null);
    }
  }, [pendingMarkBbox]);

  useEffect(() => {
    if (!traceAssistAvailable || !wizardMode || wizardStep !== 4) {
      setTraceAssistEnabled(false);
      setTraceAssistStatus(null);
      setTraceAssistBusy(false);
      traceAssistBusyRef.current = false;
      return;
    }
    setTraceAssistEnabled(true);
  }, [traceAssistAvailable, wizardMode, wizardStep]);

  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault();
    
    const stage = stageRef.current;
    if (!stage) return;

    // Use refs to get latest values (avoids stale closure on first zoom after fit)
    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    // Velocity-based zoom: use deltaY magnitude
    const deltaY = Math.abs(e.evt.deltaY);
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    
    // Map deltaY to zoom factor (small scroll = 1.02, fast scroll = 1.15)
    // Typical deltaY: trackpad ~1-10, mouse wheel ~50-150
    const minFactor = 1.02;
    const maxFactor = 1.15;
    const normalizedDelta = Math.min(deltaY / 100, 1); // clamp to 0-1
    const factor = minFactor + (maxFactor - minFactor) * normalizedDelta;
    
    const zoomed = zoomAtPoint(currentPosition, currentScale, pointer, Math.pow(factor, direction));
    const newScale = zoomed.scale;
    const newPosition = clampPanPosition(
      zoomed.position,
      newScale,
      panContextRef.current.viewport,
      panContextRef.current.imageSize
    );

    setIsZoomInteractionActive(true);
    if (interactionImageIdleTimerRef.current !== null) {
      window.clearTimeout(interactionImageIdleTimerRef.current);
    }
    interactionImageIdleTimerRef.current = window.setTimeout(() => {
      setIsZoomInteractionActive(false);
      interactionImageIdleTimerRef.current = null;
    }, INTERACTION_IMAGE_IDLE_MS);

    setScale(newScale);
    setPosition(newPosition);
    trackCanvasEvent('zoom');
  }, [trackCanvasEvent]);

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

  const clampBboxToImage = useCallback((bbox: BoundingBox) => {
    if (!image) return bbox;
    const maxX = imageCanvasWidth || image.width;
    const maxY = imageCanvasHeight || image.height;
    return {
      ...bbox,
      x1: clamp(bbox.x1, 0, maxX),
      y1: clamp(bbox.y1, 0, maxY),
      x2: clamp(bbox.x2, 0, maxX),
      y2: clamp(bbox.y2, 0, maxY),
    };
  }, [image, imageCanvasHeight, imageCanvasWidth]);

  const clampTranslatedBboxToImage = useCallback((bbox: BoundingBox) => {
    if (!image) return bbox;
    const maxX = imageCanvasWidth || image.width;
    const maxY = imageCanvasHeight || image.height;
    const width = Math.max(0, bbox.x2 - bbox.x1);
    const height = Math.max(0, bbox.y2 - bbox.y1);
    const x1 = clamp(bbox.x1, 0, Math.max(0, maxX - width));
    const y1 = clamp(bbox.y1, 0, Math.max(0, maxY - height));
    return {
      ...bbox,
      x1,
      y1,
      x2: x1 + width,
      y2: y1 + height,
    };
  }, [image, imageCanvasHeight, imageCanvasWidth]);

  // Simple polygon simplification (Douglas-Peucker-like)
  const simplifyPolygon = useCallback((points: number[], tolerance: number = 2): number[] => {
    if (points.length <= 6) return points; // keep if 3 or fewer points

    const simplified: number[] = [points[0], points[1]]; // always keep first point

    for (let i = 2; i < points.length - 1; i += 2) {
      const prevX = simplified[simplified.length - 2];
      const prevY = simplified[simplified.length - 1];
      const currX = points[i];
      const currY = points[i + 1];

      // Keep point if it's far enough from the previous kept point
      const dist = Math.sqrt((currX - prevX) ** 2 + (currY - prevY) ** 2);
      if (dist > tolerance) {
        simplified.push(currX, currY);
      }
    }

    // Always keep the last point
    simplified.push(points[points.length - 2], points[points.length - 1]);

    return simplified;
  }, []);

  // Extract cropped region as a Blob (for template creation)
  const extractCrop = useCallback(async (rect: { x: number; y: number; width: number; height: number }): Promise<Blob> => {
    if (!image) throw new Error('No image loaded');
    
    const canvas = document.createElement('canvas');
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    
    const logicalWidth = imageCanvasWidth || image.width;
    const logicalHeight = imageCanvasHeight || image.height;
    const sx = image.width / logicalWidth;
    const sy = image.height / logicalHeight;

    // use-image returns an object compatible with drawImage. Canvas coordinates stay in original image space,
    // so crop source coordinates must be scaled into the currently loaded image level.
    ctx.drawImage(
      image,
      rect.x * sx,
      rect.y * sy,
      rect.width * sx,
      rect.height * sy,
      0,
      0,
      rect.width,
      rect.height
    );
    
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob from canvas'));
      }, 'image/png');
    });
  }, [image, imageCanvasHeight, imageCanvasWidth]);

  // translate bbox top-right into viewport coords and offset 15px to right
  const computeAnchor = useCallback((box: BoundingBox): { x: number; y: number } => {
    if (!containerRef.current) return { x: 20, y: 20 };
    const rect = containerRef.current.getBoundingClientRect();
    const xCanvas = box.x2 * scaleRef.current + positionRef.current.x;
    const yCanvas = box.y1 * scaleRef.current + positionRef.current.y;
    return { x: rect.left + xCanvas + 15, y: rect.top + yCanvas };
  }, []);

  // === Template Annotation Attachment Helpers (Steps 3/4) ===
  const NEARBY_PADDING = 300; // pixels for nearby threshold

  // Get active equipment bbox (for step 3)
  const activeEquipmentBbox = useMemo(() => {
    if (!pnidState || !activeEquipmentId) return null;
    const eq = pnidState.equipments.find(e => e.id === activeEquipmentId);
    return eq?.bbox ?? null;
  }, [pnidState, activeEquipmentId]);

  // Get active node polyline (for step 4)
  const activeNodePolyline = useMemo(() => {
    if (!pnidState || !activeNodeId) return null;
    const node = pnidState.nodes.find(n => n.id === activeNodeId);
    return node?.polyline ?? null;
  }, [pnidState, activeNodeId]);

  // Helper: compute distance from point to line segment
  const pointToSegmentDistance = useCallback((px: number, py: number, x1: number, y1: number, x2: number, y2: number): number => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      return Math.hypot(px - x1, py - y1);
    }
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
    const nearX = x1 + t * dx;
    const nearY = y1 + t * dy;
    return Math.hypot(px - nearX, py - nearY);
  }, []);

  // Helper: compute minimum distance from point to polyline
  const pointToPolylineDistance = useCallback((px: number, py: number, polyline: number[]): number => {
    if (polyline.length < 4) return Infinity;
    let minDist = Infinity;
    for (let i = 0; i < polyline.length - 2; i += 2) {
      const dist = pointToSegmentDistance(px, py, polyline[i], polyline[i + 1], polyline[i + 2], polyline[i + 3]);
      if (dist < minDist) minDist = dist;
    }
    return minDist;
  }, [pointToSegmentDistance]);

  // Check if annotation is nearby active equipment (step 3) or active node (step 4)
  const isAnnotationNearby = useCallback((ann: TemplateAnnotation): boolean => {
    const annCenterX = ann.x + ann.width / 2;
    const annCenterY = ann.y + ann.height / 2;

    if (wizardStep === 3) {
      // While marking, the equipment bbox may not yet be persisted to pnidState.
      // Prefer pendingMarkBbox (mark dialog bbox) so nearby highlights appear immediately.
      const bboxToUse = pendingMarkBbox ?? activeEquipmentBbox;
      if (!bboxToUse) return false;

      // Check if annotation center is within equipment bbox expanded by padding
      const expandedBbox = {
        x1: bboxToUse.x1 - NEARBY_PADDING,
        y1: bboxToUse.y1 - NEARBY_PADDING,
        x2: bboxToUse.x2 + NEARBY_PADDING,
        y2: bboxToUse.y2 + NEARBY_PADDING,
      };
      return (
        annCenterX >= expandedBbox.x1 &&
        annCenterX <= expandedBbox.x2 &&
        annCenterY >= expandedBbox.y1 &&
        annCenterY <= expandedBbox.y2
      );
    }

    if (wizardStep === 4 && activeNodePolyline && activeNodePolyline.length >= 4) {
      // Check if annotation center is within threshold distance of polyline
      const dist = pointToPolylineDistance(annCenterX, annCenterY, activeNodePolyline);
      return dist <= NEARBY_PADDING;
    }

    return false;
  }, [wizardStep, pendingMarkBbox, activeEquipmentBbox, activeNodePolyline, pointToPolylineDistance]);

  // Check if annotation is attached to the currently active equipment/node
  const isAnnotationAttachedToActive = useCallback((ann: TemplateAnnotation): boolean => {
    if (draftDetachedAnnotationIds?.has(ann.id)) return false;
    if (draftAttachedAnnotationIds?.has(ann.id)) return true;
    if (wizardStep === 3 && activeEquipmentId) {
      return ann.attached_context_type === 'equipment' && ann.attached_context_id === activeEquipmentId;
    }
    if (wizardStep === 4 && activeNodeId) {
      return ann.attached_context_type === 'node' && ann.attached_context_id === activeNodeId;
    }
    return false;
  }, [draftDetachedAnnotationIds, draftAttachedAnnotationIds, wizardStep, activeEquipmentId, activeNodeId]);

  const isAnnotationDetachedFromActive = useCallback((ann: TemplateAnnotation): boolean => {
    return Boolean(draftDetachedAnnotationIds?.has(ann.id));
  }, [draftDetachedAnnotationIds]);

  // (debug logging removed)

  const updatePendingBbox = useCallback((bbox: BoundingBox) => {
    if (!onUpdatePendingMarkBbox) return;
    onUpdatePendingMarkBbox(clampBboxToImage(bbox));
  }, [onUpdatePendingMarkBbox, clampBboxToImage]);

  const updatePersistedBbox = useCallback((
    target: PersistedBboxCanvasTarget,
    bbox: BoundingBox,
    commit: boolean = false,
    regionIndex?: number,
  ) => {
    if (!onUpdateWizardItemBbox) return;
    onUpdateWizardItemBbox(target, clampBboxToImage(bbox), { commit, regionIndex });
  }, [onUpdateWizardItemBbox, clampBboxToImage]);

  /**
   * Move/resize affordance for one persisted rectangle. A multi-region marking renders
   * these once per region and names the region in `opts.regionIndex`, so the drag
   * commits against the rectangle the SME grabbed rather than the marking's first one.
   */
  const renderPersistedBboxHandles = useCallback((
    target: PersistedBboxCanvasTarget,
    bbox: BoundingBox,
    stroke: string,
    opts?: { onDblClick?: () => void; regionIndex?: number },
  ) => {
    if (!onUpdateWizardItemBbox || readOnly || drawMode !== 'select') return null;

    const width = bbox.x2 - bbox.x1;
    const height = bbox.y2 - bbox.y1;
    if (width <= 0 || height <= 0) return null;

    const handleSize = 10 / scale;
    const minSize = 6;
    const handles = [
      { key: 'nw', x: bbox.x1, y: bbox.y1 },
      { key: 'ne', x: bbox.x2, y: bbox.y1 },
      { key: 'se', x: bbox.x2, y: bbox.y2 },
      { key: 'sw', x: bbox.x1, y: bbox.y2 },
    ];
    const updateFromHandle = (key: string, x: number, y: number, commit = false) => {
      let { x1, y1, x2, y2 } = bbox;
      if (key === 'nw') {
        x1 = Math.min(x, x2 - minSize);
        y1 = Math.min(y, y2 - minSize);
      } else if (key === 'ne') {
        x2 = Math.max(x, x1 + minSize);
        y1 = Math.min(y, y2 - minSize);
      } else if (key === 'se') {
        x2 = Math.max(x, x1 + minSize);
        y2 = Math.max(y, y1 + minSize);
      } else if (key === 'sw') {
        x1 = Math.min(x, x2 - minSize);
        y2 = Math.max(y, y1 + minSize);
      }
      updatePersistedBbox(target, { x1, y1, x2, y2 }, commit, opts?.regionIndex);
    };

    return (
      <>
        <Rect
          x={bbox.x1}
          y={bbox.y1}
          width={width}
          height={height}
          fill="transparent"
          stroke={stroke}
          strokeWidth={1 / scale}
          dash={[3 / scale, 3 / scale]}
          draggable={!isSpaceHeld}
          name="persisted-bbox-adjust"
          onMouseEnter={(e) => {
            cancelHoverClear();
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = 'move';
          }}
          onMouseLeave={(e) => {
            scheduleHoverClear();
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = '';
          }}
          onMouseDown={(e) => {
            if (isSpaceHeld) return;
            e.cancelBubble = true;
          }}
          onDragStart={(e) => {
            e.cancelBubble = true;
            onBeginWizardItemBboxUpdate?.(target);
          }}
          onDblClick={(e) => {
            if (!opts?.onDblClick || isSpaceHeld) return;
            e.cancelBubble = true;
            opts.onDblClick();
          }}
          onClick={(e) => {
            if (panMovedRef.current || isSpaceHeld) return;
            e.cancelBubble = true;
            onSelectWizardItem?.({
              kind: target.kind,
              id: target.id,
              screenPosition: getPointerScreenPosition(),
              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
            });
          }}
          onDragMove={(e) => {
            if (isSpaceHeld) return;
            e.cancelBubble = true;
            const node = e.target;
            updatePersistedBbox(target, clampTranslatedBboxToImage({
              x1: node.x(),
              y1: node.y(),
              x2: node.x() + width,
              y2: node.y() + height,
            }), false, opts?.regionIndex);
          }}
          onDragEnd={(e) => {
            if (isSpaceHeld) return;
            e.cancelBubble = true;
            const node = e.target;
            updatePersistedBbox(target, clampTranslatedBboxToImage({
              x1: node.x(),
              y1: node.y(),
              x2: node.x() + width,
              y2: node.y() + height,
            }), true, opts?.regionIndex);
          }}
        />
        {handles.map((handle) => (
          <Rect
            key={`${target.kind}-${target.id}-region-${opts?.regionIndex ?? 0}-handle-${handle.key}`}
            x={handle.x - handleSize / 2}
            y={handle.y - handleSize / 2}
            width={handleSize}
            height={handleSize}
            fill="#ffffff"
            stroke={stroke}
            strokeWidth={2 / scale}
            name="persisted-bbox-handle"
            draggable
            onMouseEnter={(e) => {
              cancelHoverClear();
              const stage = e.target.getStage();
              if (stage) {
                stage.container().style.cursor =
                  handle.key === 'nw' || handle.key === 'se' ? 'nwse-resize' : 'nesw-resize';
              }
            }}
            onMouseLeave={(e) => {
              scheduleHoverClear();
              const stage = e.target.getStage();
              if (stage) stage.container().style.cursor = '';
            }}
            onMouseDown={(e) => {
              e.cancelBubble = true;
            }}
            onDragStart={(e) => {
              e.cancelBubble = true;
              onBeginWizardItemBboxUpdate?.(target);
            }}
            onDragMove={(e) => {
              e.cancelBubble = true;
              const node = e.target;
              updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2);
            }}
            onDragEnd={(e) => {
              e.cancelBubble = true;
              const node = e.target;
              updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2, true);
            }}
          />
        ))}
      </>
    );
  }, [cancelHoverClear, clampTranslatedBboxToImage, drawMode, getPointerScreenPosition, isSpaceHeld, onBeginWizardItemBboxUpdate, onSelectWizardItem, onUpdateWizardItemBbox, readOnly, scale, scheduleHoverClear, updatePersistedBbox]);

  const updateAnnotationBbox = useCallback((
    annotationId: string,
    bbox: BoundingBox,
    commit: boolean = false,
  ) => {
    if (!onUpdateAnnotationBbox) return;
    onUpdateAnnotationBbox(annotationId, clampBboxToImage(bbox), { commit });
  }, [onUpdateAnnotationBbox, clampBboxToImage]);

  /** Corner resize anchors for saved template annotations (same affordance as renderPersistedBboxHandles). */
  const renderAnnotationBboxHandles = useCallback((
    annotationId: string,
    bbox: BoundingBox,
    stroke: string,
  ) => {
    if (!onUpdateAnnotationBbox || readOnly || drawMode !== 'select') return null;

    const width = bbox.x2 - bbox.x1;
    const height = bbox.y2 - bbox.y1;
    if (width <= 0 || height <= 0) return null;

    const handleSize = 10 / scale;
    const minSize = 6;
    const handles = [
      { key: 'nw', x: bbox.x1, y: bbox.y1 },
      { key: 'ne', x: bbox.x2, y: bbox.y1 },
      { key: 'se', x: bbox.x2, y: bbox.y2 },
      { key: 'sw', x: bbox.x1, y: bbox.y2 },
    ];
    const updateFromHandle = (key: string, x: number, y: number, commit = false) => {
      let { x1, y1, x2, y2 } = bbox;
      if (key === 'nw') {
        x1 = Math.min(x, x2 - minSize);
        y1 = Math.min(y, y2 - minSize);
      } else if (key === 'ne') {
        x2 = Math.max(x, x1 + minSize);
        y1 = Math.min(y, y2 - minSize);
      } else if (key === 'se') {
        x2 = Math.max(x, x1 + minSize);
        y2 = Math.max(y, y1 + minSize);
      } else if (key === 'sw') {
        x1 = Math.min(x, x2 - minSize);
        y2 = Math.max(y, y1 + minSize);
      }
      updateAnnotationBbox(annotationId, { x1, y1, x2, y2 }, commit);
    };

    return (
      <>
        {handles.map((handle) => (
          <Rect
            key={`annotation-${annotationId}-handle-${handle.key}`}
            x={handle.x - handleSize / 2}
            y={handle.y - handleSize / 2}
            width={handleSize}
            height={handleSize}
            fill="#ffffff"
            stroke={stroke}
            strokeWidth={2 / scale}
            name="persisted-bbox-handle"
            draggable
            onMouseEnter={(e) => {
              cancelHoverClear();
              const stage = e.target.getStage();
              if (stage) {
                stage.container().style.cursor =
                  handle.key === 'nw' || handle.key === 'se' ? 'nwse-resize' : 'nesw-resize';
              }
            }}
            onMouseLeave={(e) => {
              scheduleHoverClear();
              const stage = e.target.getStage();
              if (stage) stage.container().style.cursor = '';
            }}
            onMouseDown={(e) => {
              e.cancelBubble = true;
            }}
            onDragStart={(e) => {
              e.cancelBubble = true;
            }}
            onDragMove={(e) => {
              e.cancelBubble = true;
              const node = e.target;
              updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2);
            }}
            onDragEnd={(e) => {
              e.cancelBubble = true;
              const node = e.target;
              updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2, true);
            }}
          />
        ))}
      </>
    );
  }, [cancelHoverClear, drawMode, onUpdateAnnotationBbox, readOnly, scale, scheduleHoverClear, updateAnnotationBbox]);

  const renderQuickDeleteBadge = useCallback((
    target: { kind: 'equipmentInstrument' | 'nodeInstrument'; id: string },
    bbox: BoundingBox,
  ) => {
    if (!onQuickDeleteWizardItem || readOnly || drawMode !== 'select') return null;
    const radius = 9 / scale;
    const armLength = radius * 0.45;
    return (
      <Group
        x={bbox.x2 + 12 / scale}
        y={bbox.y1 - 12 / scale}
        name="quick-delete-badge"
        onMouseEnter={(e) => {
          cancelHoverClear();
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = 'pointer';
        }}
        onMouseLeave={(e) => {
          scheduleHoverClear();
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = '';
        }}
        onMouseDown={(e) => {
          e.cancelBubble = true;
        }}
        onClick={(e) => {
          e.cancelBubble = true;
          if (e.evt.button !== 0) return;
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = '';
          onQuickDeleteWizardItem(target);
        }}
        onTap={(e) => {
          e.cancelBubble = true;
          onQuickDeleteWizardItem(target);
        }}
      >
        <Circle
          radius={radius}
          fill="#dc2626"
          stroke="#ffffff"
          strokeWidth={1.5 / scale}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
        />
        <Line
          points={[-armLength, -armLength, armLength, armLength]}
          stroke="#ffffff"
          strokeWidth={2 / scale}
          lineCap="round"
          listening={false}
          perfectDrawEnabled={false}
        />
        <Line
          points={[-armLength, armLength, armLength, -armLength]}
          stroke="#ffffff"
          strokeWidth={2 / scale}
          lineCap="round"
          listening={false}
          perfectDrawEnabled={false}
        />
      </Group>
    );
  }, [cancelHoverClear, drawMode, onQuickDeleteWizardItem, readOnly, scale, scheduleHoverClear]);

  const handleMouseDown = useCallback((e: any) => {
    const stage = stageRef.current;
    if (!stage) return;

    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const target = e.target;
    const isEditingPendingPolyline = Boolean(
      onUpdatePendingMarkBbox &&
      pendingMarkBbox?.polygon &&
      pendingMarkBbox.polygon.length >= 4
    );
    if (targetOrAncestorHasKonvaName(target, [
      'pending-handle',
      'pending-box',
      'pending-point',
      'annotation-shift-bounds',
      'selected-annotation-shift-bounds',
    ])) {
      return;
    }

    const isPassiveOverlayTarget = targetOrAncestorHasKonvaName(target, [
      'wizard-mark',
      'template-annotation',
      'node-point-candidate',
    ]);
    if (drawMode === 'select' && !isSpaceHeld && isPassiveOverlayTarget) {
      return;
    }
    
    // Cmd/Ctrl + click/drag for template annotation attachment (Steps 2/3/4)
    // IMPORTANT: must run BEFORE right-click pan, otherwise detach (button 2) will pan.
    const isModifierHeld = e.evt.metaKey || e.evt.ctrlKey;
    if ((wizardStep === 2 || wizardStep === 3 || wizardStep === 4) && isModifierHeld && !isPanning && onAttachTemplateAnnotations) {
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      setIsAttachingAnnotations(true);
      setAttachRectStart(adjusted);
      setAttachRectEnd(adjusted);
      // Left click (button 0) = attach, Right click (button 2) = detach
      setAttachAction(e.evt.button === 2 ? 'detach' : 'attach');
      e.evt.preventDefault();

      return;
    }

    // Cmd/Ctrl + left-drag for template annotation bulk selection (Step 2)
    // Only in select mode (not crop mode), left button only
    if (wizardStep === 2 && isModifierHeld && drawMode === 'select' && e.evt.button === 0 && !isPanning && onSelectAnnotation) {
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      setIsSelectingAnnotations(true);
      setSelectRectStart(adjusted);
      setSelectRectEnd(adjusted);
      // Store whether Shift is held for additive selection
      selectAdditiveRef.current = e.evt.shiftKey;
      e.evt.preventDefault();
      return;
    }

    // Check if it's a right-click (button 2) or middle-click (button 1) for panning
    if (e.evt.button === 2 || e.evt.button === 1) {
      e.evt.preventDefault();
      e.cancelBubble = true;
      panMovedRef.current = false;
      panPointerStartRef.current = { x: pointer.x, y: pointer.y };
      activatePanInteractionRaster();
      setIsPanning(true);
      setHoveredItem(null);
      setPanStart({
        x: pointer.x - currentPosition.x,
        y: pointer.y - currentPosition.y,
      });

      return;
    }

    // Space held = pan mode (works in ALL modes, Figma-style)
    if (isSpaceHeld && e.evt.button === 0) {
      e.cancelBubble = true;
      panMovedRef.current = false;
      panPointerStartRef.current = { x: pointer.x, y: pointer.y };
      activatePanInteractionRaster();
      setIsPanning(true);
      setHoveredItem(null);
      setPanStart({
        x: pointer.x - currentPosition.x,
        y: pointer.y - currentPosition.y,
      });
      return;
    }

    // Select mode - left click to pan (no drawing)
    if (drawMode === 'select' && e.evt.button === 0 && !isSpaceHeld) {
      // While editing a line's points, disable select-mode panning so point drag
      // interactions are not hijacked by stage pan behavior.
      if (isEditingPendingPolyline) {
        return;
      }
      // In select mode, left-click drag pans the canvas
      e.cancelBubble = true;
      panMovedRef.current = false;
      panPointerStartRef.current = { x: pointer.x, y: pointer.y };
      activatePanInteractionRaster();
      setIsPanning(true);
      setHoveredItem(null);
      setPanStart({
        x: pointer.x - currentPosition.x,
        y: pointer.y - currentPosition.y,
      });
      return;
    }

    // Comment mode - left click to create a comment
    if (drawMode === 'comment' && e.evt.button === 0 && !isPanning && onCreateComment) {
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      // Get the stage container to calculate screen position
      const stage = stageRef.current;
      const container = stage?.container();
      if (container) {
        const rect = container.getBoundingClientRect();
        const screenX = rect.left + pointer.x;
        const screenY = rect.top + pointer.y;
        onCreateComment(adjusted.x, adjusted.y, screenX, screenY);
      }
      return;
    }

    // Crop mode - start selection
    if (drawMode === 'crop' && e.evt.button === 0 && !isPanning) {
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      // Clamp coordinates to image bounds
      const clamped = {
        x: Math.max(0, Math.min(adjusted.x, imageCanvasWidth || image?.width || 0)),
        y: Math.max(0, Math.min(adjusted.y, imageCanvasHeight || image?.height || 0)),
      };
      setIsCropping(true);
      setCropStart(clamped);
      setCropEnd(clamped);
      return;
    }

    // Select-annotations mode - start rectangle selection
    if (drawMode === 'select-annotations' && e.evt.button === 0 && !isPanning) {
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      setIsSelectingAnnotations(true);
      setSelectRectStart(adjusted);
      setSelectRectEnd(adjusted);
      return;
    }

    if (drawMode === 'shift-annotations' && e.evt.button === 0 && !isPanning) {
      return;
    }

    // Left-click (button 0) for rect drawing - only if not currently panning
    if (drawMode === 'rect' && e.evt.button === 0 && !isPanning) {
      const adjustedPointer = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      
      setIsDrawing(true);
      trackCanvasEvent('draw_start');
      setDrawingBox({
        x: adjustedPointer.x,
        y: adjustedPointer.y,
        width: 0,
        height: 0,
      });
    }

    // Polygon drawing (drag to draw)
    if (drawMode === 'poly' && e.evt.button === 0 && !isPanning) {
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      setPolyPoints([adjusted.x, adjusted.y]);
      setIsPolyDrawing(true);
      return;
    }

    // Path mode: add point
    if (drawMode === 'path' && e.evt.button === 0 && !isPanning) {
      if (pendingMarkBbox) {
        return;
      }
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      if (pathPoints.length === 0 && onPathStart) {
        const stage = stageRef.current;
        const container = stage?.container();
        if (container) {
          const rect = container.getBoundingClientRect();
          const screenX = rect.left + pointer.x;
          const screenY = rect.top + pointer.y;
          onPathStart({ x: screenX, y: screenY }, { x: adjusted.x, y: adjusted.y });
        }
      }
      setPathPoints(prev => [...prev, adjusted.x, adjusted.y]);
      // Clear redo stack when adding new point (can't redo after new action)
      setPathRedoStack([]);
      return;
    }
  }, [
    activatePanInteractionRaster,
    isPanning,
    drawMode,
    isSpaceHeld,
    pendingMarkBbox,
    onUpdatePendingMarkBbox,
    onCreateComment,
    pathPoints.length,
    onPathStart,
    imageCanvasHeight,
    imageCanvasWidth,
  ]);

  /**
   * Apply a pointer position to whichever gesture is in flight.
   *
   * `handleMouseMove` calls this when the cursor moves. The edge-pan loop calls
   * it when the drawing moves under a cursor that is holding still: the screen
   * coordinate is unchanged there, but the image coordinate beneath it is not,
   * so a rectangle, crop, or path preview must keep tracking it.
   */
  const applyPointerToGestures = useCallback((
    adjustedPointer: { x: number; y: number },
    currentScale: number
  ) => {
    cursorPositionRef.current = adjustedPointer;
    if (cursorRafRef.current === null) {
      cursorRafRef.current = window.requestAnimationFrame(() => {
        cursorRafRef.current = null;
        setCursorPosition(cursorPositionRef.current);
      });
    }

    // Handle drawing
    if (isDrawing && drawingBox) {
      setDrawingBox({
        ...drawingBox,
        width: adjustedPointer.x - drawingBox.x,
        height: adjustedPointer.y - drawingBox.y,
      });
    }

    // Handle crop selection
    if (isCropping && cropStart) {
      // Clamp coordinates to image bounds
      const clampedEnd = {
        x: Math.max(0, Math.min(adjustedPointer.x, imageCanvasWidth || image?.width || 0)),
        y: Math.max(0, Math.min(adjustedPointer.y, imageCanvasHeight || image?.height || 0)),
      };
      setCropEnd(clampedEnd);
    }

    // Handle select-annotations rectangle
    if (isSelectingAnnotations && selectRectStart) {
      setSelectRectEnd(adjustedPointer);
    }

    // Handle attachment rectangle (Cmd/Ctrl + drag in steps 3/4)
    if (isAttachingAnnotations && attachRectStart) {
      setAttachRectEnd(adjustedPointer);
    }

    if (drawMode === 'poly' && isPolyDrawing) {
      const adjusted = {
        x: adjustedPointer.x,
        y: adjustedPointer.y,
      };
      
      // Only add point if it's far enough from the last point (smoothing)
      const lastX = polyPoints[polyPoints.length - 2];
      const lastY = polyPoints[polyPoints.length - 1];
      const distance = Math.sqrt((adjusted.x - lastX) ** 2 + (adjusted.y - lastY) ** 2);
      
      if (distance > 5 / currentScale) { // minimum distance threshold
        setPolyPoints(prev => [...prev, adjusted.x, adjusted.y]);
      }
      return;
    }

    // Path drawing preview - show line to cursor (when not panning)
    if (drawMode === 'path' && !isSpaceHeld && pathPoints.length >= 2 && !pendingMarkBbox) {
      const adjusted = {
        x: adjustedPointer.x,
        y: adjustedPointer.y,
      };
      setPathPreviewPoint(adjusted);
    }
  }, [isDrawing, drawingBox, drawMode, isPolyDrawing, polyPoints, pathPoints.length, isSpaceHeld, pendingMarkBbox, isCropping, cropStart, image, imageCanvasHeight, imageCanvasWidth, isSelectingAnnotations, selectRectStart, isAttachingAnnotations, attachRectStart]);

  // --- Edge pan -------------------------------------------------------------
  // The drawing scrolls while the cursor rests in a band along the canvas
  // border, so an SME reaches an off-screen area without leaving the current
  // tool for pan mode. The loop is imperative for the same reason the manual
  // drag pan is: a setState per frame re-renders every overlay on the drawing.

  const applyPointerToGesturesRef = useRef(applyPointerToGestures);
  useEffect(() => {
    applyPointerToGesturesRef.current = applyPointerToGestures;
  }, [applyPointerToGestures]);

  const canvasPointerRef = useRef<PanPointer | null>(null);
  const edgePanFrameRef = useRef<number | null>(null);
  const edgePanLastFrameMsRef = useRef(0);
  const edgePanLastCommitMsRef = useRef(0);
  const edgePanDwellStartMsRef = useRef(0);
  const runEdgePanFrameRef = useRef<(timestampMs: number) => void>(() => {});

  const stopEdgePan = useCallback(() => {
    if (edgePanFrameRef.current === null) return;
    window.cancelAnimationFrame(edgePanFrameRef.current);
    edgePanFrameRef.current = null;
    if (!edgePanMovingRef.current) return;
    edgePanMovingRef.current = false;
    releasePanInteractionRasterSoon();
    setPosition(positionRef.current);
  }, [releasePanInteractionRasterSoon]);

  const runEdgePanFrame = useCallback((timestampMs: number) => {
    const stage = stageRef.current;
    const pointer = canvasPointerRef.current;
    const { viewport, imageSize, suppressed, gestureInFlight } = panContextRef.current;
    if (!stage || !pointer || suppressed) {
      stopEdgePan();
      return;
    }

    const velocity = computeEdgePanVelocity(pointer, viewport);
    if (velocity.vx === 0 && velocity.vy === 0) {
      stopEdgePan();
      return;
    }

    // A cursor on its way to one of the canvas's own overlay controls crosses
    // the band. Only a cursor that stays there means to pan.
    if (edgePanDwellStartMsRef.current === 0) edgePanDwellStartMsRef.current = timestampMs;
    if (timestampMs - edgePanDwellStartMsRef.current < EDGE_PAN_DWELL_MS) {
      edgePanFrameRef.current = window.requestAnimationFrame(
        (nextTimestampMs) => runEdgePanFrameRef.current(nextTimestampMs)
      );
      return;
    }

    if (!edgePanMovingRef.current) {
      edgePanMovingRef.current = true;
      activatePanInteractionRaster();
      trackCanvasEvent('edge_pan');
    }

    const isFirstFrame = edgePanLastFrameMsRef.current === 0;
    const elapsedMs = isFirstFrame ? 0 : timestampMs - edgePanLastFrameMsRef.current;
    edgePanLastFrameMsRef.current = timestampMs;
    if (isFirstFrame) edgePanLastCommitMsRef.current = timestampMs;

    const currentScale = scaleRef.current;
    const nextPosition = clampPanPosition(
      stepPanPosition(positionRef.current, velocity, elapsedMs),
      currentScale,
      viewport,
      imageSize
    );
    // The clamp holds the drawing at its bound while the cursor stays in the
    // band, so a stalled position is the second way this loop ends. Velocity
    // alone never reaches zero there.
    if (
      !isFirstFrame
      && nextPosition.x === positionRef.current.x
      && nextPosition.y === positionRef.current.y
    ) {
      stopEdgePan();
      return;
    }

    positionRef.current = nextPosition;
    stage.position(nextPosition);
    stage.batchDraw();

    // The cursor holds still while the drawing moves beneath it, so the image
    // coordinate under it changed. A rectangle, crop, or path preview in flight
    // has to keep tracking that coordinate. Nothing else does: `applyPointerToGestures`
    // schedules a `setCursorPosition` on every call, and a re-render per frame
    // is the cost this loop exists to avoid.
    if (gestureInFlight) {
      applyPointerToGesturesRef.current(
        {
          x: (pointer.x - nextPosition.x) / currentScale,
          y: (pointer.y - nextPosition.y) / currentScale,
        },
        currentScale
      );
    }

    // Overlay culling reads `position` from React state. A long edge pan that
    // never commits would scroll into overlays that were culled away and never
    // re-rendered, so the SME would arrive at an empty region of the drawing.
    if (timestampMs - edgePanLastCommitMsRef.current >= EDGE_PAN_COMMIT_INTERVAL_MS) {
      edgePanLastCommitMsRef.current = timestampMs;
      setPosition(nextPosition);
    }

    edgePanFrameRef.current = window.requestAnimationFrame(
      (nextTimestampMs) => runEdgePanFrameRef.current(nextTimestampMs)
    );
  }, [activatePanInteractionRaster, stopEdgePan, trackCanvasEvent]);

  useEffect(() => {
    runEdgePanFrameRef.current = runEdgePanFrame;
  }, [runEdgePanFrame]);

  /** Start, continue, or stop the loop for the cursor's latest position. */
  const syncEdgePan = useCallback((pointer: PanPointer) => {
    canvasPointerRef.current = pointer;
    const { viewport, suppressed } = panContextRef.current;
    const velocity = computeEdgePanVelocity(pointer, viewport);
    if (suppressed || (velocity.vx === 0 && velocity.vy === 0)) {
      stopEdgePan();
      return;
    }
    if (edgePanFrameRef.current !== null) return;

    edgePanLastFrameMsRef.current = 0;
    edgePanLastCommitMsRef.current = 0;
    edgePanDwellStartMsRef.current = 0;
    edgePanFrameRef.current = window.requestAnimationFrame(
      (timestampMs) => runEdgePanFrameRef.current(timestampMs)
    );
  }, [stopEdgePan]);

  // A commit re-renders, and Konva then re-applies the `x`/`y` props from that
  // older position, which reads as a backwards jerk mid-pan. Re-assert the
  // loop's own position after the render instead.
  useLayoutEffect(() => {
    if (!isImperativePanActive()) return;
    const stage = stageRef.current;
    if (!stage) return;
    stage.position(positionRef.current);
    stage.batchDraw();
  });

  useEffect(() => () => {
    if (edgePanFrameRef.current !== null) {
      window.cancelAnimationFrame(edgePanFrameRef.current);
      edgePanFrameRef.current = null;
    }
  }, []);

  const handleMouseMove = useCallback((_e: any) => {
    const stage = stageRef.current;
    if (!stage) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;
    const adjustedPointer = {
      x: (pointer.x - currentPosition.x) / currentScale,
      y: (pointer.y - currentPosition.y) / currentScale,
    };

    // Handle panning
    if (isPanning) {
      const newPosition = clampPanPosition(
        { x: pointer.x - panStart.x, y: pointer.y - panStart.y },
        scaleRef.current,
        panContextRef.current.viewport,
        panContextRef.current.imageSize
      );
      positionRef.current = newPosition;
      stage.position(newPosition);
      stage.batchDraw();
      if (panPointerStartRef.current) {
        const dx = pointer.x - panPointerStartRef.current.x;
        const dy = pointer.y - panPointerStartRef.current.y;
        if (Math.hypot(dx, dy) > 4) {
          panMovedRef.current = true;
        }
      }
      return;
    }

    syncEdgePan(pointer);
    applyPointerToGestures(adjustedPointer, currentScale);
  }, [applyPointerToGestures, isPanning, panStart, syncEdgePan]);

  const handleMouseUp = useCallback((_e: any) => {
    // Handle panning end
    if (isPanning) {
      performance.mark('pan-end');
      const markPanEndIdle = () => {
        performance.mark('pan-end+idle');
        performance.measure('pan-end-to-idle', 'pan-end', 'pan-end+idle');
      };
      const idleCallback = window.requestIdleCallback;
      if (typeof idleCallback === 'function') {
        idleCallback(markPanEndIdle, { timeout: 1000 });
      } else {
        window.setTimeout(markPanEndIdle, 0);
      }
      setIsPanning(false);
      releasePanInteractionRasterSoon();
      setPosition(positionRef.current);
      if (panMovedRef.current) {
        const container = stageRef.current?.container();
        container?.addEventListener('click', (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
        }, { capture: true, once: true });
        trackCanvasEvent('pan');
        setTimeout(() => {
          panMovedRef.current = false;
          panPointerStartRef.current = null;
        }, 0);
      } else {
        panPointerStartRef.current = null;
      }
      return;
    }

    // Handle drawing end
    if (isDrawing && drawingBox) {
      setIsDrawing(false);

      // Convert to bounding box format
      const bbox: BoundingBox = {
        x1: Math.min(drawingBox.x, drawingBox.x + drawingBox.width),
        y1: Math.min(drawingBox.y, drawingBox.y + drawingBox.height),
        x2: Math.max(drawingBox.x, drawingBox.x + drawingBox.width),
        y2: Math.max(drawingBox.y, drawingBox.y + drawingBox.height),
      };

      // Only call onDraw if the box has some size
      if (Math.abs(bbox.x2 - bbox.x1) > 5 && Math.abs(bbox.y2 - bbox.y1) > 5) {
        const anchor = computeAnchor(bbox);
        onDraw(bbox, anchor);
        trackCanvasEvent('draw_complete');
      }

      setDrawingBox(null);
    }

    // Crop selection end
    if (isCropping && cropStart && cropEnd) {
      setIsCropping(false);
      const rect = {
        x: Math.round(Math.min(cropStart.x, cropEnd.x)),
        y: Math.round(Math.min(cropStart.y, cropEnd.y)),
        width: Math.round(Math.abs(cropEnd.x - cropStart.x)),
        height: Math.round(Math.abs(cropEnd.y - cropStart.y)),
      };
      
      // Only process crop if region is large enough
      if (rect.width > MIN_CROP_SIZE && rect.height > MIN_CROP_SIZE && onCrop) {
        extractCrop(rect).then((blob) => {
          onCrop({ imageBlob: blob, rect });
          setDrawMode('select'); // Return to select mode after crop
        }).catch((err) => {
          console.error('Failed to extract crop:', err);
        });
      }
      setCropStart(null);
      setCropEnd(null);
    }

    // Select-annotations rectangle selection end
    if (isSelectingAnnotations && selectRectStart && selectRectEnd && onSelectAnnotation) {
      setIsSelectingAnnotations(false);
      const selectRect = {
        x: Math.min(selectRectStart.x, selectRectEnd.x),
        y: Math.min(selectRectStart.y, selectRectEnd.y),
        width: Math.abs(selectRectEnd.x - selectRectStart.x),
        height: Math.abs(selectRectEnd.y - selectRectStart.y),
      };
      
      // Only select if rectangle is large enough (to distinguish from clicks)
      if (selectRect.width > 5 && selectRect.height > 5) {
        // Use stored Shift value (Cmd/Ctrl is always held for modifier-drag selection)
        const isAddingToSelection = selectAdditiveRef.current;
        
        // Helper to check if two rectangles intersect
        const rectsIntersect = (r1: { x: number; y: number; width: number; height: number }, r2: { x: number; y: number; width: number; height: number }) => {
          return !(r2.x > r1.x + r1.width || 
                   r2.x + r2.width < r1.x || 
                   r2.y > r1.y + r1.height ||
                   r2.y + r2.height < r1.y);
        };
        
        // Collect all intersecting annotation IDs first
        const intersectingIds: string[] = [];
        
        // Find all saved annotations that intersect with selection rectangle
        templateAnnotations
          .filter(ann => templateVisibility?.get(ann.template_id) !== false)
          .forEach(ann => {
            const annRect = { x: ann.x, y: ann.y, width: ann.width, height: ann.height };
            if (rectsIntersect(selectRect, annRect)) {
              intersectingIds.push(ann.id);
            }
          });
        
        // Find all unsaved annotations that intersect with selection rectangle
        Array.from(unsavedMatches?.entries() || [])
          .filter(([templateId]) => templateVisibility?.get(templateId) !== false)
          .forEach(([templateId, matches]) => {
            matches.forEach((match, idx) => {
              const matchRect = { x: match.x, y: match.y, width: match.width, height: match.height };
              if (rectsIntersect(selectRect, matchRect)) {
                const id = `unsaved-${templateId}-${idx}`;
                intersectingIds.push(id);
              }
            });
          });
        
        // Clear existing selection first if not adding to selection
        if (!isAddingToSelection && onClearAnnotationSelection) {
          onClearAnnotationSelection();
        }
        
        // Now add all intersecting annotations (always as multi-select to avoid clearing between items)
        intersectingIds.forEach(id => {
          onSelectAnnotation(id, true);
        });
      }
      
      setSelectRectStart(null);
      setSelectRectEnd(null);
    }

    // Attachment rectangle selection end (Cmd/Ctrl + drag in steps 3/4)
    if (isAttachingAnnotations && attachRectStart && attachRectEnd && onAttachTemplateAnnotations) {
      setIsAttachingAnnotations(false);
      const attachRect = {
        x: Math.min(attachRectStart.x, attachRectEnd.x),
        y: Math.min(attachRectStart.y, attachRectEnd.y),
        width: Math.abs(attachRectEnd.x - attachRectStart.x),
        height: Math.abs(attachRectEnd.y - attachRectStart.y),
      };
      
      // Helper to check if two rectangles intersect
      const rectsIntersect = (r1: { x: number; y: number; width: number; height: number }, r2: { x: number; y: number; width: number; height: number }) => {
        return !(r2.x > r1.x + r1.width || 
                 r2.x + r2.width < r1.x || 
                 r2.y > r1.y + r1.height ||
                 r2.y + r2.height < r1.y);
      };
      
      // Helper for point-in-rect check (for small selections / clicks)
      const pointInRect = (px: number, py: number, rect: { x: number; y: number; width: number; height: number }) => {
        return px >= rect.x && px <= rect.x + rect.width && py >= rect.y && py <= rect.y + rect.height;
      };
      
      // Collect all intersecting annotation IDs
      const intersectingIds: string[] = [];
      const isClickSelection = attachRect.width < 5 && attachRect.height < 5;
      
      templateAnnotations.forEach(ann => {
        const annRect = { x: ann.x, y: ann.y, width: ann.width, height: ann.height };
        const isIntersecting = isClickSelection
          ? pointInRect(attachRectStart.x, attachRectStart.y, annRect)
          : rectsIntersect(attachRect, annRect);
        if (isIntersecting) {
          intersectingIds.push(ann.id);
        }
      });

      // Call the callback with the IDs and action
      if (intersectingIds.length > 0) {
        onAttachTemplateAnnotations(intersectingIds, attachAction);
      }

      setAttachRectStart(null);
      setAttachRectEnd(null);
    }

    // Polygon drawing end
    if (drawMode === 'poly' && isPolyDrawing) {
      setIsPolyDrawing(false);
      
      if (polyPoints.length >= 6) { // at least 3 points
        // Simplify and close the polygon
        const simplified = simplifyPolygon(polyPoints);
        
        // Compute bounding box
        const xs: number[] = [];
        const ys: number[] = [];
        for (let i = 0; i < simplified.length; i += 2) {
          xs.push(simplified[i]);
          ys.push(simplified[i + 1]);
        }
        
        const bbox: BoundingBox = {
          x1: Math.min(...xs),
          y1: Math.min(...ys),
          x2: Math.max(...xs),
          y2: Math.max(...ys),
          polygon: simplified, // include actual polygon points
        };
        
        const anchor = computeAnchor(bbox);
        onDraw(bbox, anchor);
        trackCanvasEvent('draw_complete');
      }

      setPolyPoints([]);
      return;
    }
  }, [attachAction, attachRectEnd, attachRectStart, computeAnchor, cropEnd, cropStart, drawMode, drawingBox, extractCrop, isAttachingAnnotations, isCropping, isDrawing, isPanning, isPolyDrawing, isSelectingAnnotations, onAttachTemplateAnnotations, onClearAnnotationSelection, onCrop, onDraw, onSelectAnnotation, polyPoints, releasePanInteractionRasterSoon, selectRectEnd, selectRectStart, simplifyPolygon, templateAnnotations, templateVisibility, unsavedMatches]);

  // Handle mouse leave to stop panning/drawing if mouse leaves the canvas
  const handleMouseLeave = useCallback(() => {
    canvasPointerRef.current = null;
    stopEdgePan();
    if (isPanning) {
      setIsPanning(false);
      releasePanInteractionRasterSoon();
      setPosition(positionRef.current);
    }
    if (isDrawing) {
      setIsDrawing(false);
      setDrawingBox(null);
    }
    // Clear path preview but keep path points
    setPathPreviewPoint(null);
    // Clear hover tooltip
    setHoveredItem(null);
    setCursorPosition(null);
  }, [isPanning, isDrawing, releasePanInteractionRasterSoon, stopEdgePan]);

  // Helper to finish path drawing and create bbox
  const finishPathDrawing = useCallback(() => {
    if (pathPoints.length < 4) { // Need at least 2 points
      setPathPoints([]);
      setPathPreviewPoint(null);
      return;
    }

    // Compute bounding box from path points with a small width buffer
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i < pathPoints.length; i += 2) {
      xs.push(pathPoints[i]);
      ys.push(pathPoints[i + 1]);
    }

    const lineWidth = 20; // Buffer width around the path
    const bbox: BoundingBox = {
      x1: Math.min(...xs) - lineWidth / 2,
      y1: Math.min(...ys) - lineWidth / 2,
      x2: Math.max(...xs) + lineWidth / 2,
      y2: Math.max(...ys) + lineWidth / 2,
      polygon: pathPoints, // Store the actual path points
    };

    const anchor = computeAnchor(bbox);
    onDraw(bbox, anchor);
    trackCanvasEvent('draw_complete');

    // Note: Path points are NOT cleared here anymore.
    // They remain visible while the dialog is open.
    // The path will be cleared via clearDraftSignal when the user:
    // - Saves the line (after successful save)
    // - Clicks "Clear & Cancel" in the dialog
    // If user clicks "Continue Drawing", the path remains for further editing.
    setPathPreviewPoint(null);
  }, [pathPoints, computeAnchor, onDraw]);

  // Undo last path point
  const undoPathPoint = useCallback(() => {
    if (pathPoints.length < 2) return; // Need at least one point to undo
    
    // Remove last point (x, y pair)
    const lastX = pathPoints[pathPoints.length - 2];
    const lastY = pathPoints[pathPoints.length - 1];
    
    setPathPoints(prev => prev.slice(0, -2));
    setPathRedoStack(prev => [...prev, [lastX, lastY]]);
  }, [pathPoints]);

  // Redo last undone point
  const redoPathPoint = useCallback(() => {
    if (pathRedoStack.length === 0) return;
    
    // Pop from redo stack and add back to path
    const lastUndone = pathRedoStack[pathRedoStack.length - 1];
    setPathRedoStack(prev => prev.slice(0, -1));
    setPathPoints(prev => [...prev, lastUndone[0], lastUndone[1]]);
  }, [pathRedoStack]);

  // Handle double-click to finish path drawing
  const handleDoubleClick = useCallback(() => {
    if (drawMode === 'path' && pathPoints.length >= 4) {
      finishPathDrawing();
    }
  }, [drawMode, pathPoints.length, finishPathDrawing]);

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const fitToScreen = useCallback(() => {
    if (!image) return;
    
    const logicalWidth = imageCanvasWidth || image.width;
    const logicalHeight = imageCanvasHeight || image.height;
    const scaleX = width / logicalWidth;
    const scaleY = height / logicalHeight;
    const newScale = Math.min(scaleX, scaleY);
    
    const newPosition = {
      x: (width - logicalWidth * newScale) / 2,
      y: (height - logicalHeight * newScale) / 2,
    };
    setScale(newScale);
    setPosition(newPosition);
  }, [image, imageCanvasHeight, imageCanvasWidth, width, height]);

  const keyPanCommitMsRef = useRef(0);

  /**
   * Move the drawing to a position a key press computed.
   *
   * A single press commits to React state at once. An auto-repeat writes the
   * stage directly and commits on the same interval the edge-pan loop uses,
   * because a held arrow otherwise re-renders every overlay about thirty times
   * a second — the cost this file's imperative pan paths exist to avoid.
   */
  const applyKeyboardPan = useCallback((nextPosition: { x: number; y: number }, isRepeat: boolean) => {
    positionRef.current = nextPosition;
    const stage = stageRef.current;
    if (!isRepeat || !stage) {
      keyPanActiveRef.current = false;
      setPosition(nextPosition);
      return;
    }

    keyPanActiveRef.current = true;
    stage.position(nextPosition);
    stage.batchDraw();
    const nowMs = performance.now();
    if (nowMs - keyPanCommitMsRef.current >= EDGE_PAN_COMMIT_INTERVAL_MS) {
      keyPanCommitMsRef.current = nowMs;
      setPosition(nextPosition);
    }
  }, []);

  /** Commit the position a released arrow key left the drawing at. */
  const endKeyboardPan = useCallback(() => {
    if (!keyPanActiveRef.current) return;
    keyPanActiveRef.current = false;
    setPosition(positionRef.current);
  }, []);

  /**
   * Zoom about the cursor, or about the centre of the canvas when the cursor is
   * elsewhere. The clamp runs afterwards because a zoom out shrinks the drawing
   * and can leave it outside the bound the next pan would enforce.
   */
  const zoomByFactor = useCallback((factor: number) => {
    const { viewport, imageSize } = panContextRef.current;
    const anchor = canvasPointerRef.current
      ?? { x: viewport.width / 2, y: viewport.height / 2 };
    const zoomed = zoomAtPoint(positionRef.current, scaleRef.current, anchor, factor);
    const nextPosition = clampPanPosition(zoomed.position, zoomed.scale, viewport, imageSize);
    positionRef.current = nextPosition;
    setScale(zoomed.scale);
    setPosition(nextPosition);
    trackCanvasEvent('key_zoom');
  }, [trackCanvasEvent]);

  // Keyboard handler: Space for pan, Enter to finish path, Escape to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture keys if user is typing in an input/textarea
      const activeEl = document.activeElement;
      const isTyping = activeEl && (
        activeEl.tagName === 'INPUT' || 
        activeEl.tagName === 'TEXTAREA' ||
        (activeEl as HTMLElement).isContentEditable
      );

      if (drawMode === 'path' && traceAssistEnabled && !isTyping && !e.repeat) {
        const dir =
          e.key === 'ArrowRight' ? { dx: 1, dy: 0 } :
          e.key === 'ArrowLeft' ? { dx: -1, dy: 0 } :
          e.key === 'ArrowUp' ? { dx: 0, dy: -1 } :
          e.key === 'ArrowDown' ? { dx: 0, dy: 1 } :
          null;
        if (dir) {
          e.preventDefault();
          traceNextStep(dir);
          return;
        }
      }

      // The navigation keys belong to the drawing only while nothing else is
      // taking keystrokes: no field has focus, no mark dialog is open, and
      // trace assist is not claiming the arrows for its own bend stepping.
      const isDialogOpen = Boolean(pendingMarkBbox);
      const isTraceAssistActive = drawMode === 'path' && traceAssistEnabled;
      // Cmd, Ctrl, and Alt carry shipped browser gestures on these keys, from
      // history navigation to page zoom, and a page cannot cancel all of them.
      const isBrowserGesture = e.metaKey || e.ctrlKey || e.altKey;
      const canNavigate = !isTyping && !isDialogOpen && !isBrowserGesture;

      if (canNavigate && !isTraceAssistActive) {
        const panDelta = arrowPanDelta(e.key, e.shiftKey);
        if (panDelta) {
          e.preventDefault();
          const { viewport, imageSize } = panContextRef.current;
          const nextPosition = clampPanPosition(
            {
              x: positionRef.current.x + panDelta.dx,
              y: positionRef.current.y + panDelta.dy,
            },
            scaleRef.current,
            viewport,
            imageSize
          );
          applyKeyboardPan(nextPosition, e.repeat);
          trackCanvasEvent('arrow_pan');
          return;
        }
      }

      if (canNavigate) {
        // Zoom keys anchor on the cursor, so an SME reading one corner of a
        // drawing zooms into that corner.
        const zoomFactor = zoomKeyFactor(e.key, isBrowserGesture);
        if (zoomFactor !== null) {
          e.preventDefault();
          zoomByFactor(zoomFactor);
          return;
        }

        if (e.key === '0') {
          e.preventDefault();
          resetZoom();
          trackCanvasEvent('key_zoom');
          return;
        }

        // Shift+1 produces '!' on a US layout, and both spellings mean fit.
        if (e.key === '1' || e.key === '!') {
          e.preventDefault();
          fitToScreen();
          trackCanvasEvent('key_zoom');
          return;
        }
      }

      // Space key for temporary pan mode (Figma-style) - works in all modes
      if (e.code === 'Space' && !e.repeat && !isTyping) {
        e.preventDefault();
        setIsSpaceHeld(true);
      }
      
      // The collect mode owns Enter and Escape while it is armed: Enter finishes the
      // draft, Escape peels regions off it one at a time and only leaves the mode once
      // nothing is left to peel. Both return, so Escape never also resets the draw mode
      // out from under a draft the SME is still building.
      if (collectingRegions && canNavigate) {
        if (e.key === 'Enter' && draftRegions.length > 0) {
          e.preventDefault();
          onFinishRegions?.();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          if (draftRegions.length > 0) {
            onRemoveLastRegion?.();
          } else {
            onExitCollect?.();
          }
          return;
        }
      }

      // Escape in instrument sub-mode: clear context override but stay in instrument mode
      if (e.key === 'Escape' && isInstrumentSubMode && !isTyping) {
        e.preventDefault();
        onInstrumentSubModeToggle?.(true); // re-signal active (clears context override in parent)
        return;
      }

      // Path drawing shortcuts
      if (drawMode === 'path' && !isTyping) {
        if ((e.key === 'Enter' || e.key === 'Tab') && pathPoints.length >= 4) {
          e.preventDefault();
          finishPathDrawing();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setPathPoints([]);
          setPathPreviewPoint(null);
          setPathRedoStack([]); // Clear redo stack on cancel
        } else if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
          // Ctrl+Z / Cmd+Z = Undo
          e.preventDefault();
          undoPathPoint();
        } else if (
          (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey) ||
          (e.key === 'y' && (e.ctrlKey || e.metaKey))
        ) {
          // Ctrl+Shift+Z / Cmd+Shift+Z / Ctrl+Y = Redo
          e.preventDefault();
          redoPathPoint();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpaceHeld(false);
      }
      if (arrowPanDelta(e.key, false)) {
        endKeyboardPan();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [drawMode, pathPoints.length, finishPathDrawing, undoPathPoint, redoPathPoint, traceAssistEnabled, traceNextStep, isInstrumentSubMode, onInstrumentSubModeToggle, trackCanvasEvent, resetZoom, fitToScreen, applyKeyboardPan, zoomByFactor, pendingMarkBbox, endKeyboardPan, collectingRegions, draftRegions.length, onFinishRegions, onRemoveLastRegion, onExitCollect]);

  // Auto-fit only when image first loads (not on container resize)
  const [hasInitialFit, setHasInitialFit] = useState(false);
  useEffect(() => {
    if (!image || hasInitialFit) return;
    if (width <= 0 || height <= 0) return;
    fitToScreen();
    setHasInitialFit(true);
  }, [image, hasInitialFit, fitToScreen, width, height]);

  // Reset initial fit when image changes
  useEffect(() => {
    setHasInitialFit(false);
  }, [imageUrl]);

  useEffect(() => {
    if (!selectedCandidateId) return;
    const stillExists = nodePointCandidates.some((candidate) => candidate.id === selectedCandidateId);
    if (!stillExists) {
      setSelectedCandidateId(null);
    }
  }, [nodePointCandidates, selectedCandidateId]);

  // Zoom and pan to focusBbox when it changes (wizard mode)
  useEffect(() => {
    if (!focusBbox) return;
    // A deep link sets the focus before the sheet image has loaded, and the
    // initial fit-to-screen then runs and overwrites it. Wait for that fit, so
    // the focus is the last word on the first paint as well as on later ones.
    if (!hasInitialFit) return;

    // Calculate bbox dimensions with padding
    const bboxWidth = focusBbox.x2 - focusBbox.x1;
    const bboxHeight = focusBbox.y2 - focusBbox.y1;
    const padding = 100; // pixels of padding around the bbox
    const paddedWidth = bboxWidth + padding * 2;
    const paddedHeight = bboxHeight + padding * 2;
    
    // Calculate scale to fit bbox in viewport
    const scaleX = width / paddedWidth;
    const scaleY = height / paddedHeight;
    let newScale = Math.min(scaleX, scaleY);
    
    // Clamp scale to reasonable bounds (don't zoom in too much for small items)
    newScale = Math.max(MIN_CANVAS_SCALE, Math.min(MAX_CANVAS_SCALE, newScale));
    // Cap to avoid excessive zoom on tiny items, but allow meaningful zoom when searching/navigating.
    const focusZoomCap = wizardMode ? 2.5 : 3.0;
    newScale = Math.min(newScale, focusZoomCap);
    
    // Center the bbox in the viewport at the new scale
    const bboxCenterX = (focusBbox.x1 + focusBbox.x2) / 2;
    const bboxCenterY = (focusBbox.y1 + focusBbox.y2) / 2;
    const newPosition = {
      x: width / 2 - bboxCenterX * newScale,
      y: height / 2 - bboxCenterY * newScale,
    };
    
    setScale(newScale);
    setPosition(newPosition);
  }, [focusBbox, width, height, wizardMode, hasInitialFit]);

  // The draw order only needs saying while the SME has yet to draw anything; once a
  // region is down, the numbered overlays and the Done pill say it better.
  const showCollectArmingHint = collectingRegions && draftRegions.length === 0;

  const previousFocusBboxRef = useRef<BoundingBox | null | undefined>(focusBbox);
  useEffect(() => {
    if (fitOnFocusClear && previousFocusBboxRef.current && !focusBbox) {
      fitToScreen();
    }
    previousFocusBboxRef.current = focusBbox;
  }, [fitOnFocusClear, fitToScreen, focusBbox]);

  // Flash/highlight focused area briefly on navigation (search / step selection).
  const FOCUS_FLASH_DURATION_MS = 4000;
  const [focusFlashElapsedMs, setFocusFlashElapsedMs] = useState<number>(FOCUS_FLASH_DURATION_MS);
  const focusFlashRafRef = useRef<number | null>(null);
  const focusFlashKey = focusBbox ? `${focusBbox.x1},${focusBbox.y1},${focusBbox.x2},${focusBbox.y2}` : '';
  useEffect(() => {
    if (!focusBbox) {
      setFocusFlashElapsedMs(FOCUS_FLASH_DURATION_MS);
      return;
    }

    if (focusFlashRafRef.current) cancelAnimationFrame(focusFlashRafRef.current);
    setFocusFlashElapsedMs(0);

    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      if (elapsed >= FOCUS_FLASH_DURATION_MS) {
        setFocusFlashElapsedMs(FOCUS_FLASH_DURATION_MS);
        focusFlashRafRef.current = null;
        return;
      }
      setFocusFlashElapsedMs(elapsed);
      focusFlashRafRef.current = requestAnimationFrame(tick);
    };

    focusFlashRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (focusFlashRafRef.current) cancelAnimationFrame(focusFlashRafRef.current);
      focusFlashRafRef.current = null;
    };
  }, [focusFlashKey]);

  const defaultDrawMode: DrawMode = wizardMode
    ? wizardStep === 4
      ? nodeDrawingShape === 'duct' ? 'rect' : 'path'
      : wizardStep === 3
        ? 'rect'
        : 'select'
    : drawMode;

  // Track previous draw mode to detect actual mode switches
  const prevDrawModeRef = useRef<DrawMode | null>(null);
  
  // Auto-switch draw mode based on wizard step
  useEffect(() => {
    const isModeSwitching = prevDrawModeRef.current !== defaultDrawMode;

    // Reset instrument sub-mode on step change
    if (isModeSwitching) {
      setIsInstrumentSubMode(false);
      onInstrumentSubModeToggle?.(false);
    }

    if (wizardMode && defaultDrawMode === 'path') {
      // Step 4 (node connections) - path mode for drawing lines
      setDrawMode(defaultDrawMode);
      // Only clear path points when SWITCHING to path mode, not when staying in it
      if (isModeSwitching) {
        setPathPoints([]);
        setPathPreviewPoint(null);
        setPathRedoStack([]);
        setPolyPoints([]);
        setIsPolyDrawing(false);
      }
    } else if (wizardMode && defaultDrawMode === 'rect') {
      // Step 3 (equipment) - box mode for marking
      setDrawMode(defaultDrawMode);
      if (isModeSwitching) {
        setPolyPoints([]);
        setIsPolyDrawing(false);
        setPathPoints([]);
        setPathRedoStack([]);
      }
    } else if (wizardMode) {
      // Other steps - select/pan mode
      setDrawMode(defaultDrawMode);
    }

    prevDrawModeRef.current = defaultDrawMode;
  }, [wizardMode, defaultDrawMode]);

  // The workspace owns the arm; `isInstrumentSubMode` is the canvas's mirror of it, and
  // both edges have to be mirrored. A sub-mode that outlives the workspace's draft
  // leaves the Instrument button lit over a canvas that no longer collects, and the
  // next drag falls through to the step's own draw path — the equipment dialog on
  // step 3, the line popover on step 4 — instead of marking an instrument. This runs
  // after the wizard-step switch above, so the mirror settles on the parent's truth.
  useEffect(() => {
    if (collectingRegions === isInstrumentSubMode) return;
    setIsInstrumentSubMode(collectingRegions);
    if (!collectingRegions) {
      setDrawMode(defaultDrawMode);
      return;
    }
    setDrawMode('rect');
    setPathPoints([]);
    setPathPreviewPoint(null);
    setPathRedoStack([]);
    setPolyPoints([]);
    setIsPolyDrawing(false);
  }, [collectingRegions]);

  useEffect(() => {
    const selectionCount = selectedWizardItemKeys?.size ?? 0;
    const hadNoSelection = previousSavedSelectionCountRef.current === 0;
    previousSavedSelectionCountRef.current = selectionCount;
    if (
      wizardMode &&
      !effectiveReadOnly &&
      selectionCount > 0 &&
      hadNoSelection
    ) {
      setDrawMode('select');
    }
  }, [effectiveReadOnly, selectedWizardItemKeys?.size, wizardMode]);

  useEffect(() => {
    if (wizardMode && !effectiveReadOnly && savedAnnotationEditModeSignal > 0) {
      setDrawMode('select');
    }
  }, [effectiveReadOnly, savedAnnotationEditModeSignal, wizardMode]);

  // Get visual style (color + dash pattern) for marking status
  const getStatusStyle = (status: string, id?: string): MarkingStatusStyle => {
    // Selected item gets blue highlight
    if (id && selectedSectionId === id) {
      return { color: '#2563eb', dash: [4, 4], fill: 'rgba(37, 99, 235, 0.15)', label: 'Selected' };
    }

    // Handle new 3-state marking statuses
    if (status in MARKING_STATUS_STYLES) {
      return MARKING_STATUS_STYLES[status as MarkingStatus];
    }

    // Legacy/backend section statuses (for backward compatibility)
    switch (status) {
      case 'done':
        return { color: '#22c55e', dash: null, fill: 'rgba(34, 197, 94, 0.1)', label: 'Done' };
      case 'processing':
        return { color: '#f59e0b', dash: [8, 4], fill: 'rgba(245, 158, 11, 0.1)', label: 'Processing' };
      case 'error':
        return { color: '#ef4444', dash: [4, 4], fill: 'rgba(239, 68, 68, 0.1)', label: 'Error' };
      // Map old statuses to new ones for backward compatibility
      case 'unmarked':
        return MARKING_STATUS_STYLES.ai_identified;
      case 'marked':
        return MARKING_STATUS_STYLES.in_progress;
      default:
        // For pending pieces - if context empty use orange else grey
        if (!status.trim()) {
          return { color: '#ff6b35', dash: [4, 4], fill: 'rgba(255, 107, 53, 0.1)', label: 'Pending' };
        }
        return { color: 'var(--raven-text-secondary)', dash: [4, 4], fill: 'rgba(107, 114, 128, 0.1)', label: 'Unknown' };
    }
  };

  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (isDrawing) return 'crosshair';
    // Space held = grab cursor for panning (works in all modes)
    if (isSpaceHeld) return 'grab';
    if (isPanning) return 'grabbing';
    if (isPolyDrawing) return 'crosshair';
    // Comment mode - use a custom comment cursor
    if (drawMode === 'comment') return 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'%23f59e0b\' stroke=\'%23000\' stroke-width=\'1\'%3E%3Cpath d=\'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z\'/%3E%3C/svg%3E") 12 12, crosshair';
    // Crop mode - crosshair for selecting region
    if (drawMode === 'crop' || isCropping) return 'crosshair';
    // Select annotations mode - crosshair for rectangle selection
    if (drawMode === 'select-annotations' || isSelectingAnnotations) return 'crosshair';
    if (drawMode === 'shift-annotations') return isAnnotationShiftSaving ? 'wait' : 'move';
    // Select mode - default pointer for clicking/selecting
    if (drawMode === 'select') return 'default';
    if (drawMode === 'path') return 'crosshair';
    if (drawMode === 'rect') return 'crosshair';
    if (drawMode === 'poly') return 'crosshair';
    return 'grab';
  };

  /**
   * One statement per key on this strip. Trace assist owns the arrows in path
   * mode, and it prints its own line, so the pan hint drops them there.
   */
  const navigationHint = (() => {
    const panWays: string[] = [];
    if (edgePanEnabled) panWays.push('rest at an edge');
    if (!(drawMode === 'path' && traceAssistEnabled)) panWays.push('hold an arrow key');
    const panPart = panWays.length ? `↔ ${panWays.join(' or ')} to pan · ` : '';
    return `${panPart}+ − to zoom · 0 reset · 1 fit`;
  })();

  const canSelectMark = () => {
    if (!onSelectWizardItem) return false;
    if (panMovedRef.current || isSpaceHeld) return false;
    if (drawMode === 'comment' || drawMode === 'shift-annotations') return false;
    if (isDrawing || isPolyDrawing) return false;
    if (drawMode === 'path' && pathPoints.length > 0) return false;
    return true;
  };

  const getTargetStyle = (tone?: DrawTargetTone) => {
    if (!tone || tone === 'new') {
      return { bg: '#f3f4f6', border: '#e5e7eb', text: '#374151' };
    }
    if (tone === 'missing') {
      return { bg: '#fef3c7', border: '#fde68a', text: '#92400e' };
    }
    const statusStyle = MARKING_STATUS_STYLES[tone];
    /* The status colour is an ACCENT, tuned to carry meaning as a fill and a
       border. Reused as label ink it fell to 2.8:1, so the chip keeps the
       accent on its fill and border and the words take readable ink. */
    return { bg: statusStyle.fill, border: statusStyle.color, text: 'var(--raven-text-primary)' };
  };

  const targetStyle = drawTarget ? getTargetStyle(drawTarget.tone) : null;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: widthProp ? widthProp : '100%', height: heightProp ? heightProp : '100%' }}>
      {/* Draw mode toggle */}
      {wizardMode && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 11,
          padding: '6px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 6,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--raven-text-secondary)',
            letterSpacing: '0.08em',
            marginRight: 4,
          }}>
            MODE
          </span>
          <button
            onClick={() => {
              setDrawMode('select');
              setIsInstrumentSubMode(false);
              onInstrumentSubModeToggle?.(false);
            }}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              backgroundColor: drawMode === 'select' ? '#2563eb' : 'white',
              color: drawMode === 'select' ? 'white' : '#111827',
              border: '1px solid #d1d5db',
              borderRadius: 4,
              cursor: 'pointer',
            }}
            title="Pan mode"
          >
            👆 Pan
          </button>
          {!readOnly && onShiftWizardAnnotations && (
            <button
              onClick={() => {
                setDrawMode('shift-annotations');
                setIsInstrumentSubMode(false);
                onInstrumentSubModeToggle?.(false);
                setPolyPoints([]);
                setIsPolyDrawing(false);
                setPathPoints([]);
                setPathPreviewPoint(null);
                setPathRedoStack([]);
                setAnnotationShiftPreview({ dx: 0, dy: 0 });
              }}
              disabled={!annotationBounds || isAnnotationShiftSaving}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: drawMode === 'shift-annotations' ? '#111827' : 'white',
                color: drawMode === 'shift-annotations' ? 'white' : '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: annotationBounds && !isAnnotationShiftSaving ? 'pointer' : 'not-allowed',
                opacity: annotationBounds ? 1 : 0.55,
              }}
              title={
                annotationBounds
                  ? 'Move all saved annotations together'
                  : 'No saved annotations to move'
              }
            >
              Move All
            </button>
          )}
          {!readOnly && onSelectAllWizardItems && (
            <button
              onClick={() => {
                setDrawMode('select');
                onSelectAllWizardItems();
              }}
              disabled={!annotationBounds}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: selectedWizardItemKeys?.size ? '#eff6ff' : 'white',
                color: selectedWizardItemKeys?.size ? '#1d4ed8' : '#111827',
                border: selectedWizardItemKeys?.size ? '1px solid #93c5fd' : '1px solid #d1d5db',
                borderRadius: 4,
                cursor: annotationBounds ? 'pointer' : 'not-allowed',
                opacity: annotationBounds ? 1 : 0.55,
              }}
              title={annotationBounds ? 'Select all saved annotations' : 'No saved annotations to select'}
            >
              Select All
            </button>
          )}
          {!readOnly && selectedWizardItemKeys?.size ? (
            <>
              <span
                style={{
                  padding: '4px 6px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#1f2937',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                }}
              >
                {selectedWizardItemKeys.size} selected
              </span>
              {onDeleteSelectedWizardItems && (
                <button
                  onClick={onDeleteSelectedWizardItems}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#fef2f2',
                    color: '#991b1b',
                    border: '1px solid #fecaca',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Delete selected saved annotations"
                >
                  Delete selected
                </button>
              )}
              {onMarkSelectedAsRedundancySet && (
                <button
                  onClick={onMarkSelectedAsRedundancySet}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    color: 'var(--raven-text-primary)',
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Put the selected tagged items in one redundancy set. Saves immediately."
                >
                  Mark as redundancy set
                </button>
              )}
              {onClearWizardItemSelection && (
                <button
                  onClick={onClearWizardItemSelection}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    color: '#4b5563',
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Clear saved annotation selection"
                >
                  Clear
                </button>
              )}
            </>
          ) : null}
          {!readOnly && wizardStep === 4 && onNodeDrawingShapeChange && (
            <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: 4, overflow: 'hidden' }}>
              {(['pipeline', 'duct'] as const).map((shape) => (
                <button
                  key={shape}
                  type="button"
                  onClick={() => onNodeDrawingShapeChange(shape)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    border: 'none',
                    borderRight: shape === 'pipeline' ? '1px solid #d1d5db' : 'none',
                    backgroundColor: nodeDrawingShape === shape ? 'var(--raven-primary-main)' : 'white',
                    color: nodeDrawingShape === shape ? 'white' : '#111827',
                    cursor: 'pointer',
                  }}
                  title={shape === 'pipeline' ? 'Draw a linear pipeline path' : 'Draw a rectangular duct'}
                >
                  {shape === 'pipeline' ? 'Pipeline' : 'Duct'}
                </button>
              ))}
            </div>
          )}
          {!readOnly && defaultDrawMode !== 'select' && (
            <button
              onClick={() => {
                setDrawMode(defaultDrawMode);
                setIsInstrumentSubMode(false);
                onInstrumentSubModeToggle?.(false);
              }}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: drawMode === defaultDrawMode && !isInstrumentSubMode ? 'var(--raven-primary-main)' : 'white',
                color: drawMode === defaultDrawMode && !isInstrumentSubMode ? 'white' : '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              title="Draw mode"
            >
              {nodeDrawingShape === 'duct' && wizardStep === 4 ? '▭ Duct' : defaultDrawMode === 'path' ? '📐 Line' : '▢ Box'}
            </button>
          )}
          {!readOnly && (wizardStep === 3 || wizardStep === 4) && (
            <button
              onClick={() => {
                const entering = !isInstrumentSubMode;
                setIsInstrumentSubMode(entering);
                if (entering) {
                  setDrawMode('rect');
                  setPathPoints([]);
                  setPathPreviewPoint(null);
                  setPathRedoStack([]);
                  setPolyPoints([]);
                  setIsPolyDrawing(false);
                } else {
                  setDrawMode(defaultDrawMode);
                }
                onInstrumentSubModeToggle?.(entering);
              }}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: isInstrumentSubMode ? '#7c3aed' : 'white',
                color: isInstrumentSubMode ? 'white' : '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              title="Draw instrument bounding box"
            >
              🔧 Instrument
            </button>
          )}
          {!readOnly && wizardStep === 4 && nodeDrawingShape === 'pipeline' && traceAssistAvailable && (
            <button
              onClick={() => setTraceAssistEnabled((prev) => !prev)}
              disabled={!docId}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: traceAssistEnabled ? '#111827' : 'white',
                color: traceAssistEnabled ? 'white' : '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: docId ? 'pointer' : 'not-allowed',
                opacity: docId ? 1 : 0.5,
              }}
              title={docId ? 'Trace assist (Arrow keys)' : 'Trace assist unavailable (missing doc)'}
            >
              {traceAssistBusy ? '⏳' : '⚡'} Trace
            </button>
          )}
          {/* Crop mode - only on Templates step (step 2) */}
          {!readOnly && wizardStep === 2 && onCrop && (
            <button
              onClick={() => {
                setDrawMode('crop');
                setPolyPoints([]);
                setIsPolyDrawing(false);
                setPathPoints([]);
                setPathPreviewPoint(null);
                setPathRedoStack([]);
              }}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: drawMode === 'crop' ? '#7c3aed' : 'white',
                color: drawMode === 'crop' ? 'white' : '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              title="Crop region to create template"
            >
              ✂️ Crop
            </button>
          )}
          {/* Hint text for crop mode */}
          {drawMode === 'crop' && (
            <span style={{ fontSize: 12, color: 'var(--raven-text-secondary)', marginLeft: 8 }}>
              Click and drag to select a region
            </span>
          )}
          {/* Hint text for Step 2 modifier-drag selection */}
          {!readOnly && wizardStep === 2 && drawMode === 'select' && onSelectAnnotation && (
            <span style={{ fontSize: 12, color: 'var(--raven-text-secondary)', marginLeft: 8 }}>
              Cmd/Ctrl + drag to select annotations
            </span>
          )}
          {/* Selection toolbar - shows when annotations are selected */}
          {!readOnly && selectedAnnotationIds && selectedAnnotationIds.size > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginLeft: 12,
              paddingLeft: 12,
              borderLeft: '1px solid #e5e7eb',
            }}>
              <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>
                {selectedAnnotationIds.size} selected
              </span>
              {onBulkDeleteAnnotations && (
                <button
                  onClick={onBulkDeleteAnnotations}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Delete selected annotations"
                >
                  Delete Selected
                </button>
              )}
              {onClearAnnotationSelection && (
                <button
                  onClick={onClearAnnotationSelection}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Clear selection"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {/* Comment mode - same level as other modes */}
          {onToggleCommentMode && (
            <button
              onClick={() => {
                if (drawMode === 'comment') {
                  setDrawMode('select');
                  onToggleCommentMode(); // notify parent we're exiting
                } else {
                  setDrawMode('comment');
                  onToggleCommentMode(); // notify parent we're entering
                }
              }}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: drawMode === 'comment' ? '#f59e0b' : 'white',
                color: drawMode === 'comment' ? 'white' : '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              title={drawMode === 'comment' ? 'Exit comment mode' : 'Add comments to canvas'}
            >
              💬 Comment
            </button>
          )}
        </div>
      )}

      {!hidePerfHud && perfDebugEnabled && (
        <CanvasPerfHud
          scale={scale}
          perfStats={perfStats}
          nodePointMode={nodePointMode}
          setNodePointMode={setNodePointMode}
          cursorPosition={cursorPosition}
          onHide={() => setPerfDebugEnabled(false)}
        />
      )}

      {/* Zoom Controls */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={() => zoomByFactor(ZOOM_STEP_FACTOR)}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          +
        </button>
        <div style={{ fontSize: '12px', textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </div>
        <button
          onClick={() => zoomByFactor(1 / ZOOM_STEP_FACTOR)}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          -
        </button>
        <button
          onClick={resetZoom}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Reset
        </button>
        <button
          onClick={fitToScreen}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Fit
        </button>
      </div>

      {drawTarget && drawTarget.label && targetStyle && (
        <div
          style={{
            position: 'absolute',
            bottom: '54px',
            left: '10px',
            zIndex: 10,
            backgroundColor: targetStyle.bg,
            border: `1px solid ${targetStyle.border}`,
            color: targetStyle.text,
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            maxWidth: '420px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span style={{ fontWeight: 600 }}>Drawing For:</span>
          <span style={{ fontWeight: 600, color: '#111827' }}>{drawTarget.label}</span>
          {drawTarget.detail && (
            <span style={{ color: 'var(--raven-text-secondary)' }}>{drawTarget.detail}</span>
          )}
        </div>
      )}

      {/* Instructions */}
      {!hideInstructions && <div style={{
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        zIndex: 10,
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '8px',
        borderRadius: '4px',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        {isSpaceHeld && <span style={{ color: '#fbbf24' }}>⎵ Panning...</span>}
        {!isSpaceHeld && navigationHint && <span style={{ color: '#94a3b8' }}>{navigationHint}</span>}
        {!isSpaceHeld && drawMode === 'select' && <span style={{ color: '#a5b4fc' }}>👆 Pan mode: Drag to pan, hover to inspect</span>}
        {!isSpaceHeld && drawMode === 'select' && wizardStep === 4 && (
          <span style={{ color: '#93c5fd' }}>✍ Double-click a line to adjust its points</span>
        )}
        {/* An armed collect mode with nothing drawn yet needs the draw order, not the
            generic box-mode wording, so it takes the same slot rather than adding one. */}
        {!isSpaceHeld && showCollectArmingHint && (
          <span style={{ color: '#93c5fd' }}>▢ {COLLECT_ARMING_HINT}</span>
        )}
        {!isSpaceHeld && drawMode === 'rect' && !showCollectArmingHint && <span>▢ Box mode: Drag to draw rectangle</span>}
        {!isSpaceHeld && drawMode === 'poly' && <span>✏️ Draw mode: Drag to draw shape</span>}
        {!isSpaceHeld && drawMode === 'shift-annotations' && (
          <span style={{ color: '#93c5fd' }}>
            Move all mode: drag the dashed boundary to shift saved annotations
          </span>
        )}
        {!isSpaceHeld && drawMode === 'path' && (
          <>
            <span>Click to add points</span>
            {pathPoints.length >= 2 && (
              <span style={{ color: '#4ade80' }}>
                ({pathPoints.length / 2} pts)
              </span>
            )}
            {pathPoints.length >= 4 && ' | Dbl-click, Enter or Tab to finish'}
            {wizardStep === 4 && traceAssistEnabled && (
              <span style={{ color: '#fbbf24' }}>
                {' '}| Arrows: next bend
              </span>
            )}
            {traceAssistStatus && (
              <span style={{ color: '#f59e0b' }}>
                {' '}| {traceAssistStatus}
              </span>
            )}
            {/* Undo/Redo/Clear buttons */}
            {(pathPoints.length > 0 || pathRedoStack.length > 0) && (
              <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
                <button
                  onClick={undoPathPoint}
                  disabled={pathPoints.length < 2}
                  style={{
                    padding: '3px 6px',
                    backgroundColor: pathPoints.length >= 2 ? '#3b82f6' : '#4b5563',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: pathPoints.length >= 2 ? 'pointer' : 'not-allowed',
                    fontSize: '12px',
                    opacity: pathPoints.length >= 2 ? 1 : 0.5,
                  }}
                  title="Undo last point (Ctrl+Z)"
                >
                  ↩ Undo
                </button>
                <button
                  onClick={redoPathPoint}
                  disabled={pathRedoStack.length === 0}
                  style={{
                    padding: '3px 6px',
                    backgroundColor: pathRedoStack.length > 0 ? '#3b82f6' : '#4b5563',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: pathRedoStack.length > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '12px',
                    opacity: pathRedoStack.length > 0 ? 1 : 0.5,
                  }}
                  title="Redo last point (Ctrl+Shift+Z)"
                >
                  ↪ Redo
                </button>
                <button
                  onClick={() => {
                    setPathPoints([]);
                    setPathPreviewPoint(null);
                    setPathRedoStack([]);
                  }}
                  style={{
                    padding: '3px 6px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                  title="Clear all points (Esc)"
                >
                  ✕ Clear
                </button>
              </div>
            )}
          </>
        )}
        <span style={{ color: '#9ca3af', marginLeft: '8px' }}>
          ⎵ Space: Pan | Scroll: Zoom{drawMode === 'path' && pathPoints.length >= 2 ? ' | Ctrl+Z: Undo' : ''}
        </span>
      </div>}

      <div style={{ 
        border: '1px solid #ddd', 
        borderRadius: '8px', 
        overflow: 'hidden',
        cursor: getCursor()
      }}>
        {/* Guard: Don't render Stage with 0 dimensions (causes Konva crash during layout reflow) */}
        {width > 0 && height > 0 && <Stage
          ref={stageRef}
          width={width}
          height={height}
          scaleX={scale}
          scaleY={scale}
          x={position.x}
          y={position.y}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onDblClick={handleDoubleClick}
          onContextMenu={(e) => e.evt.preventDefault()}
        >
          <Layer>
            {image && (
              <KonvaImage
                image={usingInteractionImage && interactionImage ? interactionImage : image}
                width={imageCanvasWidth || image.width}
                height={imageCanvasHeight || image.height}
              />
            )}

            {inspectionOverlay?.dimOutsideFootprint && inspectionOverlay.footprint && imageCanvasWidth && imageCanvasHeight && (
              <>
                <Rect
                  x={0}
                  y={0}
                  width={imageCanvasWidth}
                  height={Math.max(0, inspectionOverlay.footprint.y1)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
                <Rect
                  x={0}
                  y={inspectionOverlay.footprint.y2}
                  width={imageCanvasWidth}
                  height={Math.max(0, imageCanvasHeight - inspectionOverlay.footprint.y2)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
                <Rect
                  x={0}
                  y={inspectionOverlay.footprint.y1}
                  width={Math.max(0, inspectionOverlay.footprint.x1)}
                  height={Math.max(0, inspectionOverlay.footprint.y2 - inspectionOverlay.footprint.y1)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
                <Rect
                  x={inspectionOverlay.footprint.x2}
                  y={inspectionOverlay.footprint.y1}
                  width={Math.max(0, imageCanvasWidth - inspectionOverlay.footprint.x2)}
                  height={Math.max(0, inspectionOverlay.footprint.y2 - inspectionOverlay.footprint.y1)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
              </>
            )}

            {inspectionOverlay?.footprint && (
              <Rect
                x={inspectionOverlay.footprint.x1}
                y={inspectionOverlay.footprint.y1}
                width={inspectionOverlay.footprint.x2 - inspectionOverlay.footprint.x1}
                height={inspectionOverlay.footprint.y2 - inspectionOverlay.footprint.y1}
                stroke="#FF9800"
                strokeWidth={4 / scale}
                listening={false}
              />
            )}

            {(inspectionOverlay?.tiles ?? []).map((item, index) => (
              <Rect
                key={`inspection-tile-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#6A1B9A"
                strokeWidth={2 / scale}
                dash={[10 / scale, 6 / scale]}
                listening={false}
              />
            ))}

            {(inspectionOverlay?.textBoxes ?? []).map((item, index) => (
              <Rect
                key={`inspection-text-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#0288D1"
                fill="rgba(2, 136, 209, 0.10)"
                strokeWidth={1.5 / scale}
                listening={false}
              />
            ))}

            {(inspectionOverlay?.equipmentBoxes ?? []).map((item, index) => (
              <Rect
                key={`inspection-equipment-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#1976D2"
                fill="rgba(25, 118, 210, 0.12)"
                strokeWidth={3 / scale}
                listening={false}
              />
            ))}

            {(inspectionOverlay?.lineBoxes ?? []).map((item, index) => (
              <Rect
                key={`inspection-line-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#388E3C"
                fill="rgba(56, 142, 60, 0.12)"
                strokeWidth={3 / scale}
                listening={false}
              />
            ))}
            
            {/* Existing sections */}
            {sections
              .filter((section) => {
                const force = section.id === selectedSectionId;
                return isInViewport(section.bbox, force);
              })
              .map((section) => {
              // Check if section has polygon data representing component boxes
              const hasComponentBoxes = section.bbox.polygon && section.bbox.polygon.length >= 8 && section.bbox.polygon.length % 8 === 0;
              
              if (hasComponentBoxes && section.bbox.polygon) {
                // Render multiple component rectangles
                const numBoxes = section.bbox.polygon.length / 8;
                const components = [];
                
                for (let i = 0; i < numBoxes; i++) {
                  const startIdx = i * 8;
                  const x1 = section.bbox.polygon[startIdx];
                  const y1 = section.bbox.polygon[startIdx + 1];
                  const x2 = section.bbox.polygon[startIdx + 2];
                  const y2 = section.bbox.polygon[startIdx + 5];
                  const style = getStatusStyle(section.status, section.id);
                  
                  components.push(
                    <Rect
                      key={`${section.id}-component-${i}`}
                      x={x1}
                      y={y1}
                      width={x2 - x1}
                      height={y2 - y1}
                      stroke={style.color}
                      strokeWidth={2 / scale}
                      dash={style.dash ? style.dash.map(d => d / scale) : undefined}
                      fill={style.fill}
                      listening={drawMode === 'select' && !isPanning}
                      onClick={() => onSelectSection(section.id)}
                    />
                  );
                }
                
                return components;
              } else {
                // Render as single rectangle (existing behavior)
                const style = getStatusStyle(section.status, section.id);
                return (
                  <Rect
                    key={section.id}
                    x={section.bbox.x1}
                    y={section.bbox.y1}
                    width={section.bbox.x2 - section.bbox.x1}
                    height={section.bbox.y2 - section.bbox.y1}
                    stroke={style.color}
                    strokeWidth={2 / scale}
                    dash={style.dash ? style.dash.map(d => d / scale) : undefined}
                    fill={style.fill}
                    listening={drawMode === 'select' && !isPanning}
                    onClick={() => onSelectSection(section.id)}
                  />
                );
              }
            }).flat()}

            {/* Current drawing box */}
            {drawingBox && (
              <Rect
                x={drawingBox.x}
                y={drawingBox.y}
                width={drawingBox.width}
                height={drawingBox.height}
                stroke={wizardStep === 2 ? '#7c3aed' : '#3b82f6'}
                strokeWidth={4 / scale}
                fill={wizardStep === 2 ? 'rgba(124, 58, 237, 0.2)' : 'rgba(59, 130, 246, 0.15)'}
                listening={false}
              />
            )}

            {/* Crop selection overlay */}
            {drawMode === 'crop' && cropStart && cropEnd && (() => {
              const imgWidth = imageCanvasWidth || (image as any)?.naturalWidth || image?.width || 1000;
              const imgHeight = imageCanvasHeight || (image as any)?.naturalHeight || image?.height || 1000;
              const cropRect = {
                x: Math.min(cropStart.x, cropEnd.x),
                y: Math.min(cropStart.y, cropEnd.y),
                width: Math.abs(cropEnd.x - cropStart.x),
                height: Math.abs(cropEnd.y - cropStart.y),
              };
              
              // Only render overlay if selection has positive dimensions
              if (cropRect.width < 1 || cropRect.height < 1) {
                return null;
              }
              
              // Calculate overlay rectangle dimensions, ensuring all are positive
              const leftWidth = Math.max(0, cropRect.x);
              const topHeight = Math.max(0, cropRect.y);
              const rightWidth = Math.max(0, imgWidth - cropRect.x - cropRect.width);
              const bottomHeight = Math.max(0, imgHeight - cropRect.y - cropRect.height);
              
              return (
                <>
                  {/* Semi-transparent overlay outside selection */}
                  {leftWidth > 0 && <Rect x={0} y={0} width={leftWidth} height={imgHeight} fill="rgba(0,0,0,0.4)" listening={false} />}
                  {topHeight > 0 && <Rect x={cropRect.x} y={0} width={cropRect.width} height={topHeight} fill="rgba(0,0,0,0.4)" listening={false} />}
                  {rightWidth > 0 && <Rect x={cropRect.x + cropRect.width} y={0} width={rightWidth} height={imgHeight} fill="rgba(0,0,0,0.4)" listening={false} />}
                  {bottomHeight > 0 && <Rect x={cropRect.x} y={cropRect.y + cropRect.height} width={cropRect.width} height={bottomHeight} fill="rgba(0,0,0,0.4)" listening={false} />}
                  {/* Selection border */}
                  <Rect
                    x={cropRect.x}
                    y={cropRect.y}
                    width={cropRect.width}
                    height={cropRect.height}
                    stroke="#7c3aed"
                    strokeWidth={2 / scale}
                    dash={[6 / scale, 3 / scale]}
                    listening={false}
                  />
                </>
              );
            })()}

            {/* Select annotations rectangle overlay */}
            {isSelectingAnnotations && selectRectStart && selectRectEnd && (() => {
              const selectRect = {
                x: Math.min(selectRectStart.x, selectRectEnd.x),
                y: Math.min(selectRectStart.y, selectRectEnd.y),
                width: Math.abs(selectRectEnd.x - selectRectStart.x),
                height: Math.abs(selectRectEnd.y - selectRectStart.y),
              };
              
              // Only render if selection has positive dimensions
              if (selectRect.width < 1 || selectRect.height < 1) {
                return null;
              }
              
              return (
                <Rect
                  x={selectRect.x}
                  y={selectRect.y}
                  width={selectRect.width}
                  height={selectRect.height}
                  stroke="#3b82f6"
                  strokeWidth={2 / scale}
                  dash={[6 / scale, 3 / scale]}
                  fill="rgba(59, 130, 246, 0.1)"
                  listening={false}
                />
              );
            })()}

            {/* Pending mark box/line (shown while mark form is open) */}
            {pendingMarkBbox && !drawingBox && (
              pendingMarkBbox.polygon && pendingMarkBbox.polygon.length >= 4 ? (
                // Render as a line for node paths
                <>
                  <Line
                    points={pendingMarkBbox.polygon}
                    stroke="#2563eb"
                    strokeWidth={3 / scale}
                    lineCap="round"
                    lineJoin="round"
                    dash={[6 / scale, 3 / scale]}
                    listening={false}
                  />
                  {Array.from({ length: pendingMarkBbox.polygon.length / 2 }).map((_, i) => (
                    <Circle
                      key={`pending-pt-${i}`}
                      x={pendingMarkBbox.polygon![i * 2]}
                      y={pendingMarkBbox.polygon![i * 2 + 1]}
                      radius={8 / scale}
                      fill="#2563eb"
                      stroke="white"
                      strokeWidth={2 / scale}
                      name="pending-point"
                      draggable={Boolean(onUpdatePendingMarkBbox)}
                      onMouseDown={(e) => {
                        e.cancelBubble = true;
                      }}
                      onDragMove={(e) => {
                        if (!onUpdatePendingMarkBbox || !pendingMarkBbox?.polygon) return;
                        e.cancelBubble = true;
                        const next = [...pendingMarkBbox.polygon];
                        next[i * 2] = e.target.x();
                        next[i * 2 + 1] = e.target.y();
                        const xs = next.filter((_, idx) => idx % 2 === 0);
                        const ys = next.filter((_, idx) => idx % 2 === 1);
                        onUpdatePendingMarkBbox({
                          x1: Math.min(...xs),
                          y1: Math.min(...ys),
                          x2: Math.max(...xs),
                          y2: Math.max(...ys),
                          polygon: next,
                        });
                      }}
                      onDragStart={(e) => {
                        e.cancelBubble = true;
                      }}
                      listening={Boolean(onUpdatePendingMarkBbox)}
                    />
                  ))}
                </>
              ) : (
                // Render as a rectangle for equipment/instruments
                <>
                  <Rect
                    x={pendingMarkBbox.x1}
                    y={pendingMarkBbox.y1}
                    width={pendingMarkBbox.x2 - pendingMarkBbox.x1}
                    height={pendingMarkBbox.y2 - pendingMarkBbox.y1}
                    stroke={wizardStep === 2 ? '#7c3aed' : '#2563eb'}
                    strokeWidth={3 / scale}
                    fill={wizardStep === 2 ? 'rgba(124, 58, 237, 0.15)' : 'rgba(37, 99, 235, 0.12)'}
                    name="pending-box"
                    draggable={Boolean(onUpdatePendingMarkBbox)}
                    onDragMove={(e) => {
                      if (!onUpdatePendingMarkBbox) return;
                      e.cancelBubble = true;
                      const node = e.target;
                      const width = pendingMarkBbox.x2 - pendingMarkBbox.x1;
                      const height = pendingMarkBbox.y2 - pendingMarkBbox.y1;
                      updatePendingBbox({
                        x1: node.x(),
                        y1: node.y(),
                        x2: node.x() + width,
                        y2: node.y() + height,
                      });
                    }}
                    onDragStart={(e) => {
                      e.cancelBubble = true;
                    }}
                    listening={Boolean(onUpdatePendingMarkBbox)}
                  />
                  {onUpdatePendingMarkBbox && (() => {
                    const handleSize = 10 / scale;
                    const minSize = 6;
                    const bounds = [
                      { key: 'nw', x: pendingMarkBbox.x1, y: pendingMarkBbox.y1 },
                      { key: 'ne', x: pendingMarkBbox.x2, y: pendingMarkBbox.y1 },
                      { key: 'se', x: pendingMarkBbox.x2, y: pendingMarkBbox.y2 },
                      { key: 'sw', x: pendingMarkBbox.x1, y: pendingMarkBbox.y2 },
                    ];
                    const updateFromHandle = (key: string, x: number, y: number) => {
                      let { x1, y1, x2, y2 } = pendingMarkBbox;
                      if (key === 'nw') {
                        x1 = Math.min(x, x2 - minSize);
                        y1 = Math.min(y, y2 - minSize);
                      } else if (key === 'ne') {
                        x2 = Math.max(x, x1 + minSize);
                        y1 = Math.min(y, y2 - minSize);
                      } else if (key === 'se') {
                        x2 = Math.max(x, x1 + minSize);
                        y2 = Math.max(y, y1 + minSize);
                      } else if (key === 'sw') {
                        x1 = Math.min(x, x2 - minSize);
                        y2 = Math.max(y, y1 + minSize);
                      }
                      updatePendingBbox({ x1, y1, x2, y2 });
                    };
                    return bounds.map((handle) => (
                      <Rect
                        key={`pending-handle-${handle.key}`}
                        x={handle.x - handleSize / 2}
                        y={handle.y - handleSize / 2}
                        width={handleSize}
                        height={handleSize}
                        fill="#ffffff"
                        stroke={wizardStep === 2 ? '#7c3aed' : '#2563eb'}
                        strokeWidth={2 / scale}
                        name="pending-handle"
                        draggable
                        onDragMove={(e) => {
                          e.cancelBubble = true;
                          const node = e.target;
                          updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2);
                        }}
                        onDragStart={(e) => {
                          e.cancelBubble = true;
                        }}
                        listening
                      />
                    ));
                  })()}
                </>
              )
            )}

            {/* Pending pieces */}
            {pendingPieces
              .filter((piece) => {
                const force = piece.id === selectedPendingId;
                return isInViewport(piece.bbox, force);
              })
              .map((piece) => (
                <Rect
                  key={piece.id}
                  x={piece.bbox.x1}
                  y={piece.bbox.y1}
                  width={piece.bbox.x2 - piece.bbox.x1}
                  height={piece.bbox.y2 - piece.bbox.y1}
                  stroke={piece.id===selectedPendingId? '#2563eb': (piece.context ? 'var(--raven-text-secondary)':'#ff6b35')}
                  strokeWidth={2 / scale}
                  dash={[5 / scale, 5 / scale]}
                  fill="rgba(128,128,128,0.15)"
                  listening={drawMode === 'select' && !isPanning && Boolean(onSelectPending)}
                  onClick={() => onSelectPending?.(piece.id)}
                />
              ))}

            {/* Polygon live preview */}
            {drawMode === 'poly' && polyPoints.length > 3 && (
              <Line
                points={polyPoints}
                closed={isPolyDrawing ? false : true}
                stroke="#3b82f6"
                strokeWidth={2 / scale}
                dash={[3 / scale, 3 / scale]}
                fill={isPolyDrawing ? "transparent" : "rgba(59, 130, 246, 0.1)"}
              />
            )}

            {/* Path drawing preview (for connection lines) */}
            {drawMode === 'path' && pathPoints.length >= 2 && (
              <>
                {/* The main path line */}
                <Line
                  points={pathPreviewPoint 
                    ? [...pathPoints, pathPreviewPoint.x, pathPreviewPoint.y] 
                    : pathPoints
                  }
                  stroke="#059669"
                  strokeWidth={3 / scale}
                  lineCap="round"
                  lineJoin="round"
                />
                {/* Points on the path */}
                {Array.from({ length: pathPoints.length / 2 }).map((_, i) => (
                  <Circle
                    key={`path-point-${i}`}
                    x={pathPoints[i * 2]}
                    y={pathPoints[i * 2 + 1]}
                    radius={5 / scale}
                    fill="#059669"
                    stroke="white"
                    strokeWidth={1 / scale}
                  />
                ))}
              </>
            )}

            {/* Wizard Mode: Render PNID data */}
            {wizardMode && pnidState && (
              <Group
                x={annotationShiftPreview.dx}
                y={annotationShiftPreview.dy}
                name="wizard-annotation-layer"
              >
                {/* Equipments - only render marked ones with bbox */}
                {pnidState.equipments
                  .filter(eq => eq.bbox)
                  .filter(eq => {
                    if (showActiveOnly) {
                      // Show only active equipment on step 3
                      if (wizardStep === 3) return eq.id === activeEquipmentId;
                      return false;
                    }
                    // Show all marked equipment on steps 2, 3, 4
                    return [2, 3, 4].includes(wizardStep ?? 0);
                  })
                  .filter(eq => {
                    const force = eq.id === activeEquipmentId || eq.id === highlightedEntityId;
                    return isInViewport(eq.bbox, force);
                  })
                  .map((eq) => {
                  // Equipment is only "active" when on equipment step (3) AND is the selected equipment
                  const isEquipmentStep = wizardStep === 3;
                  const isActive = isEquipmentStep && eq.id === activeEquipmentId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'equipment', id: eq.id })) ?? false;
                  const isHighlighted = eq.id === highlightedEntityId;
                  const bbox = eq.bbox!;
                  const isHovered = hoveredItem?.id === eq.id && hoveredItem?.type === 'equipment';
                  const inactive = wizardInactivePalette('equipment', eq.status === 'final_and_reviewed');
                  // Active/highlighted: blue; inactive: type color until reviewed, then light green
                  const strokeColor = isHovered
                    ? (isActive || isBulkSelected || isHighlighted ? '#1d4ed8' : inactive.hoverStroke)
                    : (isActive || isBulkSelected || isHighlighted ? '#2563eb' : inactive.stroke);
                  const fillColor = isHovered
                    ? (isActive || isBulkSelected || isHighlighted ? 'rgba(37, 99, 235, 0.25)' : inactive.hoverFill)
                    : (isActive || isBulkSelected || isHighlighted ? 'rgba(37, 99, 235, 0.15)' : inactive.fill);
                  return (
                    <React.Fragment key={`eq-${eq.id}`}>
                      <Rect
                        x={bbox.x1}
                        y={bbox.y1}
                        width={bbox.x2 - bbox.x1}
                        height={bbox.y2 - bbox.y1}
                        stroke={strokeColor}
                        strokeWidth={(isHovered || isActive || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                        fill={fillColor}
                        opacity={isActive || isBulkSelected || isHighlighted ? 1 : 0.85}
                        dash={isActive || isBulkSelected || isHighlighted ? undefined : [6 / scale, 3 / scale]}
                        listening={Boolean(onSelectWizardItem)}
                        name="wizard-mark equipment"
                        onClick={(e) => {
                          if (!canSelectMark()) return;
                          e.cancelBubble = true;
                          onSelectWizardItem?.({
                            kind: 'equipment',
                            id: eq.id,
                            screenPosition: getPointerScreenPosition(),
                            isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                          });
                        }}
                        onMouseEnter={() => {
                          cancelHoverClear();
                          setHoveredItem({
                            type: 'equipment',
                            id: eq.id,
                            tag: eq.equipment_no,
                            itemType: eq.equipment_type,
                            bbox,
                          });
                        }}
                        onMouseLeave={scheduleHoverClear}
                      />
                      {(isActive || isBulkSelected || isHovered) && renderPersistedBboxHandles(
                        { kind: 'equipment', id: eq.id },
                        bbox,
                        '#2563eb',
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Equipment Instruments - only render marked ones with bbox */}
                {equipmentInstruments
                  .filter(inst => instrumentRegions(inst).length > 0)
                  .filter(inst => {
                    const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                    if (showActiveOnly) {
                      // Show only active equipment's instruments on step 2
                      return wizardStep === 2 && effectiveContext.context_id === activeEquipmentId;
                    }
                    // Show all equipment instruments on steps 2, 3, 4
                    return [2, 3, 4].includes(wizardStep ?? 0);
                  })
                  .filter(inst => {
                    const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                    const force =
                      inst.id === selectedInstrumentId ||
                      inst.id === highlightedEntityId ||
                      effectiveContext.context_id === activeEquipmentId;
                    // Per region: a balloon drawn across the sheet from its symbol still
                    // draws when only the balloon is on screen.
                    return instrumentRegions(inst).some((region) => isInViewport(region, force));
                  })
                  .map((inst) => {
                  const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                  // Equipment instruments are only "active" on step 2 when parent equipment is active
                  const isEquipmentStep = wizardStep === 2;
                  const isActive = isEquipmentStep && effectiveContext.context_id === activeEquipmentId;
                  const isSelected = inst.id === selectedInstrumentId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'equipmentInstrument', id: inst.id })) ?? false;
                  const isHighlighted = inst.id === highlightedEntityId;
                  const regions = instrumentRegions(inst);
                  const isHovered = hoveredItem?.id === inst.id && hoveredItem?.type === 'equipment_instrument';
                  const reviewed = inst.status === 'final_and_reviewed';
                  const inactive = wizardInactivePalette('instrument', reviewed);
                  const baseStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#15803d' : '#7c3aed')
                    : (isActive || isHighlighted ? '#2563eb' : inactive.stroke);
                  const hoverStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#166534' : '#6d28d9')
                    : (isActive || isHighlighted ? '#1d4ed8' : inactive.hoverStroke);
                  const baseFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.2)' : 'rgba(124, 58, 237, 0.18)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.15)' : inactive.fill);
                  const hoverFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.3)' : 'rgba(124, 58, 237, 0.28)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.25)' : inactive.hoverFill);
                  const strokeColor = isHovered ? hoverStroke : baseStroke;
                  const fillColor = isHovered ? hoverFill : baseFill;
                  const hitPadding = 4 / scale;
                  const shouldTrackHover = !suppressHoverInspection;
                  return (
                    <React.Fragment key={`eq-inst-${inst.id}`}>
                      {/* One rectangle per region, all sharing the marking's hover and
                          selection state, so pointing at the balloon lights the symbol too. */}
                      {regions.map((bbox, regionIndex) => (
                        <React.Fragment key={`eq-inst-${inst.id}-region-${regionIndex}`}>
                          {regions.length > 1 && (
                            <RegionIndexChip
                              region={bbox}
                              index={regionIndex}
                              color={strokeColor}
                              scale={scale}
                            />
                          )}
                          <Rect
                            x={bbox.x1}
                            y={bbox.y1}
                            width={bbox.x2 - bbox.x1}
                            height={bbox.y2 - bbox.y1}
                            stroke={strokeColor}
                            strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={fillColor}
                            opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            dash={isActive || isSelected || isBulkSelected || isHighlighted ? undefined : [4 / scale, 2 / scale]}
                            listening={false}
                            name="wizard-mark equipment-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                          <Rect
                            x={bbox.x1 - hitPadding}
                            y={bbox.y1 - hitPadding}
                            width={bbox.x2 - bbox.x1 + hitPadding * 2}
                            height={bbox.y2 - bbox.y1 + hitPadding * 2}
                            fill="transparent"
                            listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                            name="wizard-mark equipment-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                            onMouseDown={(e) => {
                              if (drawMode === 'select' || e.evt.button !== 0) {
                                e.cancelBubble = true;
                              }
                            }}
                            onClick={(e) => {
                              if (!canSelectMark()) return;
                              e.cancelBubble = true;
                              onSelectWizardItem?.({
                                kind: 'equipmentInstrument',
                                id: inst.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                            onContextMenu={(e) => {
                              e.evt.preventDefault();
                              e.cancelBubble = true;
                              if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                              onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                            }}
                            onMouseEnter={() => {
                              if (!shouldTrackHover) return;
                              cancelHoverClear();
                              setHoveredItem({
                                type: 'equipment_instrument',
                                id: inst.id,
                                tag: getInstrumentPrimaryLabel(inst),
                                itemType: getEquipmentInstrumentName(inst.instrument_type),
                                // The region under the cursor, so the tooltip lands beside
                                // what the SME is actually pointing at.
                                bbox,
                              });
                            }}
                            onMouseLeave={() => {
                              if (!shouldTrackHover) return;
                              scheduleHoverClear();
                            }}
                          />
                          {(isSelected || isBulkSelected || isHovered) && renderPersistedBboxHandles(
                            { kind: 'equipmentInstrument', id: inst.id },
                            bbox,
                            isSelected || isBulkSelected ? '#7c3aed' : '#2563eb',
                            { regionIndex },
                          )}
                        </React.Fragment>
                      ))}
                      {/* The badge deletes the marking, not a region, so it sits on the symbol alone. */}
                      {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                        { kind: 'equipmentInstrument', id: inst.id },
                        regions[0],
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Unattached instruments — no equipment and no line owns them, so no
                    parent's layer draws them. A drawing with neither can hold nothing
                    else, and an SME who marked one has to be able to reach it. They wear
                    the violet dashed outline the unattached template/AI annotations wear
                    below, so "dashed violet" reads as "attached to nothing" everywhere. */}
                {unattachedInstruments
                  .filter(inst => instrumentRegions(inst).length > 0)
                  // Nothing owns these, so "show the active item only" has no active
                  // parent to keep them on screen for.
                  .filter(() => !showActiveOnly && [2, 3, 4].includes(wizardStep ?? 0))
                  .filter(inst => {
                    const force = inst.id === selectedInstrumentId || inst.id === highlightedEntityId;
                    // Per region: a balloon drawn across the sheet from its symbol still
                    // draws when only the balloon is on screen.
                    return instrumentRegions(inst).some((region) => isInViewport(region, force));
                  })
                  .map((inst) => {
                  const isSelected = inst.id === selectedInstrumentId;
                  // An unattached instrument keys as 'equipmentInstrument', the kind the
                  // workspace already gives every instrument that is not a line's.
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'equipmentInstrument', id: inst.id })) ?? false;
                  const isHighlighted = inst.id === highlightedEntityId;
                  const regions = instrumentRegions(inst);
                  const isHovered = hoveredItem?.id === inst.id && hoveredItem?.type === 'unattached_instrument';
                  const reviewed = inst.status === 'final_and_reviewed';
                  const unselected = wizardInactivePalette('instrument', reviewed);
                  const baseStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#15803d' : '#7c3aed')
                    : (isHighlighted ? '#2563eb' : unselected.stroke);
                  const hoverStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#166534' : '#6d28d9')
                    : (isHighlighted ? '#1d4ed8' : unselected.hoverStroke);
                  const baseFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.2)' : 'rgba(124, 58, 237, 0.18)')
                    : (isHighlighted ? 'rgba(37, 99, 235, 0.15)' : unselected.fill);
                  const hoverFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.3)' : 'rgba(124, 58, 237, 0.28)')
                    : (isHighlighted ? 'rgba(37, 99, 235, 0.25)' : unselected.hoverFill);
                  const strokeColor = isHovered ? hoverStroke : baseStroke;
                  const fillColor = isHovered ? hoverFill : baseFill;
                  const hitPadding = 4 / scale;
                  const shouldTrackHover = !suppressHoverInspection;
                  return (
                    <React.Fragment key={`unattached-inst-${inst.id}`}>
                      {/* One rectangle per region, all sharing the marking's hover and
                          selection state, so pointing at the balloon lights the symbol too. */}
                      {regions.map((bbox, regionIndex) => (
                        <React.Fragment key={`unattached-inst-${inst.id}-region-${regionIndex}`}>
                          {regions.length > 1 && (
                            <RegionIndexChip
                              region={bbox}
                              index={regionIndex}
                              color={strokeColor}
                              scale={scale}
                            />
                          )}
                          <Rect
                            x={bbox.x1}
                            y={bbox.y1}
                            width={bbox.x2 - bbox.x1}
                            height={bbox.y2 - bbox.y1}
                            stroke={strokeColor}
                            strokeWidth={(isHovered || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={fillColor}
                            opacity={isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            // Dashed at every state: the dash is what says "attached to nothing".
                            dash={[6 / scale, 3 / scale]}
                            listening={false}
                            name="wizard-mark unattached-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                          <Rect
                            x={bbox.x1 - hitPadding}
                            y={bbox.y1 - hitPadding}
                            width={bbox.x2 - bbox.x1 + hitPadding * 2}
                            height={bbox.y2 - bbox.y1 + hitPadding * 2}
                            fill="transparent"
                            listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                            name="wizard-mark unattached-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                            onMouseDown={(e) => {
                              if (drawMode === 'select' || e.evt.button !== 0) {
                                e.cancelBubble = true;
                              }
                            }}
                            onClick={(e) => {
                              if (!canSelectMark()) return;
                              e.cancelBubble = true;
                              onSelectWizardItem?.({
                                kind: 'equipmentInstrument',
                                id: inst.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                            onContextMenu={(e) => {
                              e.evt.preventDefault();
                              e.cancelBubble = true;
                              if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                              onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                            }}
                            onMouseEnter={() => {
                              if (!shouldTrackHover) return;
                              cancelHoverClear();
                              setHoveredItem({
                                type: 'unattached_instrument',
                                id: inst.id,
                                tag: getInstrumentPrimaryLabel(inst),
                                itemType: inst.instrument_type
                                  ? getInstrumentCategoryName(inst.instrument_type)
                                  : 'Unattached',
                                // The region under the cursor, so the tooltip lands beside
                                // what the SME is actually pointing at.
                                bbox,
                              });
                            }}
                            onMouseLeave={() => {
                              if (!shouldTrackHover) return;
                              scheduleHoverClear();
                            }}
                          />
                          {(isSelected || isBulkSelected || isHovered) && renderPersistedBboxHandles(
                            { kind: 'equipmentInstrument', id: inst.id },
                            bbox,
                            isSelected || isBulkSelected ? '#7c3aed' : '#a78bfa',
                            { regionIndex },
                          )}
                        </React.Fragment>
                      ))}
                      {/* The badge deletes the marking, not a region, so it sits on the symbol alone. */}
                      {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                        { kind: 'equipmentInstrument', id: inst.id },
                        regions[0],
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Nodes - render as visible corridors (like pipes) */}
                {pnidState.nodes
                  .filter((node) => {
                    // During redraw for an existing line, hide the active line so users can draw cleanly.
                    if (isNodeMarkingMode && activeNodeId && node.id === activeNodeId) {
                      return false;
                    }
                    if (showActiveOnly) {
                      // Show only active node on step 4
                      if (wizardStep === 4) return node.id === activeNodeId;
                      return false;
                    }
                    // Show all marked pipelines and rectangular ducts on steps 2, 3, 4.
                    return [2, 3, 4].includes(wizardStep ?? 0) && (
                      node.is_duct ? Boolean(node.duct_bbox) : Boolean(node.polyline && node.polyline.length >= 4)
                    );
                  })
                  .filter((node) => {
                    const nodeBounds =
                      (node.is_duct ? boundsFromBbox(node.duct_bbox) : boundsFromPolyline(node.polyline)) ??
                      boundsFromLineSegments(node.line_segments, segScale.sx, segScale.sy);
                    const force =
                      node.id === activeNodeId ||
                      node.id === highlightedEntityId ||
                      segmentReview?.nodeId === node.id ||
                      Boolean(walkthroughHighlights?.segmentIds.includes(node.id));
                    return isInViewport(
                      nodeBounds,
                      force
                    );
                  })
                  .map((node) => {
                  // Node is only "active" when on node step (4) AND is the selected node
                  const isNodeStep = wizardStep === 4;
                  const isActive = isNodeStep && node.id === activeNodeId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'node', id: node.id })) ?? false;
                  const isHighlighted = node.id === highlightedEntityId;
                  const hasLineSegments = Boolean(node.line_segments && node.line_segments.length > 0);
                  const shouldShowSegmentMarker =
                    isNodeStep && hasLineSegments && !(node.polyline && node.polyline.length >= 4);
                  const shouldShowSegmentReviewOverlay =
                    segmentReview?.nodeId === node.id && segmentReviewDistinct.length > 0;

                  if (node.is_duct && node.duct_bbox) {
                    const duct = node.duct_bbox;
                    const isNodeHovered = hoveredItem?.id === node.id && hoveredItem?.type === 'node';
                    const isEmphasized = isActive || isBulkSelected || isHighlighted;
                    const palette = wizardInactivePalette('line', node.status === 'final_and_reviewed');
                    const stroke = isEmphasized || isNodeHovered ? '#2563eb' : palette.stroke;
                    const fill = isEmphasized || isNodeHovered ? 'rgba(37, 99, 235, 0.18)' : palette.fill;
                    const canSelect = drawMode === 'select' && !isNodeMarkingMode && !isPanning;
                    return (
                      <React.Fragment key={`node-duct-${node.id}`}>
                        <Rect
                          name="wizard-mark node-duct"
                          x={duct.x1}
                          y={duct.y1}
                          width={duct.x2 - duct.x1}
                          height={duct.y2 - duct.y1}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={(isEmphasized || isNodeHovered ? 3 : 2) / scale}
                          dash={isActive ? undefined : [8 / scale, 4 / scale]}
                          listening={canSelect}
                          onMouseEnter={() => {
                            cancelHoverClear();
                            setHoveredItem({
                              type: 'node',
                              id: node.id,
                              tag: `Duct #${node.node_no}`,
                              itemType: `${node.line_no}\n${node.source_ref} → ${node.dest_ref}`,
                              lineNo: node.line_no,
                              bbox: duct,
                            });
                          }}
                          onMouseLeave={scheduleHoverClear}
                          onClick={(e) => {
                            if (!canSelect || !onSelectWizardItem || panMovedRef.current || isSpaceHeld) return;
                            e.cancelBubble = true;
                            onSelectWizardItem({
                              kind: 'node',
                              id: node.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onDblClick={(e) => {
                            if (isSpaceHeld || !onDoubleClickNode) return;
                            e.cancelBubble = true;
                            onDoubleClickNode(node.id, getPointerScreenPosition());
                          }}
                        />
                        {(isActive || isNodeHovered) && (
                          <Text
                            x={duct.x1 + 4 / scale}
                            y={duct.y1 + 4 / scale}
                            text={node.line_no?.trim() ? `Duct #${node.node_no} · ${node.line_no.trim()}` : `Duct #${node.node_no}`}
                            fontSize={13 / scale}
                            fontStyle="bold"
                            fill={stroke}
                            listening={false}
                          />
                        )}
                        {canSelect && (isActive || isBulkSelected || isNodeHovered) && renderPersistedBboxHandles(
                          { kind: 'node', id: node.id },
                          duct,
                          '#2563eb',
                          onDoubleClickNode
                            ? { onDblClick: () => onDoubleClickNode(node.id, getPointerScreenPosition()) }
                            : undefined,
                        )}
                      </React.Fragment>
                    );
                  }
                  
                  // Nodes use polyline directly (not bbox)
                  if (node.polyline && node.polyline.length >= 4) {
                    // Calculate midpoint for label placement
                    const midIdx = Math.floor(node.polyline.length / 4) * 2;
                    const labelX = node.polyline[midIdx] ?? node.polyline[0];
                    const labelY = node.polyline[midIdx + 1] ?? node.polyline[1];
                    
                    // Check if this node is highlighted by walkthrough
                    const isWalkthroughHighlighted = walkthroughHighlights?.segmentIds.includes(node.id) ?? false;
                    
                    // Inactive lines: orange until reviewed, then light green
                    const linePalette = wizardInactivePalette('line', node.status === 'final_and_reviewed');
                    const inactiveColor = linePalette.stroke;
                    const inactiveFill = linePalette.fill;
                    const isEmphasized = isActive || isBulkSelected || isHighlighted;
                    const baseColor = isWalkthroughHighlighted ? '#f59e0b' : (isEmphasized ? '#2563eb' : inactiveColor);
                    const fillColor = isWalkthroughHighlighted
                      ? 'rgba(245, 158, 11, 0.6)'
                      : (isEmphasized ? 'rgba(37, 99, 235, 0.4)' : inactiveFill);
                    const pointColor = isWalkthroughHighlighted ? '#f59e0b' : (isEmphasized ? '#2563eb' : baseColor);
                    const labelColor = isWalkthroughHighlighted ? '#d97706' : (isEmphasized ? '#2563eb' : baseColor);

                    // Corridor width - thicker for visibility (even thicker when walkthrough highlighted)
                    const corridorWidth = (isWalkthroughHighlighted ? 28 : (isEmphasized ? 24 : 12)) / scale;
                    const strokeWidth = (isWalkthroughHighlighted ? 4 : (isEmphasized ? 3 : 1.5)) / scale;
                    
                    // Compute bounding box from polyline for hover tooltip positioning
                    const nodeBbox = boundsFromPolyline(node.polyline) ?? {
                      x1: node.polyline[0],
                      y1: node.polyline[1],
                      x2: node.polyline[0],
                      y2: node.polyline[1],
                    };
                    const isNodeHovered = hoveredItem?.id === node.id && hoveredItem?.type === 'node';
                    const showLabel = (isActive || isNodeHovered) && (!suppressHoverInspection || isActive);
                    const focusT = Math.max(0, Math.min(1, 1 - focusFlashElapsedMs / FOCUS_FLASH_DURATION_MS)); // 1 -> 0
                    const isFocusFlash = Boolean(isActive && focusBbox && focusT > 0);

                    const shouldRenderNodePoints =
                      !reducePassiveOverlays &&
                      (nodePointMode === 'all' ||
                        (nodePointMode === 'active' && (isActive || isNodeHovered || scale >= 1.5)));
                    const pointCount = node.polyline.length / 2;
                    const maxPointDots = scale >= 1.5 ? 800 : 200;
                    const pointStride = pointCount > maxPointDots ? Math.ceil(pointCount / maxPointDots) : 1;
                    
                    return (
                      <React.Fragment key={`node-${node.id}`}>
                        {/* Walkthrough highlight glow effect */}
                        {isWalkthroughHighlighted && (
                          <Line
                            points={node.polyline}
                            stroke="rgba(251, 191, 36, 0.35)"
                            strokeWidth={corridorWidth * 2}
                            lineCap="round"
                            lineJoin="round"
                            opacity={0.8}
                            listening={false}
                          />
                        )}
                        {/* Background corridor - thick semi-transparent line for visibility */}
                        <Line
                          points={node.polyline}
                          stroke={isWalkthroughHighlighted ? 'rgba(251, 191, 36, 0.7)' : (isNodeHovered ? 'rgba(59, 130, 246, 0.5)' : fillColor)}
                          strokeWidth={isWalkthroughHighlighted ? corridorWidth * 1.2 : (isNodeHovered ? corridorWidth * 1.3 : corridorWidth)}
                          lineCap="round"
                          lineJoin="round"
                          opacity={isWalkthroughHighlighted ? 1 : (isActive ? 1 : (isNodeHovered ? 0.92 : 0.82))}
                          listening={true}
                          name="wizard-mark node-line"
                          onMouseEnter={() => setHoveredItem({
                            type: 'node',
                            id: node.id,
                            tag: `Node #${node.node_no}`,
                            itemType: `${node.line_no}\n${node.source_ref} → ${node.dest_ref}`,
                            lineNo: node.line_no,
                            bbox: nodeBbox,
                          })}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={(e) => {
                            // Don't open dialog if in marking mode or actively drawing
                            if (isNodeMarkingMode) return;
                            // Don't allow if actively drawing (rect, poly, or path in progress)
                            if (isDrawing || isPolyDrawing || (drawMode === 'path' && pathPoints.length > 0)) return;
                            if (drawMode === 'comment') return;
                            if (panMovedRef.current || isSpaceHeld) return;
                            if (!onSelectWizardItem) return;
                            e.cancelBubble = true;
                            onSelectWizardItem({
                              kind: 'node',
                              id: node.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onDblClick={(e) => {
                            // Double-click to enter mark mode for this node
                            if (isSpaceHeld) return;
                            e.cancelBubble = true;
                            if (onDoubleClickNode) {
                              const stage = stageRef.current;
                              const pointer = stage?.getPointerPosition();
                              const container = stage?.container();
                              if (pointer && container) {
                                const rect = container.getBoundingClientRect();
                                const screenX = rect.left + pointer.x;
                                const screenY = rect.top + pointer.y;
                                onDoubleClickNode(node.id, { x: screenX, y: screenY });
                              } else {
                                onDoubleClickNode(node.id);
                              }
                            }
                          }}
                        />
                        {/* Main line stroke on top */}
                        <Line
                          points={node.polyline}
                          stroke={isNodeHovered ? '#3b82f6' : baseColor}
                          strokeWidth={isNodeHovered ? strokeWidth * 1.5 : strokeWidth}
                          lineCap="round"
                          lineJoin="round"
                          dash={isActive ? undefined : [8 / scale, 4 / scale]}
                          opacity={isActive ? 1 : (isNodeHovered ? 1 : 0.85)}
                          listening={false}
                        />
                        {/* Line text highlight (AI label bbox) after zoom/focus */}
                        {isFocusFlash && node.label_bbox && (
                          <>
                            <Rect
                              x={node.label_bbox.x1 - 6 / scale}
                              y={node.label_bbox.y1 - 6 / scale}
                              width={node.label_bbox.x2 - node.label_bbox.x1 + 12 / scale}
                              height={node.label_bbox.y2 - node.label_bbox.y1 + 12 / scale}
                              stroke="#fde047"
                              strokeWidth={4 / scale}
                              shadowColor="#fde047"
                              shadowBlur={(16 * focusT) / scale}
                              shadowOpacity={0.55 * focusT}
                              listening={false}
                            />
                            <Rect
                              x={node.label_bbox.x1 - 10 / scale}
                              y={node.label_bbox.y1 - 10 / scale}
                              width={node.label_bbox.x2 - node.label_bbox.x1 + 20 / scale}
                              height={node.label_bbox.y2 - node.label_bbox.y1 + 20 / scale}
                              stroke="#7c3aed"
                              strokeWidth={2 / scale}
                              dash={[10 / scale, 6 / scale]}
                              dashOffset={-(focusFlashElapsedMs * 0.05) / Math.max(1e-6, scale)}
                              opacity={0.9}
                              listening={false}
                            />
                          </>
                        )}
	                        {/* Line segment review overlay (distinct merged segments) */}
	                        {shouldShowSegmentReviewOverlay && (
	                          <>
	                            {segmentReviewDistinct.map((seg) => {
	                              const status = deriveDistinctStatus(seg);
	                              const base = getSegmentStroke(status);
	                              const isSegHovered = segmentReview?.hoveredDistinctId === seg.id;
	                              const isSegSelected = segmentReview?.selectedDistinctId === seg.id;
	                              const stroke = isSegHovered ? '#2563eb' : base.stroke;
	                              const segStrokeWidth = (isSegHovered || isSegSelected ? 5 : 4) / scale;
	                              return (
	                                <React.Fragment key={`node-${node.id}-seg-${seg.id}`}>
	                                  <Line
	                                    points={[seg.x1, seg.y1, seg.x2, seg.y2]}
	                                    stroke={stroke}
	                                    strokeWidth={segStrokeWidth}
	                                    lineCap="round"
	                                    lineJoin="round"
	                                    dash={base.dash}
	                                    opacity={0.95}
	                                    listening={false}
	                                  />
	                                  {(isSegHovered || isSegSelected) && (
	                                    <>
	                                      <Circle
	                                        x={seg.x1}
	                                        y={seg.y1}
	                                        radius={6 / scale}
	                                        fill={stroke}
	                                        stroke="white"
	                                        strokeWidth={2 / scale}
	                                        listening={false}
	                                      />
	                                      <Circle
	                                        x={seg.x2}
	                                        y={seg.y2}
	                                        radius={6 / scale}
	                                        fill={stroke}
	                                        stroke="white"
	                                        strokeWidth={2 / scale}
	                                        listening={false}
	                                      />
	                                    </>
	                                  )}
	                                </React.Fragment>
	                              );
	                            })}
	                          </>
	                        )}
	                        {/* Render points along the path - smaller, cleaner */}
	                        {shouldRenderNodePoints && (() => {
	                          const dots: React.ReactNode[] = [];
	                          for (let i = 0; i < pointCount; i += pointStride) {
	                            const x = node.polyline![i * 2];
	                            const y = node.polyline![i * 2 + 1];
	                            if (typeof x !== 'number' || typeof y !== 'number') continue;
	                            dots.push(
	                              <Circle
                                key={`node-${node.id}-pt-${i}`}
                                x={x}
                                y={y}
                                radius={(isActive || isNodeHovered ? 6 : 4) / scale}
                                fill={isNodeHovered ? '#3b82f6' : pointColor}
                                stroke="white"
                                strokeWidth={1.5 / scale}
                                opacity={isActive ? 1 : (isNodeHovered ? 1 : 0.6)}
                                listening={false}
                              />
                            );
                          }
                          const lastIdx = pointCount - 1;
                          if (lastIdx >= 0 && lastIdx % pointStride !== 0) {
                            const x = node.polyline![lastIdx * 2];
                            const y = node.polyline![lastIdx * 2 + 1];
                            if (typeof x === 'number' && typeof y === 'number') {
                              dots.push(
                                <Circle
                                  key={`node-${node.id}-pt-last`}
                                  x={x}
                                  y={y}
                                  radius={(isActive || isNodeHovered ? 6 : 4) / scale}
                                  fill={isNodeHovered ? '#3b82f6' : pointColor}
                                  stroke="white"
                                  strokeWidth={1.5 / scale}
                                  opacity={isActive ? 1 : (isNodeHovered ? 1 : 0.6)}
                                  listening={false}
                                />
                              );
                            }
                          }
                          return dots;
                        })()}
                        {/* Node label (includes line text); highlighted after zoom/focus */}
                        {showLabel && (
                          <Label
                            x={labelX}
                            y={labelY}
                            offsetX={12 / scale}
                            offsetY={12 / scale}
                            opacity={isActive || isNodeHovered ? 1 : 0.7}
                          >
                            <Tag
                              fill={isFocusFlash ? '#fde047' : (isNodeHovered ? '#3b82f6' : labelColor)}
                              stroke={isFocusFlash ? '#7c3aed' : undefined}
                              strokeWidth={isFocusFlash ? 2 / scale : 0}
                              cornerRadius={4 / scale}
                              pointerDirection="down"
                              pointerWidth={6 / scale}
                              pointerHeight={4 / scale}
                            />
                            <Text
                              text={node.line_no?.trim() ? `#${node.node_no} · ${node.line_no.trim()}` : `#${node.node_no}`}
                              fontSize={14 / scale}
                              fontStyle="bold"
                              fill={isFocusFlash ? '#111827' : 'white'}
                              padding={4 / scale}
                            />
                          </Label>
                        )}
                      </React.Fragment>
                    );
                  }
                  
                  // Nodes with AI segments but no polyline yet: show a small marker so they’re visible/clickable,
                  // and (when selected) draw the distinct merged segment overlay.
                  if (shouldShowSegmentMarker) {
                    const segs = node.line_segments ?? [];
                    const midpoints = segs.map((s) => ({
                      x: ((s.x1 + s.x2) / 2) * segScale.sx,
                      y: ((s.y1 + s.y2) / 2) * segScale.sy,
                    }));
                    const marker = midpoints.length
                      ? {
                          x: midpoints.reduce((sum, p) => sum + p.x, 0) / midpoints.length,
                          y: midpoints.reduce((sum, p) => sum + p.y, 0) / midpoints.length,
                        }
                      : { x: 0, y: 0 };

                    const reviewStatus = node.segments_review_status ?? 'pending';
                    const markerStyle =
                      reviewStatus === 'confirmed'
                        ? { fill: 'rgba(34, 197, 94, 0.9)', stroke: '#166534' }
                        : reviewStatus === 'in_review'
                          ? { fill: 'rgba(245, 158, 11, 0.9)', stroke: '#92400e' }
                          : { fill: 'rgba(74, 222, 128, 0.92)', stroke: '#166534' };

                    const bbox: BoundingBox = {
                      x1: marker.x - 20,
                      y1: marker.y - 20,
                      x2: marker.x + 20,
                      y2: marker.y + 20,
                    };

                    return (
                      <React.Fragment key={`node-marker-${node.id}`}>
                        {shouldShowSegmentReviewOverlay && (
                          <>
                            {segmentReviewDistinct.map((seg) => {
                              const status = deriveDistinctStatus(seg);
                              const base = getSegmentStroke(status);
                              const isSegHovered = segmentReview?.hoveredDistinctId === seg.id;
                              const isSegSelected = segmentReview?.selectedDistinctId === seg.id;
                              const stroke = isSegHovered ? '#2563eb' : base.stroke;
                              const segStrokeWidth = (isSegHovered || isSegSelected ? 5 : 4) / scale;
                              return (
                                <React.Fragment key={`node-${node.id}-seg-${seg.id}`}>
                                  <Line
                                    points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                    stroke={stroke}
                                    strokeWidth={segStrokeWidth}
                                    lineCap="round"
                                    lineJoin="round"
                                    dash={base.dash}
                                    opacity={0.95}
                                    listening={false}
                                  />
                                  {(isSegHovered || isSegSelected) && (
                                    <>
                                      <Circle
                                        x={seg.x1}
                                        y={seg.y1}
                                        radius={6 / scale}
                                        fill={stroke}
                                        stroke="white"
                                        strokeWidth={2 / scale}
                                        listening={false}
                                      />
                                      <Circle
                                        x={seg.x2}
                                        y={seg.y2}
                                        radius={6 / scale}
                                        fill={stroke}
                                        stroke="white"
                                        strokeWidth={2 / scale}
                                        listening={false}
                                      />
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </>
                        )}

                        <Group>
                          <Circle
                            name="wizard-mark node-line"
                            x={marker.x}
                            y={marker.y}
                            radius={10 / scale}
                            fill={markerStyle.fill}
                            stroke={markerStyle.stroke}
                            strokeWidth={2 / scale}
                            opacity={0.95}
                            listening={drawMode === 'select' && !isNodeMarkingMode && !isPanning}
                            onMouseEnter={() =>
                              setHoveredItem({
                                type: 'node',
                                id: node.id,
                                tag: `Node #${node.node_no}`,
                                itemType: `${node.line_no}\n${node.source_ref} → ${node.dest_ref}`,
                                lineNo: node.line_no,
                                bbox,
                              })
                            }
                            onMouseLeave={() => setHoveredItem(null)}
                            onClick={(e) => {
                              if (isNodeMarkingMode) return;
                              // Allow node selection in path/rect modes when not actively drawing
                              if (drawMode === 'comment' || drawMode === 'poly') return;
                              if (isDrawing || pathPoints.length > 0) return;
                              if (panMovedRef.current || isSpaceHeld) return;
                              if (!onSelectWizardItem) return;
                              e.cancelBubble = true;
                              onSelectWizardItem({
                                kind: 'node',
                                id: node.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                          />
                          <Text
                            x={marker.x}
                            y={marker.y}
                            text={`#${node.node_no}`}
                            offsetX={(16 / 2) / scale}
                            offsetY={(10 / 2) / scale}
                            fontSize={10 / scale}
                            fontStyle="bold"
                            fill="white"
                            listening={false}
                          />
                        </Group>
                      </React.Fragment>
                    );
                  }

                  // Fallback: render nothing for nodes without valid polyline/segments.
                  return null;
                })}

                {/* Node Instruments - only render marked ones with bbox */}
                {nodeInstruments
                  .filter(inst => instrumentRegions(inst).length > 0)
                  .filter(inst => {
                    const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                    if (showActiveOnly) {
                      // Show only active node's instruments on step 2
                      return wizardStep === 2 && effectiveContext.context_id === activeNodeId;
                    }
                    // Show all node instruments on steps 2, 3, 4
                    return [2, 3, 4].includes(wizardStep ?? 0);
                  })
                  .filter(inst => {
                    const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                    const force =
                      inst.id === selectedInstrumentId ||
                      inst.id === highlightedEntityId ||
                      effectiveContext.context_id === activeNodeId;
                    // Per region: a balloon drawn across the sheet from its symbol still
                    // draws when only the balloon is on screen.
                    return instrumentRegions(inst).some((region) => isInViewport(region, force));
                  })
                  .map((inst) => {
                  const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                  const node = effectiveContext.context_id ? nodesById.get(effectiveContext.context_id) : undefined;
                  // Node instruments are only "active" on step 2 when parent node is active
                  const isNodeStep = wizardStep === 2;
                  const isActive = isNodeStep && node?.id === activeNodeId;
                  const isSelected = inst.id === selectedInstrumentId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'nodeInstrument', id: inst.id })) ?? false;
                  const isHighlighted = inst.id === highlightedEntityId;
                  const regions = instrumentRegions(inst);
                  // Region 0 is the symbol: the branch marker and the delete badge anchor on it.
                  const symbolRegion = regions[0];
                  const isHovered = hoveredItem?.id === inst.id && hoveredItem?.type === 'node_instrument';
                  const isBranch = Boolean(inst.branch_ref || inst.branch_direction);
                  const centerX = (symbolRegion.x1 + symbolRegion.x2) / 2;
                  const centerY = (symbolRegion.y1 + symbolRegion.y2) / 2;
                  const reviewed = inst.status === 'final_and_reviewed';
                  const inactive = wizardInactivePalette('instrument', reviewed);
                  const baseStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#15803d' : '#7c3aed')
                    : (isActive || isHighlighted ? '#2563eb' : inactive.stroke);
                  const hoverStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#166534' : '#6d28d9')
                    : (isActive || isHighlighted ? '#1d4ed8' : inactive.hoverStroke);
                  const baseFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.2)' : 'rgba(124, 58, 237, 0.18)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.15)' : inactive.fill);
                  const hoverFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.3)' : 'rgba(124, 58, 237, 0.28)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.25)' : inactive.hoverFill);
                  const hitPadding = 4 / scale;
                  const shouldTrackHover = !suppressHoverInspection;

                  if (isBranch) {
                    return (
                      <React.Fragment key={`node-inst-${inst.id}`}>
                        <Circle
                          x={centerX}
                          y={centerY}
                          radius={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 10 : 7) / scale}
                          fill={isHovered ? hoverFill : baseFill}
                          stroke={isHovered ? hoverStroke : baseStroke}
                          strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                          opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                          listening={false}
                          name="wizard-mark node-instrument"
                          perfectDrawEnabled={false}
                          shadowForStrokeEnabled={false}
                        />
                        <Circle
                          x={centerX}
                          y={centerY}
                          radius={10 / scale + hitPadding}
                          fill="transparent"
                          listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                          name="wizard-mark node-instrument"
                          perfectDrawEnabled={false}
                          shadowForStrokeEnabled={false}
                          onMouseDown={(e) => {
                            if (drawMode === 'select' || e.evt.button !== 0) {
                              e.cancelBubble = true;
                            }
                          }}
                          onClick={(e) => {
                            if (!canSelectMark()) return;
                            e.cancelBubble = true;
                            onSelectWizardItem?.({
                              kind: 'nodeInstrument',
                              id: inst.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onContextMenu={(e) => {
                            e.evt.preventDefault();
                            e.cancelBubble = true;
                            if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                            onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                          }}
                          onMouseEnter={() => {
                            if (!shouldTrackHover) return;
                            cancelHoverClear();
                            setHoveredItem({
                              type: 'node_instrument',
                              id: inst.id,
                              tag: getInstrumentPrimaryLabel(inst),
                              itemType: inst.instrument_type ? getInstrumentCategoryName(inst.instrument_type) : 'Branch',
                              bbox: symbolRegion,
                            });
                          }}
                          onMouseLeave={() => {
                            if (!shouldTrackHover) return;
                            scheduleHoverClear();
                          }}
                        />
                        {/* Balloons and captions drawn alongside the branch marker. */}
                        {regions.slice(1).map((region, offset) => (
                          <Rect
                            key={`node-inst-${inst.id}-region-${offset + 1}`}
                            x={region.x1}
                            y={region.y1}
                            width={region.x2 - region.x1}
                            height={region.y2 - region.y1}
                            stroke={isHovered ? hoverStroke : baseStroke}
                            strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={isHovered ? hoverFill : baseFill}
                            opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            dash={isActive || isHovered || isSelected || isBulkSelected || isHighlighted ? undefined : [4 / scale, 2 / scale]}
                            listening={false}
                            name="wizard-mark node-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                        ))}
                        {(isSelected || isBulkSelected || isHovered) && regions.map((region, regionIndex) => (
                          <React.Fragment key={`node-inst-${inst.id}-handles-${regionIndex}`}>
                            {renderPersistedBboxHandles(
                              { kind: 'nodeInstrument', id: inst.id },
                              region,
                              isSelected || isBulkSelected ? '#7c3aed' : '#2563eb',
                              { regionIndex },
                            )}
                          </React.Fragment>
                        ))}
                        {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                          { kind: 'nodeInstrument', id: inst.id },
                          symbolRegion,
                        )}
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={`node-inst-${inst.id}`}>
                      {/* One rectangle per region, all sharing the marking's hover and
                          selection state, so pointing at the balloon lights the symbol too. */}
                      {regions.map((region, regionIndex) => (
                        <React.Fragment key={`node-inst-${inst.id}-region-${regionIndex}`}>
                          {regions.length > 1 && (
                            <RegionIndexChip
                              region={region}
                              index={regionIndex}
                              color={isHovered ? hoverStroke : baseStroke}
                              scale={scale}
                            />
                          )}
                          <Rect
                            x={region.x1}
                            y={region.y1}
                            width={region.x2 - region.x1}
                            height={region.y2 - region.y1}
                            stroke={isHovered ? hoverStroke : baseStroke}
                            strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={isHovered ? hoverFill : baseFill}
                            opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            dash={isActive || isHovered || isSelected || isBulkSelected || isHighlighted ? undefined : [4 / scale, 2 / scale]}
                            listening={false}
                            name="wizard-mark node-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                          <Rect
                            x={region.x1 - hitPadding}
                            y={region.y1 - hitPadding}
                            width={region.x2 - region.x1 + hitPadding * 2}
                            height={region.y2 - region.y1 + hitPadding * 2}
                            fill="transparent"
                            listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                            name="wizard-mark node-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                            onMouseDown={(e) => {
                              if (drawMode === 'select' || e.evt.button !== 0) {
                                e.cancelBubble = true;
                              }
                            }}
                            onClick={(e) => {
                              if (!canSelectMark()) return;
                              e.cancelBubble = true;
                              onSelectWizardItem?.({
                                kind: 'nodeInstrument',
                                id: inst.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                            onContextMenu={(e) => {
                              e.evt.preventDefault();
                              e.cancelBubble = true;
                              if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                              onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                            }}
                            onMouseEnter={() => {
                              if (!shouldTrackHover) return;
                              cancelHoverClear();
                              setHoveredItem({
                                type: 'node_instrument',
                                id: inst.id,
                                tag: getInstrumentPrimaryLabel(inst),
                                itemType: inst.instrument_type ? getInstrumentCategoryName(inst.instrument_type) : 'Instrument',
                                // The region under the cursor, so the tooltip lands beside
                                // what the SME is actually pointing at.
                                bbox: region,
                              });
                            }}
                            onMouseLeave={() => {
                              if (!shouldTrackHover) return;
                              scheduleHoverClear();
                            }}
                          />
                          {(isSelected || isBulkSelected || isHovered) && renderPersistedBboxHandles(
                            { kind: 'nodeInstrument', id: inst.id },
                            region,
                            isSelected || isBulkSelected ? '#7c3aed' : '#2563eb',
                            { regionIndex },
                          )}
                        </React.Fragment>
                      ))}
                      {/* The badge deletes the marking, not a region, so it sits on the symbol alone. */}
                      {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                        { kind: 'nodeInstrument', id: inst.id },
                        symbolRegion,
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Control loops - render imported/marked loop bounding boxes */}
                {pnidState.control_loops
                  .filter((loop) => loop.bbox)
                  .filter((loop) => {
                    const force = loop.id === highlightedEntityId;
                    return isInViewport(loop.bbox, force);
                  })
                  .map((loop) => {
                    const bbox = loop.bbox!;
                    const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'controlLoop', id: loop.id })) ?? false;
                    const isHovered = hoveredItem?.id === loop.id && hoveredItem?.type === 'control_loop';
                    const strokeColor = isBulkSelected || isHovered ? '#7c3aed' : '#9333ea';
                    const fillColor = isBulkSelected || isHovered ? 'rgba(124, 58, 237, 0.16)' : 'rgba(147, 51, 234, 0.08)';
                    return (
                      <React.Fragment key={`control-loop-${loop.id}`}>
                        <Rect
                          x={bbox.x1}
                          y={bbox.y1}
                          width={bbox.x2 - bbox.x1}
                          height={bbox.y2 - bbox.y1}
                          stroke={strokeColor}
                          strokeWidth={(isBulkSelected || isHovered ? 3 : 2) / scale}
                          fill={fillColor}
                          dash={isBulkSelected ? undefined : [5 / scale, 3 / scale]}
                          opacity={isBulkSelected || isHovered ? 1 : 0.85}
                          listening={Boolean(onSelectWizardItem)}
                          name="wizard-mark control-loop"
                          onClick={(e) => {
                            if (!canSelectMark()) return;
                            e.cancelBubble = true;
                            onSelectWizardItem?.({
                              kind: 'controlLoop',
                              id: loop.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onMouseEnter={() => {
                            cancelHoverClear();
                            setHoveredItem({
                              type: 'control_loop',
                              id: loop.id,
                              tag: loop.loop_tag,
                              itemType: 'Control loop',
                              bbox,
                            });
                          }}
                          onMouseLeave={scheduleHoverClear}
                        />
                        {(isBulkSelected || isHovered) && renderPersistedBboxHandles(
                          { kind: 'controlLoop', id: loop.id },
                          bbox,
                          '#7c3aed',
                        )}
                      </React.Fragment>
                    );
                  })}

                {/* Node point candidates (inline selection) */}
                {nodePointCandidates.map((candidate) => {
                  const isSelected = candidate.id === selectedCandidateId;
                  const statusColor = candidate.status === 'error'
                    ? '#dc2626'
                    : candidate.status === 'running'
                      ? '#2563eb'
                      : '#f59e0b';
                  const fillColor = candidate.status === 'ready'
                    ? 'rgba(245, 158, 11, 0.25)'
                    : candidate.status === 'running'
                      ? 'rgba(37, 99, 235, 0.2)'
                      : 'rgba(220, 38, 38, 0.2)';
                  const radius = (isSelected ? 7 : 5) / scale;
                  const menuWidth = 190 / scale;
                  const rowHeight = 22 / scale;
                  const menuPadding = 6 / scale;
                  const options = [...(candidate.options || [])].sort((a, b) => b.score - a.score).slice(0, 3);
                  const rows = [
                    { key: 'ignore', label: 'Ignore (default)', score: null, value: 'ignore' as const },
                    ...options.map((option, idx) => ({
                      key: `opt-${idx}`,
                      label:
                        option.label === 'Branch'
                          ? (candidate.branchLabel ? `Branch to ${candidate.branchLabel}` : 'Branch')
                          : getInstrumentCategoryName(option.label as InstrumentCategory),
                      score: option.score,
                      value: option.label,
                    })),
                  ];
                  const menuHeight = rows.length * rowHeight + menuPadding * 2;
                  const menuX = candidate.point.x + 12 / scale;
                  const menuY = candidate.point.y - menuHeight / 2;

                  return (
                    <React.Fragment key={candidate.id}>
                      {isSelected && candidate.bbox && (
                        <Rect
                          x={candidate.bbox.x1}
                          y={candidate.bbox.y1}
                          width={candidate.bbox.x2 - candidate.bbox.x1}
                          height={candidate.bbox.y2 - candidate.bbox.y1}
                          stroke={statusColor}
                          strokeWidth={2 / scale}
                          dash={[6 / scale, 3 / scale]}
                          fill="rgba(255,255,255,0.0)"
                          listening={false}
                        />
                      )}
                      <Circle
                        name="node-point-candidate"
                        x={candidate.point.x}
                        y={candidate.point.y}
                        radius={radius}
                        fill={fillColor}
                        stroke={statusColor}
                        strokeWidth={2 / scale}
                        listening={Boolean(onResolveNodePointCandidate)}
                        onMouseDown={(e) => {
                          if (!onResolveNodePointCandidate) return;
                          e.cancelBubble = true;
                        }}
                        onClick={(e) => {
                          if (!onResolveNodePointCandidate) return;
                          e.cancelBubble = true;
                          setSelectedCandidateId((prev) => (prev === candidate.id ? null : candidate.id));
                        }}
                      />
                      {candidate.status === 'running' && (
                        <Text
                          x={candidate.point.x + 10 / scale}
                          y={candidate.point.y - 10 / scale}
                          text="Identifying..."
                          fontSize={10 / scale}
                          fill="#2563eb"
                          listening={false}
                        />
                      )}
                      {isSelected && onResolveNodePointCandidate && candidate.status !== 'running' && (
                        <Group x={menuX} y={menuY}>
                          <Rect
                            width={menuWidth}
                            height={menuHeight}
                            fill="white"
                            stroke="#e5e7eb"
                            strokeWidth={1 / scale}
                            cornerRadius={6 / scale}
                            shadowColor="black"
                            shadowBlur={6 / scale}
                            shadowOffsetX={1 / scale}
                            shadowOffsetY={1 / scale}
                            shadowOpacity={0.12}
                            onMouseDown={(e) => {
                              e.cancelBubble = true;
                            }}
                          />
                          {rows.map((row, idx) => {
                            const rowY = menuPadding + idx * rowHeight;
                            const rowWidth = menuWidth - menuPadding * 2;
                            const scoreText = row.score == null ? '' : `${Math.round(row.score * 100)}%`;
                            const isIgnore = row.value === 'ignore';
                            const rowColor = isIgnore ? 'var(--raven-text-secondary)' : '#111827';
                            return (
                              <Group key={row.key} x={menuPadding} y={rowY}>
                                <Rect
                                  width={rowWidth}
                                  height={rowHeight - 2 / scale}
                                  fill={isIgnore ? '#f3f4f6' : '#f9fafb'}
                                  cornerRadius={4 / scale}
                                  listening
                                  onMouseDown={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                  onClick={(e) => {
                                    e.cancelBubble = true;
                                    onResolveNodePointCandidate(
                                      candidate.id,
                                      row.value === 'ignore' ? 'ignore' : (row.value as NodePointCandidateLabel)
                                    );
                                    setSelectedCandidateId(null);
                                  }}
                                />
                                <Text
                                  text={row.label}
                                  x={6 / scale}
                                  y={4 / scale}
                                  fontSize={11 / scale}
                                  fill={rowColor}
                                  width={rowWidth - 12 / scale}
                                  align="left"
                                  listening={false}
                                />
                                {scoreText && (
                                  <Text
                                    text={scoreText}
                                    x={6 / scale}
                                    y={4 / scale}
                                    fontSize={10 / scale}
                                    fill="var(--raven-text-secondary)"
                                    width={rowWidth - 12 / scale}
                                    align="right"
                                    listening={false}
                                  />
                                )}
                              </Group>
                            );
                          })}
                        </Group>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Focus highlight (search / navigation) */}
                {focusBbox && (() => {
                  const t = Math.max(0, Math.min(1, 1 - focusFlashElapsedMs / FOCUS_FLASH_DURATION_MS)); // 1 -> 0
                  const stroke = '#7c3aed'; // vlink-ish violet
                  const dashOffset = -(focusFlashElapsedMs * 0.06) / Math.max(1e-6, scale);

                  // Keep highlight OUTSIDE the target so it doesn't cover text.
                  // `pad` is in image coords so it stays constant in screen pixels.
                  const pad = 10 / Math.max(1e-6, scale);
                  const wImg = Math.max(1, focusBbox.x2 - focusBbox.x1);
                  const hImg = Math.max(1, focusBbox.y2 - focusBbox.y1);
                  const x = focusBbox.x1 - pad;
                  const y = focusBbox.y1 - pad;
                  const w = wImg + pad * 2;
                  const h = hImg + pad * 2;

                  const cx = focusBbox.x1 + wImg / 2;
                  const cy = focusBbox.y1 + hImg / 2;
                  const pingR = (14 + 26 * (1 - t)) / scale;
                  const bracket = 16 / Math.max(1e-6, scale);

                  return (
                    <>
                      <Rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        stroke={stroke}
                        strokeWidth={(t > 0 ? 3.25 : 2.25) / scale}
                        dash={[10 / scale, 6 / scale]}
                        dashOffset={dashOffset}
                        shadowColor={stroke}
                        shadowBlur={(t > 0 ? 18 * t : 0) / scale}
                        shadowOpacity={t > 0 ? 0.55 * t : 0}
                        listening={false}
                      />
                      {/* Corner brackets (stroke-only, does not obscure text) */}
                      <Line
                        points={[x, y + bracket, x, y, x + bracket, y]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      <Line
                        points={[x + w - bracket, y, x + w, y, x + w, y + bracket]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      <Line
                        points={[x, y + h - bracket, x, y + h, x + bracket, y + h]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      <Line
                        points={[x + w - bracket, y + h, x + w, y + h, x + w, y + h - bracket]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      {t > 0 && (
                        <Circle
                          x={cx}
                          y={cy}
                          radius={pingR}
                          stroke={stroke}
                          strokeWidth={2.2 / scale}
                          opacity={0.55 * t}
                          listening={false}
                        />
                      )}
                    </>
                  );
                })()}

                {/* Hover tooltip for marked items */}
                {hoveredItem && !suppressHoverInspection && (
                  <Group
                    x={hoveredItem.bbox.x2 + 8 / scale}
                    y={hoveredItem.bbox.y1}
                  >
                    <Label>
                      <Tag
                        fill="#1f2937"
                        cornerRadius={4 / scale}
                        shadowColor="black"
                        shadowBlur={4}
                        shadowOffsetX={1}
                        shadowOffsetY={1}
                        shadowOpacity={0.3}
                      />
                      <Text
                        text={`${hoveredItem.tag}\n${hoveredItem.itemType}`}
                        fontSize={12 / scale}
                        fontFamily="system-ui, -apple-system, sans-serif"
                        fill="white"
                        padding={6 / scale}
                        lineHeight={1.4}
                      />
                    </Label>
                  </Group>
                )}

                {/* Hover line details near cursor (node + line no + refs) */}
                {hoveredItem?.type === 'node' && cursorPosition && !suppressHoverInspection && (
                  <Group
                    x={cursorPosition.x + 14 / Math.max(1e-6, scale)}
                    y={cursorPosition.y + 14 / Math.max(1e-6, scale)}
                    listening={false}
                  >
                    <Label opacity={0.98}>
                      <Tag
                        fill="rgba(17, 24, 39, 0.92)"
                        cornerRadius={6 / scale}
                        shadowColor="black"
                        shadowBlur={6}
                        shadowOffsetX={1}
                        shadowOffsetY={1}
                        shadowOpacity={0.25}
                      />
                      <Text
                        text={(() => {
                          const lineNo = (hoveredItem.lineNo || '').trim();
                          const header = lineNo ? `${hoveredItem.tag} · ${lineNo}` : hoveredItem.tag;
                          const parts = (hoveredItem.itemType || '').split('\n').map((s) => s.trim()).filter(Boolean);
                          // itemType for nodes is "line_no\nsource → dest" — show only the ref line below header.
                          const refLine =
                            parts.length >= 2 ? parts.slice(1).join(' · ') :
                            (parts.length === 1 && parts[0] !== lineNo ? parts[0] : '');
                          return refLine ? `${header}\n${refLine}` : header;
                        })()}
                        fontSize={12 / scale}
                        fontStyle="bold"
                        fontFamily="system-ui, -apple-system, sans-serif"
                        fill="#fde047"
                        padding={6 / scale}
                        lineHeight={1.25}
                      />
                    </Label>
                  </Group>
                )}
              </Group>
            )}

            {/* Template Annotations - render on steps 2, 3, 4 with different behavior */}
            {(wizardStep === 2 || wizardStep === 3 || wizardStep === 4) && (
              <>
                {/* Saved annotations */}
                {templateAnnotations
                  .filter(ann => {
                    // Step 2: show all visible annotations
                    if (wizardStep === 2) {
                      return templateVisibility?.get(ann.template_id) !== false;
                    }
                    // Steps 3/4: only show annotations that are nearby or attached to the active item
                    const isNearby = isAnnotationNearby(ann);
                    const isAttached = isAnnotationAttachedToActive(ann);
                    const isDetached = isAnnotationDetachedFromActive(ann);
                    return isNearby || isAttached || isDetached;
                  })
                  .filter(ann => isInViewport(
                    rectBounds(ann.x, ann.y, ann.width, ann.height),
                    ann.id === activeAnnotationId ||
                      Boolean(selectedAnnotationIds?.has(ann.id)) ||
                      isAnnotationAttachedToActive(ann)
                  ))
                  .map((ann) => {
                    const isSelected = selectedAnnotationIds?.has(ann.id);
                    const isActive = ann.id === activeAnnotationId;
                    const isHovered = hoveredAnnotationId === ann.id;
                    const showAnnotationLabel = scale >= LABEL_DETAIL_SCALE || isActive || Boolean(isSelected) || isHovered;
                    const showAnnotationStats = wizardStep === 2 && !reducePassiveOverlays && scale >= STATS_DETAIL_SCALE;
                    const showDeleteBtn = isHovered && onDeleteAnnotation && wizardStep === 2 && !reducePassiveOverlays;
                    
                    // Compute display colors based on step and attachment state
                    let displayColor: string;
                    let displayFill: string;
                    let displayDash: number[] | undefined;

                    // Context-free (unattached anywhere) template/AI instruments are click-to-attach
                    // candidates in steps 3/4 when an equipment/node is active.
                    const isContextFree = !ann.attached_context_type;
                    const hasActiveAttachTarget =
                      (wizardStep === 3 && Boolean(activeEquipmentId)) ||
                      (wizardStep === 4 && Boolean(activeNodeId));
                    const isDirectAttachable =
                      (wizardStep === 3 || wizardStep === 4) &&
                      isContextFree &&
                      !isAnnotationAttachedToActive(ann) &&
                      hasActiveAttachTarget &&
                      Boolean(onDirectAttachAnnotation) &&
                      !readOnly &&
                      drawMode === 'select';

                    if (wizardStep === 3 || wizardStep === 4) {
                      // Steps 3/4: Use attachment-based colors
                      const isAttached = isAnnotationAttachedToActive(ann);
                      if (isAttached) {
                        // Attached: Follow active parent color
                        displayColor = '#2563eb';
                        displayFill = 'rgba(37, 99, 235, 0.15)';
                      } else if (isContextFree) {
                        // Unattached AI/template instrument: violet dashed = attachable
                        displayColor = isHovered && isDirectAttachable ? '#7c3aed' : '#a78bfa';
                        displayFill = isHovered && isDirectAttachable
                          ? 'rgba(167, 139, 250, 0.25)'
                          : 'rgba(167, 139, 250, 0.15)';
                        displayDash = [6 / scale, 3 / scale];
                      } else {
                        // Nearby but attached to another equipment/node: Very light yellow
                        displayColor = '#eab308';
                        displayFill = 'rgba(250, 204, 21, 0.15)';
                      }
                    } else {
                      // Step 2: Active (blue), Reviewed (green/final), Not reviewed yet (yellow)
                      const isReviewed = ann.status === 'final_and_reviewed';
                      displayColor = isActive ? '#3b82f6' : (isReviewed ? '#22c55e' : '#eab308');
                      displayFill = isActive ? 'rgba(59, 130, 246, 0.2)' : (isReviewed ? 'rgba(34, 197, 94, 0.1)' : 'rgba(234, 179, 8, 0.1)');
                    }
                    
                    const isReviewed = ann.status === 'final_and_reviewed';
                    
                    return (
                      <Group
                        key={ann.id}
                        onMouseEnter={(e) => {
                          setHoveredAnnotationId(ann.id);
                          if (isDirectAttachable) {
                            const stage = e.target.getStage();
                            if (stage) stage.container().style.cursor = 'pointer';
                          }
                        }}
                        onMouseLeave={(e) => {
                          setHoveredAnnotationId(null);
                          if (isDirectAttachable) {
                            const stage = e.target.getStage();
                            if (stage) stage.container().style.cursor = '';
                          }
                        }}
                      >
                        <Rect
                          name="template-annotation"
                          x={ann.x}
                          y={ann.y}
                          width={ann.width}
                          height={ann.height}
                          stroke={isSelected ? '#3b82f6' : displayColor}
                          strokeWidth={(isSelected || isActive ? 3 : 2) / scale}
                          fill={isSelected ? 'rgba(59, 130, 246, 0.15)' : displayFill}
                          dash={displayDash}
                          onClick={(e) => {
                            // Step 2: Cmd/Ctrl+click toggles selection
                            if (wizardStep === 2 && (e.evt.metaKey || e.evt.ctrlKey) && onSelectAnnotation) {
                              e.cancelBubble = true;
                              onSelectAnnotation(ann.id, true);
                              return;
                            }
                            // Steps 3/4: plain click adopts an unattached AI/template instrument
                            // onto the active equipment/node (persists immediately).
                            if (isDirectAttachable && !(e.evt.metaKey || e.evt.ctrlKey) && onDirectAttachAnnotation) {
                              e.cancelBubble = true;
                              onDirectAttachAnnotation(ann.id);
                              return;
                            }
                            // Handle PAN mode click for verification workflow (Step 2 only)
                            if (drawMode === 'select' && wizardStep === 2 && onAnnotationClick) {
                              e.cancelBubble = true;
                              // Determine action based on annotation status
                              const action = ann.status === 'unverified' ? 'sme-capture' : 'edit';
                              // Calculate screen position for popup placement (below the annotation)
                              const stage = stageRef.current;
                              const container = stage?.container();
                              let screenPos = { x: 400, y: 200 }; // fallback
                              if (container) {
                                const rect = container.getBoundingClientRect();
                                // Convert annotation bbox to screen coordinates - position below annotation
                                const xScreen = ann.x * scale + position.x + rect.left;
                                const yScreen = (ann.y + ann.height) * scale + position.y + rect.top + 10;
                                screenPos = { x: xScreen, y: yScreen };
                              }
                              onAnnotationClick(ann.id, action, screenPos);
                            }
                          }}
                          onContextMenu={(e) => {
                            e.evt.preventDefault();
                            e.cancelBubble = true;
                            if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                            onRightClickInstrument?.(ann.id, { x: e.evt.clientX, y: e.evt.clientY });
                          }}
                        />
                        {/* Label with template name and status badge */}
                        {showAnnotationLabel && (
                          <Label x={ann.x} y={ann.y - 18 / scale}>
                            <Tag
                              fill={displayColor}
                              cornerRadius={3 / scale}
                          />
                          <Text
                            text={`${ann.template_name || ann.instrument_text || 'AI instrument'}${isActive ? ' ●' : (isReviewed ? ' ✓' : (wizardStep !== 2 && isAnnotationAttachedToActive(ann) ? ' 🔗' : (isDirectAttachable && isHovered ? ' · click to attach' : '')))}`}
                            fontSize={10 / scale}
                            fontFamily="system-ui, -apple-system, sans-serif"
                            fill="white"
                            padding={3 / scale}
                          />
                          </Label>
                        )}
                        {/* Stats line - only show in step 2 */}
                        {showAnnotationStats && (
                          <Text
                            x={ann.x}
                            y={ann.y + ann.height + 4 / scale}
                            text={`${(ann.confidence * 100).toFixed(0)}% | ${ann.chamfer_distance.toFixed(2)}`}
                            fontSize={9 / scale}
                            fontFamily="system-ui, -apple-system, sans-serif"
                            fill={displayColor}
                          />
                        )}
                        {/* Delete button on hover - inside top right corner (Step 2 only) */}
                        {showDeleteBtn && (
                          <Group
                            x={ann.x + ann.width - 14 / scale}
                            y={ann.y + 14 / scale}
                            onClick={(e) => {
                              e.cancelBubble = true;
                              onDeleteAnnotation(ann.id, false);
                            }}
                          >
                            <Circle
                              radius={10 / scale}
                              fill="#dc2626"
                              stroke="white"
                              strokeWidth={1 / scale}
                            />
                            <Text
                              text="×"
                              x={-5 / scale}
                              y={-7 / scale}
                              fontSize={14 / scale}
                              fontFamily="system-ui, -apple-system, sans-serif"
                              fill="white"
                              fontStyle="bold"
                            />
                          </Group>
                        )}
                        {/* Corner resize anchors (Step 2 only) */}
                        {wizardStep === 2 && (isSelected || isActive || isHovered) && renderAnnotationBboxHandles(
                          ann.id,
                          { x1: ann.x, y1: ann.y, x2: ann.x + ann.width, y2: ann.y + ann.height },
                          isSelected ? '#3b82f6' : displayColor,
                        )}
                      </Group>
                    );
                  })}

                {/* Unsaved annotations - ORANGE (Step 2 only) */}
                {wizardStep === 2 && Array.from(unsavedMatches?.entries() || [])
                  .filter(([templateId]) => templateVisibility?.get(templateId) !== false)
                  .flatMap(([templateId, matches]) => 
                    matches
                      .map((match, idx) => ({ match, idx }))
                      .filter(({ match, idx }) => {
                        const id = `unsaved-${templateId}-${idx}`;
                        return isInViewport(
                          rectBounds(match.x, match.y, match.width, match.height),
                          Boolean(selectedAnnotationIds?.has(id))
                        );
                      })
                      .map(({ match, idx }) => {
                      const id = `unsaved-${templateId}-${idx}`;
                      const isSelected = selectedAnnotationIds?.has(id);
                      const isHovered = hoveredAnnotationId === id;
                      const showMatchStats = !reducePassiveOverlays && scale >= STATS_DETAIL_SCALE;
                      const showDeleteBtn = isHovered && onDeleteAnnotation && !reducePassiveOverlays;
                      return (
                        <Group 
                          key={id}
                          onMouseEnter={() => setHoveredAnnotationId(id)}
                          onMouseLeave={() => setHoveredAnnotationId(null)}
                        >
                          <Rect
                            name="template-annotation"
                            x={match.x}
                            y={match.y}
                            width={match.width}
                            height={match.height}
                            stroke={isSelected ? '#3b82f6' : '#f97316'}
                            strokeWidth={(isSelected ? 3 : 2) / scale}
                            fill={isSelected ? 'rgba(59, 130, 246, 0.15)' : 'rgba(249, 115, 22, 0.1)'}
                            onClick={(e) => {
                              // Cmd/Ctrl+click toggles selection
                              if ((e.evt.metaKey || e.evt.ctrlKey) && onSelectAnnotation) {
                                e.cancelBubble = true;
                                onSelectAnnotation(id, true);
                              }
                            }}
                          />
                          {/* Stats line */}
                          {showMatchStats && (
                            <Text
                              x={match.x}
                              y={match.y + match.height + 4 / scale}
                            text={`${(match.confidence * 100).toFixed(0)}% | ${match.chamfer_distance.toFixed(2)}`}
                            fontSize={9 / scale}
                            fontFamily="system-ui, -apple-system, sans-serif"
                              fill="#f97316"
                            />
                          )}
                          {/* Delete button on hover - inside top right corner */}
                          {showDeleteBtn && (
                            <Group
                              x={match.x + match.width - 14 / scale}
                              y={match.y + 14 / scale}
                              onClick={(e) => {
                                e.cancelBubble = true;
                                onDeleteAnnotation(id, true, templateId, idx);
                              }}
                            >
                              <Circle
                                radius={10 / scale}
                                fill="#dc2626"
                                stroke="white"
                                strokeWidth={1 / scale}
                              />
                              <Text
                                text="×"
                                x={-5 / scale}
                                y={-7 / scale}
                                fontSize={14 / scale}
                                fontFamily="system-ui, -apple-system, sans-serif"
                                fill="white"
                                fontStyle="bold"
                              />
                            </Group>
                          )}
                        </Group>
                      );
                    })
                  )}
              </>
            )}
            
            {/* Attachment selection rectangle (Cmd/Ctrl + drag in steps 3/4) */}
            {isAttachingAnnotations && attachRectStart && attachRectEnd && (
              <Rect
                x={Math.min(attachRectStart.x, attachRectEnd.x)}
                y={Math.min(attachRectStart.y, attachRectEnd.y)}
                width={Math.abs(attachRectEnd.x - attachRectStart.x)}
                height={Math.abs(attachRectEnd.y - attachRectStart.y)}
                stroke={attachAction === 'attach' ? '#06b6d4' : '#ef4444'}
                strokeWidth={2 / scale}
                fill={attachAction === 'attach' ? 'rgba(6, 182, 212, 0.2)' : 'rgba(239, 68, 68, 0.2)'}
                dash={[8 / scale, 4 / scale]}
              />
            )}

            {/* Every region of the marking being identified, waiting on one answer */}
            {identifyingBboxes.map((identifying, index) => (
              <Group key={`identifying-${index}`}>
                <Rect
                  x={identifying.x1}
                  y={identifying.y1}
                  width={identifying.x2 - identifying.x1}
                  height={identifying.y2 - identifying.y1}
                  stroke={wizardStep === 2 ? '#7c3aed' : '#2563eb'}
                  strokeWidth={3 / scale}
                  dash={[8 / scale, 4 / scale]}
                  fill={wizardStep === 2 ? 'rgba(124, 58, 237, 0.15)' : 'rgba(37, 99, 235, 0.15)'}
                  listening={false}
                />
                {/* Loading label */}
                <Label
                  x={(identifying.x1 + identifying.x2) / 2}
                  y={(identifying.y1 + identifying.y2) / 2}
                  offsetX={50}
                  offsetY={12}
                >
                  <Tag
                    fill={wizardStep === 2 ? '#7c3aed' : '#2563eb'}
                    cornerRadius={4}
                    pointerDirection="none"
                  />
                  <Text
                    text="🔍 Identifying..."
                    fontSize={14 / scale}
                    fontStyle="bold"
                    fill="white"
                    padding={6 / scale}
                  />
                </Label>
              </Group>
            ))}

            {/* Comment bubbles */}
            {comments
              .filter(c => showResolvedComments || !c.resolved)
              .filter(c => {
                const force = c.id === selectedCommentId;
                return isInViewport(rectBounds(c.x - 48, c.y - 48, 96, 96), force);
              })
              .map(comment => (
                <CommentBubble
                  key={comment.id}
                  comment={comment}
                  scale={scale}
                  isSelected={selectedCommentId === comment.id}
                  onClick={() => {
                    if (onSelectComment) {
                      // Get screen coordinates from the click position
                      const stage = stageRef.current;
                      const container = stage?.container();
                      if (container) {
                        const rect = container.getBoundingClientRect();
                        // Use the comment's canvas position and current stage transform to screen coords
                        const currentScale = scaleRef.current;
                        const currentPosition = positionRef.current;
                        const screenX = rect.left + (comment.x * currentScale + currentPosition.x);
                        const screenY = rect.top + (comment.y * currentScale + currentPosition.y);
                        onSelectComment(comment.id, screenX, screenY);
                      }
                    }
                  }}
                />
              ))}

            {wizardMode &&
              selectedAnnotationBounds &&
              selectedWizardItemKeys?.size &&
              shouldRenderSelectedShiftBounds &&
              drawMode === 'select' &&
              onShiftSelectedWizardAnnotations && (
                <Rect
                  x={selectedAnnotationBounds.x1 + selectedAnnotationShiftPreview.dx}
                  y={selectedAnnotationBounds.y1 + selectedAnnotationShiftPreview.dy}
                  width={Math.max(1, selectedAnnotationBounds.x2 - selectedAnnotationBounds.x1)}
                  height={Math.max(1, selectedAnnotationBounds.y2 - selectedAnnotationBounds.y1)}
                  stroke={isSelectedAnnotationShiftSaving ? '#f59e0b' : '#1d4ed8'}
                  strokeWidth={2 / scale}
                  dash={[10 / scale, 6 / scale]}
                  fill="rgba(37, 99, 235, 0.035)"
                  draggable={!isSpaceHeld && !isSelectedAnnotationShiftSaving}
                  name="selected-annotation-shift-bounds"
                  onMouseDown={(e) => {
                    if (isSpaceHeld || isSelectedAnnotationShiftSaving) return;
                    e.cancelBubble = true;
                  }}
                  onDragStart={(e) => {
                    e.cancelBubble = true;
                    setHoveredItem(null);
                  }}
                  onDragMove={(e) => {
                    e.cancelBubble = true;
                    const node = e.target;
                    setSelectedAnnotationShiftPreview({
                      dx: node.x() - selectedAnnotationBounds.x1,
                      dy: node.y() - selectedAnnotationBounds.y1,
                    });
                  }}
                  onDragEnd={async (e) => {
                    e.cancelBubble = true;
                    const node = e.target;
                    const dx = node.x() - selectedAnnotationBounds.x1;
                    const dy = node.y() - selectedAnnotationBounds.y1;
                    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
                      setSelectedAnnotationShiftPreview({ dx: 0, dy: 0 });
                      return;
                    }
                    setIsSelectedAnnotationShiftSaving(true);
                    try {
                      await onShiftSelectedWizardAnnotations(
                        { dx, dy },
                        { lineSegmentScale: segScale }
                      );
                      setSelectedAnnotationShiftPreview({ dx: 0, dy: 0 });
                    } catch (error) {
                      console.error('Failed to shift selected annotations:', error);
                      setSelectedAnnotationShiftPreview({ dx: 0, dy: 0 });
                    } finally {
                      setIsSelectedAnnotationShiftSaving(false);
                    }
                  }}
                />
              )}

            {wizardMode && annotationBounds && drawMode === 'shift-annotations' && onShiftWizardAnnotations && (
              <Rect
                x={annotationBounds.x1 + annotationShiftPreview.dx}
                y={annotationBounds.y1 + annotationShiftPreview.dy}
                width={Math.max(1, annotationBounds.x2 - annotationBounds.x1)}
                height={Math.max(1, annotationBounds.y2 - annotationBounds.y1)}
                stroke={isAnnotationShiftSaving ? '#f59e0b' : '#111827'}
                strokeWidth={2 / scale}
                dash={[12 / scale, 8 / scale]}
                fill="rgba(17, 24, 39, 0.04)"
                draggable={!isSpaceHeld && !isAnnotationShiftSaving}
                name="annotation-shift-bounds"
                onMouseDown={(e) => {
                  if (isSpaceHeld || isAnnotationShiftSaving) return;
                  e.cancelBubble = true;
                }}
                onDragStart={(e) => {
                  e.cancelBubble = true;
                  setHoveredItem(null);
                }}
                onDragMove={(e) => {
                  e.cancelBubble = true;
                  const node = e.target;
                  setAnnotationShiftPreview({
                    dx: node.x() - annotationBounds.x1,
                    dy: node.y() - annotationBounds.y1,
                  });
                }}
                onDragEnd={async (e) => {
                  e.cancelBubble = true;
                  const node = e.target;
                  const dx = node.x() - annotationBounds.x1;
                  const dy = node.y() - annotationBounds.y1;
                  if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
                    setAnnotationShiftPreview({ dx: 0, dy: 0 });
                    return;
                  }
                  setIsAnnotationShiftSaving(true);
                  try {
                    await onShiftWizardAnnotations(
                      { dx, dy },
                      { lineSegmentScale: segScale }
                    );
                    setAnnotationShiftPreview({ dx: 0, dy: 0 });
                  } catch (error) {
                    console.error('Failed to shift annotations:', error);
                    setAnnotationShiftPreview({ dx: 0, dy: 0 });
                  } finally {
                    setIsAnnotationShiftSaving(false);
                  }
                }}
              />
            )}

            {/*
              The collected draft. Region 1 is the anchor — the symbol every downstream
              reader sees and the box the distance rules measure from — so it carries the
              primary blue and a heavier stroke while the rest sit back in the secondary
              tone. Culling is per region: a balloon drawn across the sheet from its
              symbol still draws when only it is on screen.
            */}
            {draftRegions
              .map((region, index) => ({ region, index }))
              .filter(({ region }) => isInViewport(region))
              .map(({ region, index }) => {
                const isAnchor = index === 0;
                const stroke = isAnchor ? '#2563eb' : '#60a5fa';
                const fill = isAnchor ? 'rgba(37, 99, 235, 0.15)' : 'rgba(59, 130, 246, 0.08)';
                return (
                  <React.Fragment key={`draft-region-${index}`}>
                    <Rect
                      x={region.x1}
                      y={region.y1}
                      width={region.x2 - region.x1}
                      height={region.y2 - region.y1}
                      stroke={stroke}
                      strokeWidth={(isAnchor ? 4 : 2) / scale}
                      dash={[8 / scale, 4 / scale]}
                      fill={fill}
                      listening={false}
                      perfectDrawEnabled={false}
                      shadowForStrokeEnabled={false}
                    />
                    <RegionIndexChip
                      region={region}
                      index={index}
                      color={stroke}
                      scale={scale}
                    />
                  </React.Fragment>
                );
              })}

          </Layer>
        </Stage>}

        {/*
          The collecting pill is HTML rather than a Konva shape so the keys can be
          real <kbd> elements: they are the content, and a canvas-drawn imitation
          reads as decoration. It follows the region just drawn, so the SME's next
          click is where their eye already is, and it is clamped into the canvas so
          it stays reachable when that region sits against an edge.
        */}
        {collectingRegions && draftRegions.length > 0 && (() => {
          const lastRegion = draftRegions[draftRegions.length - 1];
          const clamp = (value: number, low: number, high: number) =>
            Math.min(Math.max(value, low), high);
          const left = clamp(
            lastRegion.x1 * scale + position.x,
            DONE_PILL_VIEWPORT_MARGIN_PX,
            Math.max(DONE_PILL_VIEWPORT_MARGIN_PX, width - DONE_PILL_VIEWPORT_MARGIN_PX - 320),
          );
          const top = clamp(
            lastRegion.y2 * scale + position.y + DONE_PILL_GAP_PX,
            DONE_PILL_VIEWPORT_MARGIN_PX,
            Math.max(DONE_PILL_VIEWPORT_MARGIN_PX, height - DONE_PILL_VIEWPORT_MARGIN_PX - DONE_PILL_HEIGHT_PX),
          );
          return (
            <button
              type="button"
              aria-label="Finish this marking"
              onClick={(e) => {
                e.stopPropagation();
                onFinishRegions?.();
              }}
              // The canvas starts a new rectangle on mouse-down, and the press that
              // lands on the pill must not also begin a drag underneath it.
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                left,
                top,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: DONE_PILL_HEIGHT_PX,
                padding: '0 12px',
                borderRadius: DONE_PILL_HEIGHT_PX / 2,
                border: '2px solid #ffffff',
                background: '#2563eb',
                color: '#ffffff',
                font: '600 12px system-ui, -apple-system, sans-serif',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.35)',
                zIndex: 5,
              }}
            >
              <span><kbd style={COLLECT_KEY_STYLE}>Enter</kbd> to finish</span>
              <span style={{ opacity: 0.55 }}>·</span>
              <span><kbd style={COLLECT_KEY_STYLE}>Esc</kbd> removes the last region</span>
            </button>
          );
        })()}
      </div>
    </div>
  );
};

export default PidCanvas; 
